import React, { useEffect, useState, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { Section, Card, Badge, Button, Alert, Input, Label } from "../design-system";
import { useAuth } from "../auth/AuthProvider";
import { AdminPriceListsPage } from "./AdminPriceListsPage";

// Feb 2026 — Mapping centralizzato label italiane per discount_type.
// Usato da PromotionModal (tab Promozioni), PromotionEditor (tab Contratto),
// e PromoDraftEditor (dentro Nuovo contratto). NON cambia gli enum backend.
const DISCOUNT_TYPE_LABELS = {
  PERCENT: "Percentuale (%)",
  FIXED_PRICE: "Prezzo fisso (€/kg)",
  AMOUNT: "Importo fisso sconto (€)",
  CARTONI_OMAGGIO: "Cartoni omaggio (BUY X + Y)",
};
const DISCOUNT_TYPE_OPTIONS = Object.entries(DISCOUNT_TYPE_LABELS);
const FREE_PRODUCT_MODE_LABELS = {
  SAME_PRODUCT: "Stesso prodotto (il gratis è dello stesso SKU maturato)",
  SPECIFIC_PRODUCT: "Prodotto specifico (scegli qui sotto)",
};

const TABS = [
  { key: "products", label: "Prodotti" },
  { key: "categories", label: "Categorie" },
  { key: "packaging", label: "Profili Imballaggio" },
  { key: "promotions", label: "Promozioni" },
  { key: "contracts", label: "Contratti" },
  { key: "cooperatives", label: "Cooperative" },
  { key: "agents", label: "Agenti" },
  { key: "customers", label: "Clienti" },
  { key: "customer-categories", label: "Categorie clienti" },
  { key: "pickup-locations", label: "Sedi di ritiro" },
  { key: "shipping-rules", label: "Regole Trasporto" },
  { key: "discount-rules", label: "Sconti" },
  { key: "price-lists", label: "Listini prezzi" },
  { key: "carriers", label: "Vettori" },
];

export function AdminEntitiesPage() {
  // Support ?tab=<key> deep-linking from the Admin home QuickBtns so a
  // direct link ("Sedi di ritiro") lands the user on the right tab.
  // Feb 2026 — Also reacts to in-page navigation (Contratti → PFA deeplink)
  // so the tab state updates when the URL query changes.
  const location = useLocation();
  const readTabFromQuery = () => {
    try {
      const q = new URLSearchParams(location.search).get("tab");
      if (q && TABS.some((t) => t.key === q)) return q;
    } catch { /* ignore */ }
    return null;
  };
  const [tab, setTab] = useState(readTabFromQuery() || "products");
  useEffect(() => {
    const t = readTabFromQuery();
    if (t && t !== tab) setTab(t);
    // eslint-disable-next-line
  }, [location.search]);
  return (
    <Section title="Configurazioni Admin" subtitle="Gestione anagrafiche commerciali e logistiche"
             data-testid="admin-entities-page">
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16, borderBottom: "1px solid #e5e7eb" }}>
        {TABS.map((t) => (
          <button key={t.key} data-testid={`entities-tab-${t.key}`}
                  onClick={() => setTab(t.key)}
                  style={{ padding: "8px 16px",
                           border: "none",
                           borderBottom: tab === t.key ? "3px solid #0F4C81" : "3px solid transparent",
                           background: "transparent",
                           fontWeight: tab === t.key ? 600 : 400,
                           cursor: "pointer",
                           color: tab === t.key ? "#0F4C81" : "#666" }}>
            {t.label}
          </button>
        ))}
      </div>
      {tab === "products" && <ProductsTab />}
      {tab === "categories" && <CategoriesTab />}
      {tab === "packaging" && <PackagingProfilesTab />}
      {tab === "promotions" && <PromotionsTab />}
      {tab === "contracts" && <ContractsTab />}
      {tab === "cooperatives" && <CooperativesTab />}
      {tab === "agents" && <AgentsTab />}
      {tab === "customers" && <CustomersTab />}
      {tab === "customer-categories" && <CustomerCategoriesTab />}
      {tab === "pickup-locations" && <PickupLocationsTab />}
      {tab === "shipping-rules" && <ShippingRulesTab />}
      {tab === "discount-rules" && <DiscountRulesTab />}
      {tab === "price-lists" && <AdminPriceListsPage />}
      {tab === "carriers" && <CarriersTab />}
    </Section>
  );
}

// ---------- Sedi di ritiro (dedicated tab) ----------
// Feb 2026 v34 — Iterazione 1 di Admin Clienti: lista read-only con
// filtri (categoria, agente, attivo/inattivo) + ricerca (codice, ragione sociale).
// Il dettaglio/edit arriva in Iterazione 2. Riusa endpoint esistenti:
// GET /api/customers, /api/customer-categories, /api/admin/agents, /api/admin/price-lists.
function CustomersTab() {
  const { apiGet, apiPatch, apiPost, apiDelete } = useAuth();
  const [customers, setCustomers] = useState(null);
  const [cats, setCats] = useState([]);
  const [agents, setAgents] = useState([]);
  const [lists, setLists] = useState([]);
  // Pilot membership (canonical source: email_pilot_customers). Read-only Set
  // of customer_ids currently in the allowlist. Reused endpoints exactly as
  // already used by `AdminNotificationsConfigPage.PilotPanel` — no new APIs.
  const [pilotIds, setPilotIds] = useState(new Set());
  const [pilotBusy, setPilotBusy] = useState(null); // customer_id currently mutating
  const [err, setErr] = useState(null);
  const [q, setQ] = useState("");
  const [fCat, setFCat] = useState("");
  const [fAgent, setFAgent] = useState("");
  const [fActive, setFActive] = useState("all");
  const [fPilot, setFPilot] = useState("all"); // all | pilot | non_pilot
  const [editing, setEditing] = useState(null); // customer being edited
  const [newCatOpen, setNewCatOpen] = useState(false);

  const load = async () => {
    try {
      const [cs, cx, ags, pls, pil] = await Promise.all([
        apiGet("/api/customers"),
        apiGet("/api/customer-categories").catch(() => []),
        apiGet("/api/admin/agents").catch(() => []),
        apiGet("/api/admin/price-lists").catch(() => []),
        apiGet("/api/admin/email-pilot-customers").catch(() => []),
      ]);
      setCustomers(cs || []); setCats(cx || []); setAgents(ags || []); setLists(pls || []);
      setPilotIds(new Set((pil || []).map((p) => String(p.customer_id))));
    } catch (e) { setErr(String(e?.message || e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const reloadPilot = async () => {
    try {
      const pil = await apiGet("/api/admin/email-pilot-customers");
      setPilotIds(new Set((pil || []).map((p) => String(p.customer_id))));
    } catch (e) { setErr(String(e?.message || e)); }
  };

  const activatePilot = async (c) => {
    const msg = `Confermi l'attivazione PILOT per questo Cliente?\n\n${c.code} — ${c.business_name}\n\nAttivando il PILOT, gli eventi email customer-facing configurati PILOT potranno essere inviati al destinatario reale del Cliente. Email Office/Carrier/Internal/Staff restano in TEST.`;
    // eslint-disable-next-line no-alert
    if (!window.confirm(msg)) return;
    setPilotBusy(c.id); setErr(null);
    try {
      await apiPost("/api/admin/email-pilot-customers", { customer_id: c.id });
      await reloadPilot(); // refetch, do NOT optimistically assume success
    } catch (e) { setErr("Errore attivazione PILOT: " + (e?.message || e)); }
    finally { setPilotBusy(null); }
  };

  const removePilot = async (c) => {
    const msg = `Confermi la rimozione del Cliente dalla fase PILOT?\n\n${c.code} — ${c.business_name}\n\nIl Cliente non riceverà più email reali per gli eventi customer-facing configurati PILOT. Nessun altro dato del Cliente viene modificato.`;
    // eslint-disable-next-line no-alert
    if (!window.confirm(msg)) return;
    setPilotBusy(c.id); setErr(null);
    try {
      await apiDelete(`/api/admin/email-pilot-customers/${c.id}`);
      await reloadPilot();
    } catch (e) { setErr("Errore rimozione PILOT: " + (e?.message || e)); }
    finally { setPilotBusy(null); }
  };

  const catById = useMemo(() => Object.fromEntries(cats.map(c => [c.id, c])), [cats]);
  const agentById = useMemo(() => Object.fromEntries(agents.map(a => [a.id, a])), [agents]);
  const listById = useMemo(() => Object.fromEntries(lists.map(p => [p.id, p])), [lists]);
  const defaultList = useMemo(() => lists.find(p => p.is_default), [lists]);
  const listByCatId = useMemo(() => {
    const m = {};
    for (const p of lists) if (p.is_active && p.customer_category_id) m[p.customer_category_id] = p;
    return m;
  }, [lists]);
  const resolveList = (c) => {
    if (c.price_list_id && listById[c.price_list_id]) return { label: listById[c.price_list_id].code, kind: "direct" };
    if (c.customer_category_id && listByCatId[c.customer_category_id]) return { label: `Eredita: ${listByCatId[c.customer_category_id].code}`, kind: "inherited" };
    if (defaultList) return { label: `${defaultList.code} (DEFAULT)`, kind: "default" };
    return { label: "—", kind: "none" };
  };

  const filtered = useMemo(() => {
    if (!customers) return [];
    const query = q.trim().toLowerCase();
    return customers.filter(c => {
      if (query && !((c.code || "").toLowerCase().includes(query)
                  || (c.business_name || "").toLowerCase().includes(query))) return false;
      if (fCat && c.customer_category_id !== fCat) return false;
      if (fAgent && c.agent_id !== fAgent) return false;
      const isActive = c.status ? c.status === "ACTIVE" : true;
      if (fActive === "active" && !isActive) return false;
      if (fActive === "inactive" && isActive) return false;
      const isPilot = pilotIds.has(String(c.id));
      if (fPilot === "pilot" && !isPilot) return false;
      if (fPilot === "non_pilot" && isPilot) return false;
      return true;
    });
  }, [customers, q, fCat, fAgent, fActive, fPilot, pilotIds]);

  const onCategoryCreated = async (newCat) => {
    setNewCatOpen(false);
    await load();
    if (editing) setEditing(prev => ({ ...prev, customer_category_id: newCat.id }));
  };

  const saveCustomer = async (payload) => {
    try {
      const { price_list_id, ...rest } = payload;
      await apiPatch(`/api/admin/customers/${editing.id}`, rest);
      await apiPatch(`/api/admin/customers/${editing.id}/price-list`, { price_list_id: price_list_id || null });
      setEditing(null);
      await load();
    } catch (e) { alert("Errore salvataggio: " + (e?.message || e)); }
  };

  if (!customers) return <Card><Card.Body className="muted">Caricamento…</Card.Body></Card>;
  return (
    <div data-testid="admin-customers-tab">
      {err && <Alert tone="danger">{err}</Alert>}
      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr", gap: 8, marginBottom: 12 }}>
        <Input value={q} onChange={e => setQ(e.target.value)}
               placeholder="Cerca codice o ragione sociale…" data-testid="customers-search" />
        <select value={fCat} onChange={e => setFCat(e.target.value)} data-testid="customers-filter-category"
                style={{ padding: "6px 8px", border: "1px solid #cbd5e1", borderRadius: 6 }}>
          <option value="">Tutte le categorie</option>
          {cats.map(c => <option key={c.id} value={c.id}>{c.code} — {c.name}</option>)}
        </select>
        <select value={fAgent} onChange={e => setFAgent(e.target.value)} data-testid="customers-filter-agent"
                style={{ padding: "6px 8px", border: "1px solid #cbd5e1", borderRadius: 6 }}>
          <option value="">Tutti gli agenti</option>
          {agents.filter(a => a.is_active).map(a =>
            <option key={a.id} value={a.id}>{a.code} — {a.display_name || `${a.first_name || ""} ${a.last_name || ""}`.trim()}</option>)}
        </select>
        <select value={fActive} onChange={e => setFActive(e.target.value)} data-testid="customers-filter-active"
                style={{ padding: "6px 8px", border: "1px solid #cbd5e1", borderRadius: 6 }}>
          <option value="all">Tutti</option>
          <option value="active">Solo attivi</option>
          <option value="inactive">Solo inattivi</option>
        </select>
        <select value={fPilot} onChange={e => setFPilot(e.target.value)} data-testid="customers-filter-pilot"
                style={{ padding: "6px 8px", border: "1px solid #cbd5e1", borderRadius: 6 }}>
          <option value="all">PILOT: tutti</option>
          <option value="pilot">Solo PILOT attivi</option>
          <option value="non_pilot">Solo NON PILOT</option>
        </select>
      </div>
      <div className="muted" style={{ fontSize: ".8rem", marginBottom: 6 }}>
        {filtered.length} cliente/i · gerarchia listino: Override cliente → Categoria → DEFAULT
      </div>
      <Card><Card.Body style={{ padding: 0 }}>
        <div className="ds-table-wrap">
          <table className="ds-table" data-testid="admin-customers-table">
            <thead><tr>
              <th>Codice</th><th>Ragione sociale</th><th>Categoria</th>
              <th>Agente</th><th>Listino</th><th>Stato</th><th>PILOT</th><th>Azioni</th>
            </tr></thead>
            <tbody>
              {filtered.slice(0, 500).map(c => {
                const cat = c.customer_category_id ? catById[c.customer_category_id] : null;
                const ag = c.agent_id ? agentById[c.agent_id] : null;
                const list = resolveList(c);
                const isActive = c.status ? c.status === "ACTIVE" : true;
                const isPilot = pilotIds.has(String(c.id));
                const busyThis = pilotBusy === c.id;
                return (
                  <tr key={c.id} data-testid={`cust-row-${c.code}`}>
                    <td><strong>{c.code}</strong></td>
                    <td>{c.business_name}</td>
                    <td data-testid={`cust-cat-${c.code}`}>{cat ? `${cat.code} — ${cat.name}` : "—"}</td>
                    <td data-testid={`cust-agent-${c.code}`}>{ag ? (ag.display_name || ag.code) : "—"}</td>
                    <td data-testid={`cust-list-${c.code}`}><span style={{ fontSize: ".82rem" }}>{list.label}</span></td>
                    <td><Badge tone={isActive ? "success" : "neutral"}>{isActive ? "ATTIVO" : (c.status || "INATTIVO")}</Badge></td>
                    <td data-testid={`cust-pilot-badge-${c.code}`}>
                      <Badge tone={isPilot ? "warning" : "neutral"}>
                        PILOT {isPilot ? "ATTIVO" : "NON ATTIVO"}
                      </Badge>
                    </td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      <Button size="sm" onClick={() => setEditing(c)} data-testid={`cust-edit-${c.code}`}>Modifica</Button>
                      {" "}
                      {isPilot ? (
                        <Button size="sm" variant="ghost" disabled={busyThis || !isActive}
                                onClick={() => removePilot(c)}
                                data-testid={`cust-pilot-remove-${c.code}`}>
                          {busyThis ? "…" : "Rimuovi PILOT"}
                        </Button>
                      ) : (
                        <Button size="sm" variant="primary" disabled={busyThis || !isActive}
                                onClick={() => activatePilot(c)}
                                data-testid={`cust-pilot-activate-${c.code}`}>
                          {busyThis ? "…" : "Attiva PILOT"}
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={8} className="muted" style={{ padding: 12, textAlign: "center" }}>Nessun cliente corrispondente.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card.Body></Card>
      {editing && (
        <CustomerModal data={editing} cats={cats} agents={agents} lists={lists}
                       defaultList={defaultList} listByCatId={listByCatId}
                       onClose={() => setEditing(null)}
                       onSubmit={saveCustomer}
                       onNewCategory={() => setNewCatOpen(true)} />
      )}
      {newCatOpen && (
        <CustomerCategoryModal onClose={() => setNewCatOpen(false)}
                               onCreated={onCategoryCreated} apiPost={apiPost} />
      )}
    </div>
  );
}

function CustomerModal({ data, cats, agents, lists, defaultList, listByCatId, onClose, onSubmit, onNewCategory }) {
  const [f, setF] = useState({
    business_name: data.business_name || "",
    vat_number: data.vat_number || "",
    tax_code: data.tax_code || "",
    email: data.email || "",
    phone: data.phone || "",
    legal_address: data.legal_address || "",
    legal_zip_code: data.legal_zip_code || "",
    legal_city: data.legal_city || "",
    legal_province: data.legal_province || "",
    customer_category_id: data.customer_category_id || "",
    agent_id: data.agent_id || "",
    price_list_id: data.price_list_id || "",
    status: data.status || "ACTIVE",
  });
  useEffect(() => {
    if (data.customer_category_id !== f.customer_category_id) {
      setF(prev => ({ ...prev, customer_category_id: data.customer_category_id || prev.customer_category_id }));
    }
    // eslint-disable-next-line
  }, [data.customer_category_id]);

  const fallbackHint = () => {
    if (f.price_list_id) return null;
    const cat = f.customer_category_id ? cats.find(x => x.id === f.customer_category_id) : null;
    const inh = cat && listByCatId[cat.id];
    if (inh) return `Eredita da categoria: ${cat.code} → ${inh.code}`;
    if (defaultList) return `Usa DEFAULT: ${defaultList.code}`;
    return "Nessun fallback (verifica DEFAULT).";
  };

  return (
    <Modal title={`Modifica cliente ${data.code}`} onClose={onClose} testid="customer-modal">
      <div style={{ fontSize: ".85rem", fontWeight: 600, margin: "4px 0 8px", color: "#0F4C81" }}>Anagrafica</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <FormField label="Codice"><Input value={data.code} disabled /></FormField>
        <FormField label="Ragione sociale *"><Input value={f.business_name} data-testid="cm-business-name"
          onChange={e => setF({ ...f, business_name: e.target.value })} /></FormField>
        <FormField label="P.IVA"><Input value={f.vat_number} onChange={e => setF({ ...f, vat_number: e.target.value })} /></FormField>
        <FormField label="Codice fiscale"><Input value={f.tax_code} onChange={e => setF({ ...f, tax_code: e.target.value })} /></FormField>
        <FormField label="Email"><Input type="email" value={f.email} data-testid="cm-email"
          onChange={e => setF({ ...f, email: e.target.value })} /></FormField>
        <FormField label="Telefono"><Input value={f.phone} onChange={e => setF({ ...f, phone: e.target.value })} /></FormField>
      </div>
      <FormField label="Indirizzo legale"><Input value={f.legal_address} onChange={e => setF({ ...f, legal_address: e.target.value })} /></FormField>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr 1fr", gap: 8 }}>
        <FormField label="CAP"><Input value={f.legal_zip_code} onChange={e => setF({ ...f, legal_zip_code: e.target.value })} /></FormField>
        <FormField label="Città"><Input value={f.legal_city} onChange={e => setF({ ...f, legal_city: e.target.value })} /></FormField>
        <FormField label="Prov."><Input value={f.legal_province} maxLength={4}
          onChange={e => setF({ ...f, legal_province: e.target.value.toUpperCase() })} /></FormField>
      </div>
      <div style={{ borderTop: "1px solid #e5e7eb", margin: "12px 0 8px" }} />
      <div style={{ fontSize: ".85rem", fontWeight: 600, margin: "4px 0 8px", color: "#0F4C81" }}>Commerciale</div>
      <FormField label="Categoria cliente">
        <div style={{ display: "flex", gap: 6 }}>
          <select value={f.customer_category_id} onChange={e => setF({ ...f, customer_category_id: e.target.value })}
                  data-testid="cm-category"
                  style={{ flex: 1, padding: "6px 8px", border: "1px solid #cbd5e1", borderRadius: 6 }}>
            <option value="">— Nessuna categoria —</option>
            {cats.filter(c => c.is_active).map(c => (
              <option key={c.id} value={c.id}>{c.code} — {c.name}</option>
            ))}
          </select>
          <Button size="sm" onClick={onNewCategory} data-testid="cm-new-category-btn">+ Nuova</Button>
        </div>
      </FormField>
      <FormField label="Agente">
        <select value={f.agent_id} onChange={e => setF({ ...f, agent_id: e.target.value })}
                data-testid="cm-agent"
                style={{ width: "100%", padding: "6px 8px", border: "1px solid #cbd5e1", borderRadius: 6 }}>
          <option value="">— Nessun agente —</option>
          {agents.filter(a => a.is_active).map(a => (
            <option key={a.id} value={a.id}>{a.code} — {a.display_name || `${a.first_name || ""} ${a.last_name || ""}`.trim()}</option>
          ))}
        </select>
      </FormField>
      <FormField label="Listino cliente specifico">
        <select value={f.price_list_id} onChange={e => setF({ ...f, price_list_id: e.target.value })}
                data-testid="cm-list"
                style={{ width: "100%", padding: "6px 8px", border: "1px solid #cbd5e1", borderRadius: 6 }}>
          <option value="">Nessun listino specifico</option>
          {lists.filter(l => l.is_active && !l.is_default).map(l => (
            <option key={l.id} value={l.id}>{l.code} — {l.name}</option>
          ))}
        </select>
        {!f.price_list_id && <div className="muted" style={{ fontSize: ".76rem", marginTop: 4 }}>{fallbackHint()}</div>}
      </FormField>
      <label style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8 }}>
        <input type="checkbox" checked={f.status === "ACTIVE"} data-testid="cm-active"
               onChange={e => setF({ ...f, status: e.target.checked ? "ACTIVE" : "BLOCKED" })} />
        Cliente attivo
      </label>
      <div style={{ borderTop: "1px solid #e5e7eb", margin: "16px 0 8px" }} />
      <div style={{ fontSize: ".85rem", fontWeight: 600, margin: "4px 0 6px", color: "#0F4C81" }}>Ordini via email</div>
      <div className="muted" style={{ fontSize: ".78rem", marginBottom: 8 }}>
        Consenti a questo cliente di inviare ordini via email dagli indirizzi
        esplicitamente autorizzati. Ogni indirizzo va aggiunto manualmente
        (nessun wildcard di dominio, nessun fuzzy matching).
      </div>
      <Link to={`/admin/customers/${data.id}/email-intake`}
            data-testid="cm-email-intake-link"
            onClick={onClose}
            style={{ display: "inline-block", padding: "8px 14px",
                     background: "#0F4C81", color: "#fff",
                     borderRadius: 6, textDecoration: "none",
                     fontSize: 13, fontWeight: 600 }}>
        Gestisci ordini via email →
      </Link>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
        <Button onClick={onClose}>Annulla</Button>
        <Button variant="primary" data-testid="cm-submit" onClick={() => onSubmit({
          business_name: f.business_name || null,
          vat_number: f.vat_number || null,
          tax_code: f.tax_code || null,
          email: f.email || null,
          phone: f.phone || null,
          legal_address: f.legal_address || null,
          legal_zip_code: f.legal_zip_code || null,
          legal_city: f.legal_city || null,
          legal_province: f.legal_province || null,
          customer_category_id: f.customer_category_id || null,
          agent_id: f.agent_id || null,
          status: f.status,
          price_list_id: f.price_list_id || null,
        })}>Salva</Button>
      </div>
    </Modal>
  );
}

function CustomerCategoryModal({ data, onClose, onCreated, onUpdated, apiPost, apiPatch }) {
  const [f, setF] = useState({
    code: data?.code || "",
    name: data?.name || "",
    description: data?.description || "",
    is_active: data?.is_active !== false,
  });
  const [err, setErr] = useState(null);
  const submit = async () => {
    setErr(null);
    if (!f.code.trim() || !f.name.trim()) { setErr("Codice e Nome obbligatori"); return; }
    try {
      if (data && apiPatch) {
        const r = await apiPatch(`/api/admin/customer-categories/${data.id}`,
          { name: f.name, description: f.description || null, is_active: f.is_active });
        onUpdated && onUpdated(r);
      } else if (apiPost) {
        const r = await apiPost("/api/admin/customer-categories", {
          code: f.code, name: f.name, description: f.description || null, is_active: f.is_active,
        });
        onCreated && onCreated(r);
      }
    } catch (e) { setErr(e?.data?.detail || String(e?.message || e)); }
  };
  return (
    <Modal title={data ? `Modifica categoria ${data.code}` : "Nuova categoria cliente"}
           onClose={onClose} testid="customer-category-modal">
      {err && <Alert tone="danger">{typeof err === "string" ? err : JSON.stringify(err)}</Alert>}
      <FormField label="Codice *"><Input value={f.code} disabled={!!data} data-testid="cc-code"
        onChange={e => setF({ ...f, code: e.target.value.toUpperCase() })} /></FormField>
      <FormField label="Nome *"><Input value={f.name} data-testid="cc-name"
        onChange={e => setF({ ...f, name: e.target.value })} /></FormField>
      <FormField label="Descrizione">
        <textarea className="ds-input" rows={2} value={f.description}
                  onChange={e => setF({ ...f, description: e.target.value })} />
      </FormField>
      <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <input type="checkbox" checked={f.is_active} onChange={e => setF({ ...f, is_active: e.target.checked })} />
        Categoria attiva
      </label>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
        <Button onClick={onClose}>Annulla</Button>
        <Button variant="primary" onClick={submit} data-testid="cc-submit">Salva</Button>
      </div>
    </Modal>
  );
}

function CustomerCategoriesTab() {
  const { apiGet, apiPost, apiPatch, apiDelete } = useAuth();
  const [rows, setRows] = useState(null);
  const [lists, setLists] = useState([]);
  const [modal, setModal] = useState(null); // {mode:'create'|'edit', data?}
  const load = async () => {
    const [cs, pls] = await Promise.all([
      apiGet("/api/customer-categories").catch(() => []),
      apiGet("/api/admin/price-lists").catch(() => []),
    ]);
    setRows(cs || []); setLists(pls || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const listByCatId = useMemo(() => {
    const m = {};
    for (const p of lists) if (p.is_active && p.customer_category_id) m[p.customer_category_id] = p;
    return m;
  }, [lists]);
  const toggleActive = async (c) => {
    await apiPatch(`/api/admin/customer-categories/${c.id}`, { is_active: !c.is_active });
    await load();
  };
  const remove = async (c) => {
    const r = await apiDelete(`/api/admin/customer-categories/${c.id}`);
    if (r?.disabled) alert(`Categoria in uso da ${r.customers_count} cliente/i e ${r.price_lists_count} listino/i: disattivata invece che cancellata.`);
    await load();
  };
  if (!rows) return <Card><Card.Body className="muted">Caricamento…</Card.Body></Card>;
  return (
    <div data-testid="admin-customer-categories-tab">
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
        <p className="muted" style={{ margin: 0, fontSize: ".85rem" }}>
          Categorie clienti utilizzate per raggruppare i clienti e collegare listini prezzi.
        </p>
        <Button variant="primary" onClick={() => setModal({ mode: "create" })}
                data-testid="cc-new-btn">+ Nuova categoria</Button>
      </div>
      <Card><Card.Body style={{ padding: 0 }}>
        <div className="ds-table-wrap">
          <table className="ds-table">
            <thead><tr><th>Codice</th><th>Nome</th><th>Descrizione</th><th>Listino</th><th>Stato</th><th>Azioni</th></tr></thead>
            <tbody>
              {rows.map(c => (
                <tr key={c.id} data-testid={`cc-row-${c.code}`}>
                  <td><strong>{c.code}</strong></td>
                  <td>{c.name}</td>
                  <td className="muted" style={{ fontSize: ".8rem" }}>{c.description || "—"}</td>
                  <td>{listByCatId[c.id]?.code || "—"}</td>
                  <td><Badge tone={c.is_active ? "success" : "neutral"}>{c.is_active ? "ATTIVA" : "DISATTIVA"}</Badge></td>
                  <td style={{ display: "flex", gap: 6 }}>
                    <Button size="sm" onClick={() => setModal({ mode: "edit", data: c })} data-testid={`cc-edit-${c.code}`}>Modifica</Button>
                    <Button size="sm" onClick={() => toggleActive(c)}>{c.is_active ? "Disattiva" : "Attiva"}</Button>
                    <Button size="sm" onClick={() => remove(c)}>Elimina</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card.Body></Card>
      {modal && (
        <CustomerCategoryModal data={modal.data} apiPost={apiPost} apiPatch={apiPatch}
                               onClose={() => setModal(null)}
                               onCreated={() => { setModal(null); load(); }}
                               onUpdated={() => { setModal(null); load(); }} />
      )}
    </div>
  );
}


// ---------- Sedi di ritiro (dedicated tab) ----------
// Riusa il CRUD Admin PickupRule già esteso (schema="pickup"). La differenza
// col sotto-tab "Sconti > Ritiro cliente" è solo di posizionamento: qui
// l'operatore trova immediatamente la gestione anagrafica delle sedi.
function PickupLocationsTab() {
  // Feb 2026 v34 — Vista active-only (le sedi test/demo restano in DB per
  // preservare FK con ordini storici snapshot, ma non compaiono qui).
  // v34.5 — Aggiunto toggle "Mostra archiviate" per permettere all'admin di
  // vedere/riattivare sedi disattivate senza toccare il DB.
  const [showArchived, setShowArchived] = React.useState(false);
  const basePath = showArchived
    ? "/api/admin/discount-rules/pickup"
    : "/api/admin/discount-rules/pickup?active=true";
  return (
    <>
      <p className="muted" style={{ marginTop: 0 }}>
        Sedi fisiche dove il cliente può ritirare la merce. Le sedi <strong>attive</strong> compaiono nel checkout PICKUP;
        gli ordini già confermati mantengono la sede scelta al momento dell'ordine (snapshot immutabile).
      </p>
      <label style={{ display: "inline-flex", alignItems: "center", gap: 6, marginBottom: 10, fontSize: ".88rem" }}
             data-testid="pickup-show-archived-label">
        <input type="checkbox" checked={showArchived}
               onChange={(e) => setShowArchived(e.target.checked)}
               data-testid="pickup-show-archived-toggle" />
        Mostra archiviate
      </label>
      <GenericRuleTab basePath={basePath}
                       title="" testid="pickup-loc" schema="pickup" />
    </>
  );
}

// ---------- Generic CRUD helper ----------
function useCrud(basePath) {
  const { apiGet, apiPost, apiPatch } = useAuth();
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);
  const load = async (params = "") => {
    setErr(null);
    try { setRows(await apiGet(`${basePath}${params}`)); }
    catch (e) { setErr(e?.data?.detail || e?.message || String(e)); setRows([]); }
  };
  return { rows, err, load, apiGet, apiPost, apiPatch };
}

// ---------- PRODUCTS ----------
function PackagingProfilesTab() {
  const { apiGet, apiPost, apiPatch } = useAuth();
  const [rows, setRows] = useState([]);
  const [modal, setModal] = useState(null);
  const [err, setErr] = useState(null);
  const load = async () => {
    try { setRows(await apiGet("/api/admin/packaging-profiles")); }
    catch (e) { setErr(String(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  return (
    <>
      {err && <Alert variant="danger">{err}</Alert>}
      <Card>
        <Card.Header>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <strong>Profili di imballaggio / pallettizzazione</strong>
            <Button variant="primary" data-testid="pp-new"
                    onClick={() => setModal({ mode: "create", data: {} })}>+ Nuovo profilo</Button>
          </div>
        </Card.Header>
        <Card.Body>
          <table className="ds-table" data-testid="pp-table">
            <thead><tr>
              <th>Codice</th><th>Nome</th>
              <th>Cart/strato</th><th>Strati/pallet</th><th>Cart/pallet</th>
              <th>H max cm</th><th>H cartone</th><th>Peso max kg</th>
              <th>Uso</th><th>Attivo</th><th></th>
            </tr></thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id} data-testid={`pp-row-${p.code}`}>
                  <td><code>{p.code}</code></td>
                  <td>{p.name}</td>
                  <td>{p.cartons_per_layer ?? "—"}</td>
                  <td>{p.layers_per_std_pallet ?? "—"}</td>
                  <td>{p.cartons_per_std_pallet ?? "—"}</td>
                  <td>{p.max_pallet_height_cm ?? "—"}</td>
                  <td>{p.carton_height_cm ?? "—"}</td>
                  <td>{p.max_pallet_weight_kg ?? "—"}</td>
                  <td><Badge>{p.usage_count} prodotti</Badge></td>
                  <td><Badge tone={p.is_active ? "success" : "neutral"}>{p.is_active ? "SÌ" : "NO"}</Badge></td>
                  <td><Button size="sm" data-testid={`pp-edit-${p.code}`}
                              onClick={() => setModal({ mode: "edit", data: p })}>Modifica</Button></td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={11} className="muted" style={{ textAlign: "center", padding: 24 }}>
                  Nessun profilo — creane uno per condividere i dati pallet tra più prodotti.
                </td></tr>
              )}
            </tbody>
          </table>
        </Card.Body>
      </Card>
      {modal && (
        <PackagingProfileModal mode={modal.mode} data={modal.data}
          onClose={() => setModal(null)}
          onSubmit={async (payload) => {
            if (modal.mode === "create") await apiPost("/api/admin/packaging-profiles", payload);
            else await apiPatch(`/api/admin/packaging-profiles/${modal.data.id}`, payload);
            setModal(null); await load();
          }} />
      )}
    </>
  );
}

function PackagingProfileModal({ mode, data, onClose, onSubmit }) {
  const [f, setF] = useState({
    code: data.code || "", name: data.name || "",
    cartons_per_layer: data.cartons_per_layer ?? "",
    layers_per_std_pallet: data.layers_per_std_pallet ?? "",
    cartons_per_std_pallet: data.cartons_per_std_pallet ?? "",
    max_pallet_height_cm: data.max_pallet_height_cm ?? "",
    carton_height_cm: data.carton_height_cm ?? "",
    max_pallet_weight_kg: data.max_pallet_weight_kg ?? "",
    is_active: data.is_active ?? true,
  });
  const numOr = (v) => v === "" ? null : Number(v);
  const submit = () => onSubmit({
    ...f,
    cartons_per_layer: numOr(f.cartons_per_layer),
    layers_per_std_pallet: numOr(f.layers_per_std_pallet),
    cartons_per_std_pallet: numOr(f.cartons_per_std_pallet),
    max_pallet_height_cm: numOr(f.max_pallet_height_cm),
    carton_height_cm: numOr(f.carton_height_cm),
    max_pallet_weight_kg: numOr(f.max_pallet_weight_kg),
  });
  return (
    <Modal title={mode === "create" ? "Nuovo profilo" : `Modifica ${data.code}`}
           onClose={onClose} testid="pp-modal">
      {mode === "create" && (
        <FormField label="Codice *"><Input value={f.code}
          onChange={(e) => setF({ ...f, code: e.target.value })} data-testid="pp-code" /></FormField>
      )}
      <FormField label="Nome *"><Input value={f.name}
        onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="pp-name" /></FormField>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
        <FormField label="Cartoni/strato"><Input type="number" value={f.cartons_per_layer}
          onChange={(e) => setF({ ...f, cartons_per_layer: e.target.value })} /></FormField>
        <FormField label="Strati/pallet"><Input type="number" value={f.layers_per_std_pallet}
          onChange={(e) => setF({ ...f, layers_per_std_pallet: e.target.value })} /></FormField>
        <FormField label="Cartoni/pallet"><Input type="number" value={f.cartons_per_std_pallet}
          onChange={(e) => setF({ ...f, cartons_per_std_pallet: e.target.value })} /></FormField>
        <FormField label="H max pallet cm"><Input type="number" step="0.01"
          value={f.max_pallet_height_cm}
          onChange={(e) => setF({ ...f, max_pallet_height_cm: e.target.value })} /></FormField>
        <FormField label="H cartone cm"><Input type="number" step="0.01"
          value={f.carton_height_cm}
          onChange={(e) => setF({ ...f, carton_height_cm: e.target.value })} /></FormField>
        <FormField label="Peso max pallet kg"><Input type="number" step="0.01"
          value={f.max_pallet_weight_kg}
          onChange={(e) => setF({ ...f, max_pallet_weight_kg: e.target.value })} /></FormField>
      </div>
      <label style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8 }}>
        <input type="checkbox" checked={f.is_active} data-testid="pp-active"
               onChange={(e) => setF({ ...f, is_active: e.target.checked })} /> Attivo
      </label>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
        <Button onClick={onClose}>Annulla</Button>
        <Button variant="primary" onClick={submit} data-testid="pp-submit">Salva</Button>
      </div>
    </Modal>
  );
}



function CategoriesTab() {
  const { apiGet, apiPost, apiPatch, apiDelete } = useAuth();
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState(null);
  const [modal, setModal] = useState(null);

  const load = async () => {
    try { setRows(await apiGet("/api/admin/categories")); }
    catch (e) { setErr(e?.response?.data?.detail || String(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const del = async (c) => {
    if (!window.confirm(`Eliminare categoria "${c.name}"?`)) return;
    await apiDelete(`/api/admin/categories/${c.id}`);
    await load();
  };

  return (
    <>
      {err && <Alert variant="danger">{err}</Alert>}
      <Card>
        <Card.Header>
          <div style={{ display: "flex", gap: 8, alignItems: "center", justifyContent: "space-between" }}>
            <strong>Categorie commerciali</strong>
            <div style={{ display: "flex", gap: 8 }}>
              <span className="muted" style={{ fontSize: ".85rem" }}>
                {rows.length} categorie · <b>NON</b> mappate automaticamente da ARGRUMER
              </span>
              <Button variant="primary" data-testid="cat-new"
                      onClick={() => setModal({ mode: "create", data: {} })}>
                + Nuova categoria
              </Button>
            </div>
          </div>
        </Card.Header>
        <Card.Body>
          <table className="ds-table" data-testid="cat-table">
            <thead><tr>
              <th>Codice</th><th>Nome</th><th>Descrizione</th>
              <th>Ordine</th><th>Attiva</th><th></th>
            </tr></thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id} data-testid={`cat-row-${c.code}`}>
                  <td><code>{c.code}</code></td>
                  <td>{c.name}</td>
                  <td className="muted">{c.description || "—"}</td>
                  <td>{c.sort_order}</td>
                  <td>
                    <Badge tone={c.is_active ? "success" : "neutral"}>
                      {c.is_active ? "SÌ" : "NO"}
                    </Badge>
                  </td>
                  <td>
                    <div style={{ display: "flex", gap: 4 }}>
                      <Button size="sm" onClick={() => setModal({ mode: "edit", data: c })}
                              data-testid={`cat-edit-${c.code}`}>Modifica</Button>
                      <Button size="sm" onClick={() => del(c)}
                              data-testid={`cat-del-${c.code}`}>Elimina</Button>
                    </div>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={6} className="muted" style={{ textAlign: "center", padding: 24 }}>
                  Nessuna categoria — creane una per iniziare a mappare i prodotti importati.
                </td></tr>
              )}
            </tbody>
          </table>
        </Card.Body>
      </Card>
      {modal && (
        <CategoryModal mode={modal.mode} data={modal.data}
                       onClose={() => setModal(null)}
                       onSubmit={async (payload) => {
                         if (modal.mode === "create") await apiPost("/api/admin/categories", payload);
                         else await apiPatch(`/api/admin/categories/${modal.data.id}`, payload);
                         setModal(null);
                         await load();
                       }} />
      )}
    </>
  );
}


function CategoryModal({ mode, data, onClose, onSubmit }) {
  const [f, setF] = useState({
    code: data.code || "",
    name: data.name || "",
    description: data.description || "",
    sort_order: data.sort_order ?? 0,
    is_active: data.is_active ?? true,
  });
  return (
    <Modal title={mode === "create" ? "Nuova categoria" : `Modifica ${data.code}`}
           onClose={onClose} testid="cat-modal">
      {mode === "create" && (
        <FormField label="Codice interno *">
          <Input value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })}
                 data-testid="cat-code" />
        </FormField>
      )}
      <FormField label="Nome *">
        <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })}
               data-testid="cat-name" />
      </FormField>
      <FormField label="Descrizione">
        <Input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })}
               data-testid="cat-desc" />
      </FormField>
      <FormField label="Ordine visualizzazione">
        <Input type="number" value={f.sort_order}
               onChange={(e) => setF({ ...f, sort_order: Number(e.target.value) || 0 })} />
      </FormField>
      <label style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8 }}>
        <input type="checkbox" checked={f.is_active}
               data-testid="cat-active"
               onChange={(e) => setF({ ...f, is_active: e.target.checked })} />
        Attiva (visibile ai clienti)
      </label>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
        <Button onClick={onClose}>Annulla</Button>
        <Button variant="primary" onClick={() => onSubmit(f)} data-testid="cat-submit">
          Salva
        </Button>
      </div>
    </Modal>
  );
}


function ProductsTab() {
  const { rows, err, load, apiPost, apiPatch } = useCrud("/api/admin/products");
  const { apiPost: doPost } = useAuth();
  const [q, setQ] = useState("");
  const [tryFilter, setTryFilter] = useState("");
  const [catFilter, setCatFilter] = useState("");  // "", "__none__", or category_id
  const [logisticsFilter, setLogisticsFilter] = useState("");  // "", "incomplete"
  const [ppFilter, setPpFilter] = useState("");  // "", "__none__", or packaging_profile_id
  const [sugFilter, setSugFilter] = useState("");  // "", "high", "low", "excluded", "none"
  const [modal, setModal] = useState(null);
  const [docsPanel, setDocsPanel] = useState(null);
  const [categories, setCategories] = useState([]);
  const [packagingProfiles, setPackagingProfiles] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkCatId, setBulkCatId] = useState("");
  const [bulkPpId, setBulkPpId] = useState("");
  const [bulkStatus, setBulkStatus] = useState(null);
  const [bulkTryPriority, setBulkTryPriority] = useState("");
  const { apiGet, apiPost: doApiPost } = useAuth();
  const API = process.env.REACT_APP_BACKEND_URL;

  const buildQuery = () => {
    const parts = [];
    if (q) parts.push(`q=${encodeURIComponent(q)}`);
    if (tryFilter === "true") parts.push(`show_in_try_products=true`);
    if (tryFilter === "false") parts.push(`show_in_try_products=false`);
    if (catFilter === "__none__") parts.push(`uncategorized=true`);
    else if (catFilter) parts.push(`category_id=${catFilter}`);
    if (logisticsFilter === "incomplete") parts.push(`logistics_incomplete=true`);
    if (ppFilter === "__none__") parts.push(`no_packaging_profile=true`);
    else if (ppFilter) parts.push(`packaging_profile_id=${ppFilter}`);
    if (sugFilter) parts.push(`suggestion_confidence=${sugFilter}`);
    return parts.length ? `?${parts.join("&")}` : "";
  };

  const reloadCats = () => apiGet("/api/admin/categories").then(setCategories).catch(() => {});
  const reloadPps = () => apiGet("/api/admin/packaging-profiles").then(setPackagingProfiles).catch(() => {});
  useEffect(() => { load(""); reloadCats(); reloadPps(); /* eslint-disable-next-line */ }, []);
  const toggle = async (p) => { await doPost(`/api/admin/products/${p.id}/toggle-status`, {}); await load(buildQuery()); };
  // Feb 2026 — quick action "Da provare" inline: NON apre il modal, tocca solo
  // show_in_try_products. Non modifica altri campi (nome, cat, prezzo, ecc.).
  const toggleTry = async (p) => {
    try {
      await doPost(`/api/admin/products/${p.id}/toggle-try`, {});
      await load(buildQuery());
    } catch (e) {
      setBulkStatus("❌ " + (e?.data?.detail || String(e)));
    }
  };
  const doBulkSetTry = async (show) => {
    setBulkStatus(null);
    try {
      const payload = {
        product_ids: Array.from(selectedIds),
        show_in_try_products: show,
      };
      if (show && bulkTryPriority !== "") {
        payload.priority = parseInt(bulkTryPriority);
      }
      const r = await doApiPost("/api/admin/products/bulk-set-try", payload);
      setBulkStatus(`✅ ${r.updated_count} prodotti aggiornati (${show ? "aggiunti" : "rimossi"} "Da provare")`);
      setSelectedIds(new Set());
      await load(buildQuery());
    } catch (e) {
      setBulkStatus("❌ " + (e?.data?.detail || String(e)));
    }
  };

  const toggleRow = (id) => {
    const s = new Set(selectedIds);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelectedIds(s);
  };
  const toggleAll = () => {
    if (selectedIds.size === rows.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(rows.map((r) => r.id)));
  };
  const doBulkAssign = async () => {
    setBulkStatus(null);
    try {
      const r = await doApiPost("/api/admin/products/bulk-assign-category", {
        product_ids: Array.from(selectedIds),
        category_id: bulkCatId || null,
      });
      setBulkStatus(`✅ ${r.updated_count} prodotti aggiornati`);
      setSelectedIds(new Set());
      await load(buildQuery());
    } catch (e) {
      setBulkStatus("❌ " + (e?.response?.data?.detail || String(e)));
    }
  };
  const doBulkAssignPp = async () => {
    setBulkStatus(null);
    try {
      const r = await doApiPost("/api/admin/products/bulk-assign-packaging-profile", {
        product_ids: Array.from(selectedIds),
        packaging_profile_id: bulkPpId || null,
      });
      setBulkStatus(`✅ ${r.updated_count} prodotti aggiornati (profilo)`);
      setSelectedIds(new Set());
      await load(buildQuery());
    } catch (e) {
      setBulkStatus("❌ " + (e?.response?.data?.detail || String(e)));
    }
  };
  const doBulkConfirmSuggestion = async () => {
    setBulkStatus(null);
    if (selectedIds.size > 50 &&
        !window.confirm(
          `Stai per confermare il profilo imballaggio suggerito per ${selectedIds.size} prodotti.\n` +
          `L'operazione è irreversibile senza intervento manuale.\n\nProcedere?`)) {
      return;
    }
    try {
      const r = await doApiPost("/api/admin/products/bulk-confirm-packaging-suggestion", {
        product_ids: Array.from(selectedIds),
      });
      const excluded = r?.skipped_excluded?.length ?? 0;
      const noSug = r?.skipped_no_suggestion?.length ?? 0;
      const noChange = r?.skipped_no_change?.length ?? 0;
      const confirmed = r?.confirmed_count ?? 0;
      const msg =
        `✅ Confermati ${confirmed}` +
        (excluded ? ` · esclusi anomalie: ${excluded}` : "") +
        (noSug ? ` · senza suggerimento: ${noSug}` : "") +
        (noChange ? ` · già coincidenti: ${noChange}` : "");
      setBulkStatus(msg);
      setSelectedIds(new Set());
      await load(buildQuery());
    } catch (e) {
      setBulkStatus("❌ " + (e?.response?.data?.detail || String(e)));
    }
  };

  return (
    <>
      {err && <Alert variant="danger">Errore: {String(err)}</Alert>}
      <Card>
        <Card.Header>
          <div className="products-compact-table-filters" style={{ display: "grid", gap: "8px 10px", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", alignItems: "flex-end" }}>
            <div style={{ gridColumn: "span 2", minWidth: 0 }}>
              <Label>Cerca SKU / Nome</Label>
              <Input value={q} onChange={(e) => setQ(e.target.value)}
                     onKeyDown={(e) => e.key === "Enter" && load(buildQuery())}
                     data-testid="prod-search" />
            </div>
            <div>
              <Label>Categoria commerciale</Label>
              <select className="ds-input" value={catFilter}
                      data-testid="prod-filter-cat"
                      onChange={(e) => setCatFilter(e.target.value)}
                      style={{ minWidth: 200 }}>
                <option value="">Tutte</option>
                <option value="__none__">— Senza categoria —</option>
                {categories.filter((c) => c.is_active !== false).map((c) => (
                  <option key={c.id} value={c.id}>{c.name} ({c.code})</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Logistica</Label>
              <select className="ds-input" value={logisticsFilter}
                      data-testid="prod-filter-logistics"
                      onChange={(e) => setLogisticsFilter(e.target.value)}>
                <option value="">Tutti</option>
                <option value="incomplete">Logistica incompleta</option>
              </select>
            </div>
            <div>
              <Label>Profilo imballaggio</Label>
              <select className="ds-input" value={ppFilter}
                      data-testid="prod-filter-pp"
                      onChange={(e) => setPpFilter(e.target.value)}
                      style={{ minWidth: 180 }}>
                <option value="">Tutti</option>
                <option value="__none__">— Senza profilo —</option>
                {packagingProfiles.filter((pp) => pp.is_active !== false).map((pp) => (
                  <option key={pp.id} value={pp.id}>{pp.name} ({pp.code})</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Suggerimento packaging</Label>
              <select className="ds-input" value={sugFilter}
                      data-testid="prod-filter-suggestion"
                      onChange={(e) => setSugFilter(e.target.value)}
                      style={{ minWidth: 160 }}>
                <option value="">Tutti</option>
                <option value="high">HIGH confidence</option>
                <option value="low">LOW / no listino</option>
                <option value="excluded">EXCLUDED (anomalia)</option>
                <option value="none">Nessun suggerimento</option>
              </select>
            </div>
            <div>
              <Label>Nuovi prodotti da provare</Label>
              <select className="ds-input" value={tryFilter}
                      data-testid="prod-filter-try"
                      onChange={(e) => setTryFilter(e.target.value)}>
                <option value="">Tutti</option>
                <option value="true">Solo attivi in "da provare"</option>
                <option value="false">Solo NON attivi in "da provare"</option>
              </select>
            </div>
            <Button onClick={() => load(buildQuery())} variant="primary" data-testid="prod-filter-apply">Applica</Button>
            <Button variant="primary" onClick={() => setModal({ mode: "create", data: {} })}
                    data-testid="prod-new">+ Nuovo prodotto</Button>
          </div>
        </Card.Header>
        <Card.Body>
          {!rows && <p className="muted">Caricamento…</p>}
          {rows && rows.length === 0 && <Alert tone="info">Nessun prodotto.</Alert>}
          {rows && rows.length > 0 && (
            <div className="ds-table-wrap products-compact-table">
              <style>{`
                /* v43+UX (Mar 2026) — ProductsTab desktop compact layout.
                   Scoped via .products-compact-table so other admin tables
                   (categories, packaging, pricing, etc.) are unaffected. */
                .products-compact-table { overflow-x: auto; }
                .products-compact-table table.ds-table { table-layout: fixed; width: 100%; font-size: 12.5px; }
                .products-compact-table table.ds-table th,
                .products-compact-table table.ds-table td {
                  padding: 6px 6px !important;
                  vertical-align: middle;
                  overflow: hidden;
                }
                .products-compact-table th { font-size: 11px; line-height: 1.15; white-space: normal; }
                .products-compact-table td { word-break: break-word; }
                .products-compact-table .prod-name {
                  display: -webkit-box;
                  -webkit-line-clamp: 2;
                  -webkit-box-orient: vertical;
                  overflow: hidden;
                  line-height: 1.25;
                  font-size: 12.5px;
                }
                .products-compact-table .badge,
                .products-compact-table [class*="Badge"] {
                  font-size: 10.5px !important;
                  padding: 2px 5px !important;
                  line-height: 1.2;
                  white-space: normal;
                }
                .products-compact-table code { font-size: 11.5px; }
                /* Actions sticky right — sempre cliccabile anche con overflow */
                .products-compact-table td.prod-actions,
                .products-compact-table th.prod-actions {
                  position: sticky;
                  right: 0;
                  background: #fff;
                  box-shadow: -6px 0 6px -4px rgba(0,0,0,.06);
                  z-index: 2;
                }
                .products-compact-table tr:hover td.prod-actions { background: #f9fafb; }
                .products-compact-table .prod-actions button,
                .products-compact-table .prod-actions .btn { padding: 3px 8px !important; font-size: 11px !important; }
                /* Filtri responsive: grid compatta senza aumentare l'altezza */
                .products-compact-table-filters { display: grid;
                  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
                  gap: 8px 10px; align-items: flex-end; }
                .products-compact-table-filters .ds-input,
                .products-compact-table-filters select.ds-input,
                .products-compact-table-filters input.ds-input { width: 100%; min-width: 0 !important; }
              `}</style>
              <table className="ds-table" data-testid="prod-table">
                <colgroup>
                  <col style={{ width: 32 }} />
                  <col style={{ width: 44 }} />
                  <col style={{ width: 70 }} />
                  <col style={{ width: 220 }} />
                  <col style={{ width: 110 }} />
                  <col style={{ width: 72 }} />
                  <col style={{ width: 90 }} />
                  <col style={{ width: 88 }} />
                  <col style={{ width: 62 }} />
                  <col style={{ width: 76 }} />
                  <col style={{ width: 56 }} />
                  <col style={{ width: 78 }} />
                  <col style={{ width: 66 }} />
                  <col style={{ width: 104 }} />
                  <col style={{ width: 44 }} />
                  <col style={{ width: 96 }} />
                </colgroup>
                <thead><tr>
                  <th>
                    <input type="checkbox"
                           data-testid="prod-select-all"
                           checked={selectedIds.size === rows.length && rows.length > 0}
                           onChange={toggleAll} />
                  </th>
                  <th></th>
                  <th>SKU</th><th>Nome</th><th>Cat</th>
                  <th title="Profilo imballaggio">Profilo</th>
                  <th title="Profilo suggerito">Sugger.</th>
                  <th title="Confidenza suggerimento">Conf</th>
                  <th title="Kg per cartone">Kg/<br/>cart.</th>
                  <th title="Cartoni per pallet standard">Cart/<br/>pallet</th>
                  <th title="Dati logistici completi">Logist.</th>
                  <th title="Disponibilità">Disp.</th>
                  <th>Stato</th>
                  <th title="Nuovi prodotti da provare">Da provare</th>
                  <th title="PFA eligible">PFA</th>
                  <th className="prod-actions">Azioni</th>
                </tr></thead>
                <tbody>
                  {rows.map((p) => (
                    <tr key={p.id} data-testid={`prod-row-${p.sku}`}>
                      <td>
                        <input type="checkbox"
                               data-testid={`prod-select-${p.sku}`}
                               checked={selectedIds.has(p.id)}
                               onChange={() => toggleRow(p.id)} />
                      </td>
                      <td style={{ width: 40 }}>
                        {p.has_image ? (
                          <img src={p.image_url.startsWith("http") ? p.image_url : `${API}${p.image_url}`}
                               alt={p.sku}
                               style={{ width: 32, height: 32, objectFit: "cover", borderRadius: 4 }} />
                        ) : (
                          <div style={{ width: 32, height: 32, background: "#f3f4f6", borderRadius: 4 }} />
                        )}
                      </td>
                      <td><code>{p.sku}</code></td>
                      <td><div className="prod-name" title={p.name}>{p.name}</div></td>
                      <td>
                        {p.category_id
                          ? <Badge>{categories.find((c) => c.id === p.category_id)?.name || p.category_id.slice(0, 6)}</Badge>
                          : <span className="muted" title={p.category_source_code ? `ARGRUMER: ${p.category_source_code}` : ""}>
                              — {p.category_source_code && <span style={{ fontSize: 10 }}>({p.category_source_code})</span>}
                            </span>}
                      </td>
                      <td data-testid={`prod-pp-${p.sku}`}>
                        {p.packaging_profile_id
                          ? <Badge>{packagingProfiles.find((pp) => pp.id === p.packaging_profile_id)?.code || "?"}</Badge>
                          : <span className="muted">—</span>}
                      </td>
                      <td data-testid={`prod-sug-${p.sku}`}>
                        {p.suggested_packaging_profile_code
                          ? <Badge tone="info">{p.suggested_packaging_profile_code}</Badge>
                          : <span className="muted">—</span>}
                      </td>
                      <td data-testid={`prod-conf-${p.sku}`}>
                        {p.suggestion_confidence === "high" && <Badge tone="success">HIGH</Badge>}
                        {p.suggestion_confidence === "low" && <Badge tone="warning">LOW</Badge>}
                        {p.suggestion_confidence === "excluded" && <Badge tone="danger">EXCL</Badge>}
                        {!p.suggestion_confidence && <span className="muted">—</span>}
                        {p.suggestion_reason && (
                          <div className="muted" style={{ fontSize: 10, marginTop: 2 }}
                               title={p.suggestion_reason}>
                            {p.suggestion_reason.length > 26
                              ? p.suggestion_reason.slice(0, 24) + "…"
                              : p.suggestion_reason}
                          </div>
                        )}
                      </td>
                      <td>{p.kg_per_carton ?? "—"}</td>
                      <td>{p.cartons_per_std_pallet ?? "—"}</td>
                      <td data-testid={`prod-logistics-${p.sku}`}>
                        {p.logistics_complete
                          ? <Badge tone="success">✓</Badge>
                          : <Badge tone="warning" title="dati logistici incompleti">!</Badge>}
                      </td>
                      <td><Badge tone={p.availability === "AVAILABLE" ? "success" : "warning"}>{p.availability}</Badge></td>
                      <td><Badge tone={p.status === "ACTIVE" ? "success" : "neutral"}>{p.status}</Badge></td>
                      <td data-testid={`prod-try-${p.sku}`}>
                        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                          {p.show_in_try_products
                            ? <Badge tone="success">SÌ{p.try_products_priority != null ? ` · #${p.try_products_priority}` : ""}</Badge>
                            : <span className="muted">—</span>}
                          <Button size="sm" variant="ghost"
                                  data-testid={`prod-toggle-try-${p.sku}`}
                                  onClick={() => toggleTry(p)}
                                  title={p.show_in_try_products
                                    ? "Rimuovi da 'Da provare'"
                                    : "Segna come 'Da provare'"}
                                  style={{ padding: "2px 6px", fontSize: 11 }}>
                            {p.show_in_try_products ? "− rimuovi" : "+ segna"}
                          </Button>
                        </div>
                      </td>
                      <td>{p.pfa_eligible ? "✓" : "—"}</td>
                      <td className="prod-actions">
                        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                          <Button size="sm" onClick={() => setModal({ mode: "edit", data: p })} title="Modifica prodotto">Modifica</Button>
                          <Button size="sm" onClick={() => setDocsPanel(p)}
                                  data-testid={`prod-docs-${p.sku}`}>Documenti</Button>
                          <Button size="sm" onClick={() => toggle(p)}>
                            {p.status === "ACTIVE" ? "Disattiva" : "Attiva"}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {bulkStatus && (
                <div data-testid="prod-bulk-status"
                     role="status"
                     style={{ marginTop: 8, padding: "8px 12px", borderRadius: 8,
                              background: bulkStatus.startsWith("❌") ? "#fee2e2" : "#dcfce7",
                              border: `1px solid ${bulkStatus.startsWith("❌") ? "#fca5a5" : "#86efac"}`,
                              fontWeight: 500 }}>
                  {bulkStatus}
                  <button style={{ marginLeft: 12, background: "transparent", border: "none",
                                    cursor: "pointer", fontSize: 14 }}
                          onClick={() => setBulkStatus(null)}>✕</button>
                </div>
              )}
              {selectedIds.size > 0 && (
                <div data-testid="prod-bulk-bar"
                     style={{ position: "sticky", bottom: 0, background: "#fef3c7", border: "1px solid #fbbf24",
                              padding: "8px 12px", borderRadius: 8, marginTop: 8,
                              display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <strong>{selectedIds.size} prodotti selezionati</strong>
                  <span>·</span>
                  <label>Categoria:</label>
                  <select className="ds-input" value={bulkCatId}
                          data-testid="prod-bulk-cat-select"
                          onChange={(e) => setBulkCatId(e.target.value)}
                          style={{ minWidth: 180 }}>
                    <option value="">— nessuna (clear) —</option>
                    {categories.filter((c) => c.is_active !== false).map((c) => (
                      <option key={c.id} value={c.id}>{c.name} ({c.code})</option>
                    ))}
                  </select>
                  <Button variant="primary" data-testid="prod-bulk-assign"
                          onClick={doBulkAssign}>Assegna categoria</Button>
                  <span style={{ width: 8 }} />
                  <span>·</span>
                  <label>Profilo:</label>
                  <select className="ds-input" value={bulkPpId}
                          data-testid="prod-bulk-pp-select"
                          onChange={(e) => setBulkPpId(e.target.value)}
                          style={{ minWidth: 180 }}>
                    <option value="">— nessuno (clear) —</option>
                    {packagingProfiles.filter((pp) => pp.is_active !== false).map((pp) => (
                      <option key={pp.id} value={pp.id}>{pp.name} ({pp.code})</option>
                    ))}
                  </select>
                  <Button variant="primary" data-testid="prod-bulk-assign-pp"
                          onClick={doBulkAssignPp}>Assegna profilo imballaggio</Button>
                  <span style={{ width: 8 }} />
                  <span>·</span>
                  <Button variant="primary" data-testid="prod-bulk-confirm-suggestion"
                          onClick={doBulkConfirmSuggestion}>Conferma suggerimento packaging</Button>
                  <span style={{ width: 8 }} />
                  <span>·</span>
                  <label>Da provare:</label>
                  <Input type="number" min="0" style={{ width: 90 }}
                         placeholder="priorità"
                         value={bulkTryPriority}
                         data-testid="prod-bulk-try-priority"
                         onChange={(e) => setBulkTryPriority(e.target.value)} />
                  <Button variant="primary" data-testid="prod-bulk-try-on"
                          onClick={() => doBulkSetTry(true)}>+ Segna da provare</Button>
                  <Button data-testid="prod-bulk-try-off"
                          onClick={() => doBulkSetTry(false)}>− Rimuovi da provare</Button>
                  <Button data-testid="prod-bulk-clear"
                          onClick={() => setSelectedIds(new Set())}>Deseleziona</Button>
                </div>
              )}
            </div>
          )}
        </Card.Body>
      </Card>
      {modal && (
        <ProductModal mode={modal.mode} data={modal.data} categories={categories}
                      packagingProfiles={packagingProfiles}
                      onClose={() => setModal(null)}
                      onSubmit={async (payload) => {
                        if (modal.mode === "create") await apiPost("/api/admin/products", payload);
                        else await apiPatch(`/api/admin/products/${modal.data.id}`, payload);
                        setModal(null);
                        await load(buildQuery());
                      }} />
      )}
      {docsPanel && (
        <TechDocsModal product={docsPanel} onClose={() => setDocsPanel(null)} />
      )}
    </>
  );
}

function TechDocsModal({ product, onClose }) {
  const { apiGet, apiPatch, apiPost, token } = useAuth();
  const [docs, setDocs] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notifyDoc, setNotifyDoc] = useState(null);
  const [form, setForm] = useState({
    title: "", doc_type: "SCHEDA_TECNICA", version: "",
    effective_date: "", visible_to_customer: true, file: null,
  });

  const API = process.env.REACT_APP_BACKEND_URL;

  const load = async () => {
    try { setDocs(await apiGet(`/api/admin/products/${product.id}/documents`)); }
    catch (e) { setErr(e?.message || String(e)); setDocs([]); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [product.id]);

  const upload = async () => {
    if (!form.file || !form.title) { setErr("File e titolo obbligatori"); return; }
    setBusy(true); setErr(null);
    const fd = new FormData();
    fd.append("file", form.file);
    fd.append("title", form.title);
    fd.append("doc_type", form.doc_type);
    fd.append("visible_to_customer", String(form.visible_to_customer));
    if (form.version) fd.append("version", form.version);
    if (form.effective_date) fd.append("effective_date", form.effective_date);
    try {
      const r = await fetch(`${API}/api/admin/products/${product.id}/documents`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd,
      });
      if (!r.ok) throw new Error(await r.text());
      setForm({ ...form, file: null, title: "", version: "" });
      await load();
    } catch (e) { setErr(e.message || String(e)); }
    finally { setBusy(false); }
  };

  const toggleArchive = async (d) => {
    await apiPost(`/api/admin/documents/${d.id}/archive`, {});
    await load();
  };
  const toggleVisibility = async (d) => {
    await apiPatch(`/api/admin/documents/${d.id}`, { visible_to_customer: !d.visible_to_customer });
    await load();
  };
  const download = async (d) => {
    const r = await fetch(`${API}/api/documents/${d.id}/download`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!r.ok) { setErr("Download non autorizzato"); return; }
    const blob = await r.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = d.original_filename || `doc-${d.id}`;
    a.click();
  };

  return (
    <Modal title={`Documenti tecnici — ${product.sku} · ${product.name}`}
           onClose={onClose} testid="tech-docs-modal">
      {err && <Alert variant="danger">Errore: {String(err)}</Alert>}
      <Card style={{ background: "#fafafa", marginBottom: 12 }}>
        <Card.Header><strong>Upload nuovo documento</strong></Card.Header>
        <Card.Body>
          <div style={{ display: "grid", gap: 8 }}>
            <FormField label="Titolo *"><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="td-title" /></FormField>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              <FormField label="Tipo">
                <select className="ds-input" value={form.doc_type} onChange={(e) => setForm({ ...form, doc_type: e.target.value })} data-testid="td-type">
                  <option value="SCHEDA_TECNICA">Scheda Tecnica</option>
                  <option value="ALLERGENI">Allergeni</option>
                  <option value="INGREDIENTI">Ingredienti</option>
                  <option value="VALORI_NUTRIZIONALI">Valori Nutrizionali</option>
                  <option value="CERTIFICAZIONE">Certificazione</option>
                  <option value="ALTRO">Altro</option>
                </select>
              </FormField>
              <FormField label="Versione"><Input value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })} placeholder="v1.0" /></FormField>
            </div>
            <FormField label="Data effettiva"><Input type="date" value={form.effective_date} onChange={(e) => setForm({ ...form, effective_date: e.target.value })} /></FormField>
            <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <input type="checkbox" checked={form.visible_to_customer} onChange={(e) => setForm({ ...form, visible_to_customer: e.target.checked })} data-testid="td-visible" />
              Visibile ai clienti
            </label>
            <FormField label="File *"><input type="file" onChange={(e) => setForm({ ...form, file: e.target.files?.[0] })} data-testid="td-file" /></FormField>
            <Button variant="primary" onClick={upload} loading={busy} disabled={busy || !form.file || !form.title} data-testid="td-upload">
              {busy ? "Caricamento…" : "Carica documento"}
            </Button>
          </div>
        </Card.Body>
      </Card>

      <strong>Documenti esistenti ({docs?.length ?? "…"})</strong>
      {docs && docs.length === 0 && <Alert tone="info">Nessun documento caricato.</Alert>}
      {docs && docs.length > 0 && (
        <div className="ds-table-wrap" style={{ marginTop: 8 }}>
          <table className="ds-table" data-testid="td-table">
            <thead><tr><th>Titolo</th><th>Tipo</th><th>Ver.</th><th>Data</th><th>File</th><th>Cliente</th><th>Stato</th><th></th></tr></thead>
            <tbody>
              {docs.map((d) => (
                <tr key={d.id} data-testid={`td-row-${d.id}`}>
                  <td>{d.title}</td>
                  <td><code style={{ fontSize: ".8rem" }}>{d.doc_type}</code></td>
                  <td>{d.version || "—"}</td>
                  <td>{d.effective_date || "—"}</td>
                  <td style={{ fontSize: ".85rem" }}>{d.original_filename || "—"}<br />
                    <span className="muted">{d.size_bytes ? `${(d.size_bytes / 1024).toFixed(1)} KB` : ""}</span></td>
                  <td>{d.visible_to_customer ? <Badge tone="success">Sì</Badge> : <Badge tone="neutral">No</Badge>}</td>
                  <td><Badge tone={d.status === "ACTIVE" ? "success" : "neutral"}>{d.status}</Badge></td>
                  <td>
                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                      <Button size="sm" onClick={() => download(d)} data-testid={`td-download-${d.id}`}>Scarica</Button>
                      <Button size="sm" onClick={() => toggleVisibility(d)}>
                        {d.visible_to_customer ? "Nascondi cliente" : "Mostra cliente"}
                      </Button>
                      <Button size="sm" onClick={() => toggleArchive(d)}>
                        {d.status === "ACTIVE" ? "Archivia" : "Riattiva"}
                      </Button>
                      {d.doc_type === "SCHEDA_TECNICA"
                        && d.status === "ACTIVE"
                        && d.visible_to_customer && (
                        <Button size="sm" variant="primary"
                                onClick={() => setNotifyDoc(d)}
                                data-testid={`td-notify-${d.id}`}>
                          Notifica aggiornamento scheda
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {notifyDoc && (
        <TechSheetNotifyModal
          product={product}
          document={notifyDoc}
          onClose={() => setNotifyDoc(null)}
          onSent={() => { setNotifyDoc(null); load(); }}
        />
      )}
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// v43.31 — Technical Sheet Purchaser Notification Modal
// ─────────────────────────────────────────────────────────────────────────────

const _NOTIFY_STATUS_LABEL = {
  DA_NOTIFICARE:       { label: "Da notificare",           tone: "success" },
  GIA_NOTIFICATO:      { label: "Già notificato",          tone: "neutral" },
  NON_PILOT_SOSPESO:   { label: "NON-PILOT — sospeso durante pilot", tone: "warning" },
  NESSUN_DESTINATARIO: { label: "Senza destinatario",      tone: "danger" },
  CLIENTE_NON_ATTIVO:  { label: "Cliente non attivo",      tone: "danger" },
};

function TechSheetNotifyModal({ product, document, onClose, onSent }) {
  const { apiGet, apiPost } = useAuth();
  const [preview, setPreview] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [result, setResult] = useState(null);

  const load = async () => {
    setErr(null);
    try {
      const p = await apiGet(
        `/api/admin/products/${product.id}/technical-sheet/${document.id}/notification-preview`,
      );
      setPreview(p);
    } catch (e) { setErr(e?.message || String(e)); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [product.id, document.id]);

  const notifiableCount = preview?.counts?.notifiable_now ?? 0;
  const testMode = preview?.email_test_mode === true;

  const send = async () => {
    if (!confirmed || notifiableCount === 0) return;
    setBusy(true); setErr(null);
    try {
      const r = await apiPost(
        `/api/admin/products/${product.id}/technical-sheet/${document.id}/notify-purchasers`,
        {},
      );
      setResult(r);
    } catch (e) { setErr(e?.message || String(e)); }
    finally { setBusy(false); }
  };

  return (
    <Modal title="Aggiornamento scheda tecnica"
           onClose={onClose} testid="tech-sheet-notify-modal">
      {err && <Alert tone="danger">Errore: {String(err)}</Alert>}

      <Card style={{ marginBottom: 12 }}>
        <Card.Body>
          <div><strong>SKU:</strong> {product.sku}</div>
          <div><strong>Prodotto:</strong> {product.name}</div>
          <div style={{ marginTop: 6, fontSize: ".9rem", color: "#555" }}>
            <div><strong>File:</strong> {document.original_filename || "—"}</div>
            <div><strong>Versione:</strong> {document.version || "—"}
                 &nbsp;·&nbsp;<strong>Data validità:</strong> {document.effective_date || "—"}</div>
            <div style={{ color: "#888" }}>Lookback: ultimi 18 mesi</div>
          </div>
        </Card.Body>
      </Card>

      {!preview && !err && <div>Caricamento preview…</div>}

      {preview && (
        <>
          <div style={{ display: "grid",
                        gridTemplateColumns: "repeat(5, minmax(0,1fr))",
                        gap: 8, marginBottom: 12 }}>
            <StatBox label="Clienti acquirenti"    value={preview.counts.purchasers_18m}
                     testid="notify-count-purchasers" />
            <StatBox label="Notificabili ora"      value={preview.counts.notifiable_now}
                     tone="success" testid="notify-count-notifiable" />
            <StatBox label="Già notificati"        value={preview.counts.already_notified}
                     testid="notify-count-already" />
            <StatBox label="Non-PILOT sospesi"     value={preview.counts.non_pilot_suppressed}
                     tone="warning" testid="notify-count-suppressed" />
            <StatBox label="Senza destinatario"    value={preview.counts.missing_recipient}
                     tone="danger" testid="notify-count-missing" />
          </div>

          {testMode && (
            <Alert tone="warning" style={{ marginBottom: 12 }}>
              I clienti non-PILOT sono esclusi dall'invio durante la fase pilot.
            </Alert>
          )}

          <div className="ds-table-wrap" style={{ maxHeight: 360, overflow: "auto" }}>
            <table className="ds-table" data-testid="notify-recipients-table">
              <thead>
                <tr>
                  <th>Codice</th>
                  <th>Cliente</th>
                  <th>Ultimo acquisto</th>
                  <th>Destinatario</th>
                  <th>PILOT</th>
                  <th>Stato</th>
                </tr>
              </thead>
              <tbody>
                {preview.recipients.length === 0 && (
                  <tr><td colSpan={6} style={{ textAlign: "center", padding: 16 }}>
                    Nessun acquirente negli ultimi 18 mesi.
                  </td></tr>
                )}
                {preview.recipients.map((r) => {
                  const sc = _NOTIFY_STATUS_LABEL[r.notification_status]
                             || { label: r.notification_status, tone: "neutral" };
                  return (
                    <tr key={r.customer_id} data-testid={`notify-row-${r.customer_code}`}>
                      <td><code style={{ fontSize: ".85rem" }}>{r.customer_code}</code></td>
                      <td>{r.customer_name}</td>
                      <td style={{ fontSize: ".85rem" }}>
                        {r.last_purchase_at
                          ? new Date(r.last_purchase_at).toLocaleDateString("it-IT")
                          : "—"}
                      </td>
                      <td style={{ fontSize: ".85rem" }}>
                        {r.resolved_recipient || <span className="muted">—</span>}
                      </td>
                      <td>{r.pilot_member
                        ? <Badge tone="success">PILOT</Badge>
                        : <Badge tone="neutral">no</Badge>}</td>
                      <td><Badge tone={sc.tone}>{sc.label}</Badge></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {!result && (
            <div style={{ marginTop: 14, padding: 12,
                          background: "#f5f7fb", borderRadius: 6 }}>
              <label style={{ display: "flex", gap: 8, alignItems: "center",
                              fontSize: ".9rem" }}>
                <input type="checkbox"
                       checked={confirmed}
                       onChange={(e) => setConfirmed(e.target.checked)}
                       data-testid="notify-confirm" />
                <span>
                  Verrà inviata una comunicazione ai clienti eleggibili che hanno
                  acquistato questo prodotto negli ultimi 18 mesi.
                </span>
              </label>
              <div style={{ marginTop: 10, display: "flex", gap: 8 }}>
                <Button variant="primary"
                        disabled={!confirmed || notifiableCount === 0 || busy}
                        loading={busy}
                        onClick={send}
                        data-testid="notify-send">
                  {notifiableCount === 0
                    ? "Nessun cliente da notificare"
                    : `Invia notifica a ${notifiableCount} client${notifiableCount === 1 ? "e" : "i"}`}
                </Button>
                <Button variant="ghost" onClick={onClose}>Annulla</Button>
              </div>
            </div>
          )}

          {result && (
            <Alert tone="success" style={{ marginTop: 14 }}
                   data-testid="notify-result">
              <div><strong>{result.enqueued_count}</strong> notifiche create.</div>
              <div>{result.already_notified_count} già notificate ·&nbsp;
                   {result.non_pilot_suppressed_count} non-PILOT sospesi ·&nbsp;
                   {result.missing_recipient_count} senza destinatario</div>
              <div style={{ marginTop: 8 }}>
                <Button size="sm" variant="primary" onClick={onSent}>Chiudi</Button>
              </div>
            </Alert>
          )}
        </>
      )}
    </Modal>
  );
}

function StatBox({ label, value, tone = "neutral", testid }) {
  const bg = tone === "success" ? "#e6f7ec"
           : tone === "warning" ? "#fff8e1"
           : tone === "danger"  ? "#fdecea"
                                 : "#f4f5f7";
  const fg = tone === "success" ? "#127a2c"
           : tone === "warning" ? "#8a5a00"
           : tone === "danger"  ? "#a12b1f"
                                 : "#333";
  return (
    <div data-testid={testid}
         style={{ background: bg, color: fg, borderRadius: 6,
                  padding: "10px 12px", textAlign: "center" }}>
      <div style={{ fontSize: ".75rem", opacity: .85 }}>{label}</div>
      <div style={{ fontSize: "1.4rem", fontWeight: 700 }}>{value ?? 0}</div>
    </div>
  );
}

function ProductModal({ mode, data, categories, packagingProfiles = [], onClose, onSubmit }) {
  const { apiPost, apiDelete } = useAuth();
  const API = process.env.REACT_APP_BACKEND_URL;
  const [f, setF] = useState({
    sku: data.sku || "", name: data.name || "", description: data.description || "",
    category_id: data.category_id || (categories[0]?.id || ""),
    packaging_profile_id: data.packaging_profile_id || "",
    kg_per_carton: data.kg_per_carton || "",
    cartons_per_layer: data.cartons_per_layer || "",
    layers_per_std_pallet: data.layers_per_std_pallet || "",
    cartons_per_std_pallet: data.cartons_per_std_pallet || "",
    max_pallet_height_cm: data.max_pallet_height_cm || "",
    min_order_qty: data.min_order_qty || 1,
    availability: data.availability || "AVAILABLE",
    pfa_eligible: data.pfa_eligible ?? true,
    pallet_discount_eligible: data.pallet_discount_eligible ?? true,
    publish_web: data.publish_web ?? false,
    show_in_try_products: data.show_in_try_products ?? false,
    try_products_priority: data.try_products_priority ?? "",
    try_products_from: data.try_products_from ? data.try_products_from.slice(0, 10) : "",
    try_products_to: data.try_products_to ? data.try_products_to.slice(0, 10) : "",
  });
  const [imgUrl, setImgUrl] = useState(data.image_url || null);
  const [hasImage, setHasImage] = useState(!!data.has_image);
  const [imgErr, setImgErr] = useState(null);
  const [imgBusy, setImgBusy] = useState(false);

  const uploadImage = async (file) => {
    setImgErr(null); setImgBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      // Mar 2026 — Passa dal client canonico (apiPost) per beneficiare del
      // silent-refresh 401 → refresh cookie → retry-once. axios rileva
      // FormData e imposta multipart/form-data con boundary corretto.
      const j = await apiPost(`/api/admin/products/${data.id}/image`, fd);
      setImgUrl(j.image_url + "&t=" + Date.now()); setHasImage(true);
    } catch (e) { setImgErr(e.message || String(e)); }
    finally { setImgBusy(false); }
  };
  const clearImage = async () => {
    setImgErr(null); setImgBusy(true);
    try {
      await apiDelete(`/api/admin/products/${data.id}/image`);
      setImgUrl(null); setHasImage(false);
    } catch (e) { setImgErr(e.message || String(e)); }
    finally { setImgBusy(false); }
  };

  const numOr = (v) => v === "" || v === null || v === undefined ? null : Number(v);
  const submit = () => {
    const payload = {
      ...f,
      kg_per_carton: numOr(f.kg_per_carton),
      cartons_per_layer: numOr(f.cartons_per_layer),
      layers_per_std_pallet: numOr(f.layers_per_std_pallet),
      cartons_per_std_pallet: numOr(f.cartons_per_std_pallet),
      max_pallet_height_cm: numOr(f.max_pallet_height_cm),
      try_products_priority: numOr(f.try_products_priority),
      category_id: f.category_id || null,
      packaging_profile_id: f.packaging_profile_id || null,
      try_products_from: f.try_products_from || null,
      try_products_to: f.try_products_to || null,
    };
    onSubmit(payload);
  };

  return (
    <Modal title={mode === "create" ? "Nuovo prodotto" : `Modifica ${data.sku}`} onClose={onClose} testid="prod-modal">
      {mode === "create" && (
        <FormField label="SKU *"><Input value={f.sku} onChange={(e) => setF({ ...f, sku: e.target.value })} data-testid="prod-sku" /></FormField>
      )}
      <FormField label="Nome *"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="prod-name" /></FormField>
      <FormField label="Categoria">
        <select className="ds-input" value={f.category_id || ""} onChange={(e) => setF({ ...f, category_id: e.target.value })} data-testid="prod-category">
          <option value="">—</option>
          {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </FormField>
      <FormField label="Profilo imballaggio">
        <select className="ds-input" value={f.packaging_profile_id || ""}
                onChange={(e) => setF({ ...f, packaging_profile_id: e.target.value })}
                data-testid="prod-packaging-profile">
          <option value="">— nessuno —</option>
          {packagingProfiles.filter((pp) => pp.is_active !== false).map((pp) => (
            <option key={pp.id} value={pp.id}>{pp.name} ({pp.code})</option>
          ))}
        </select>
        <p className="muted" style={{ fontSize: 11, marginTop: 4 }}>
          Il profilo fornisce i valori pallet di default. Puoi comunque sovrascriverli sotto per singolo prodotto.
        </p>
      </FormField>
      {mode === "edit" && (
        <Card style={{ background: "#fafafa", margin: "8px 0" }}>
          <Card.Header><strong>Immagine prodotto</strong></Card.Header>
          <Card.Body>
            {imgErr && <Alert variant="danger">{String(imgErr)}</Alert>}
            <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
              <div data-testid="prod-image-preview"
                   style={{ width: 120, height: 120, border: "1px solid #e5e7eb", borderRadius: 8,
                            background: "#fff", display: "flex", alignItems: "center",
                            justifyContent: "center", overflow: "hidden" }}>
                {hasImage && imgUrl ? (
                  <img src={imgUrl.startsWith("http") ? imgUrl : `${API}${imgUrl}`}
                       alt={data.sku} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                ) : (
                  <span className="muted" style={{ fontSize: 11 }}>Nessuna immagine</span>
                )}
              </div>
              <div style={{ flex: 1 }}>
                <input type="file" accept="image/jpeg,image/png,image/webp"
                       data-testid="prod-image-input"
                       disabled={imgBusy}
                       onChange={(e) => e.target.files?.[0] && uploadImage(e.target.files[0])} />
                <p className="muted" style={{ fontSize: 11, marginTop: 6 }}>
                  JPG / PNG / WebP · max 5 MB
                </p>
                {hasImage && (
                  <Button size="sm" data-testid="prod-image-clear" disabled={imgBusy}
                          onClick={clearImage}>Rimuovi immagine</Button>
                )}
              </div>
            </div>
          </Card.Body>
        </Card>
      )}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <FormField label="Kg/cartone"><Input type="number" step="0.001" value={f.kg_per_carton} onChange={(e) => setF({ ...f, kg_per_carton: e.target.value })} /></FormField>
        <FormField label="Cartoni/pallet"><Input type="number" value={f.cartons_per_std_pallet} onChange={(e) => setF({ ...f, cartons_per_std_pallet: e.target.value })} /></FormField>
        <FormField label="Cartoni/strato"><Input type="number" value={f.cartons_per_layer} onChange={(e) => setF({ ...f, cartons_per_layer: e.target.value })} /></FormField>
        <FormField label="Strati/pallet"><Input type="number" value={f.layers_per_std_pallet} onChange={(e) => setF({ ...f, layers_per_std_pallet: e.target.value })} /></FormField>
      </div>
      <FormField label="Altezza max pallet (cm)"><Input type="number" step="0.01" value={f.max_pallet_height_cm} onChange={(e) => setF({ ...f, max_pallet_height_cm: e.target.value })} /></FormField>
      <FormField label="Disponibilità">
        <select className="ds-input" value={f.availability} onChange={(e) => setF({ ...f, availability: e.target.value })}>
          <option value="AVAILABLE">Disponibile</option>
          <option value="OUT_OF_STOCK">Non disponibile</option>
          <option value="ON_REQUEST">Su richiesta</option>
        </select>
      </FormField>
      <label style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8 }}>
        <input type="checkbox" checked={f.pfa_eligible} onChange={(e) => setF({ ...f, pfa_eligible: e.target.checked })} /> PFA eligibile
      </label>
      <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <input type="checkbox" checked={f.pallet_discount_eligible} onChange={(e) => setF({ ...f, pallet_discount_eligible: e.target.checked })} /> Sconto pallet eligibile
      </label>
      <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <input type="checkbox" checked={f.publish_web} data-testid="prod-publish-web"
               onChange={(e) => setF({ ...f, publish_web: e.target.checked })} /> Pubblica sul catalogo web
      </label>
      <Card style={{ background: "#fff7ed", margin: "12px 0", border: "1px solid #fed7aa" }}>
        <Card.Header><strong>Nuovi prodotti da provare</strong></Card.Header>
        <Card.Body>
          <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <input type="checkbox" checked={f.show_in_try_products}
                   data-testid="prod-show-in-try"
                   onChange={(e) => setF({ ...f, show_in_try_products: e.target.checked })} />
            Mostra in "Nuovi prodotti da provare"
          </label>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginTop: 8 }}>
            <FormField label="Priorità (min = prima)">
              <Input type="number" value={f.try_products_priority}
                     data-testid="prod-try-priority"
                     onChange={(e) => setF({ ...f, try_products_priority: e.target.value })} />
            </FormField>
            <FormField label="Data inizio">
              <Input type="date" value={f.try_products_from}
                     data-testid="prod-try-from"
                     onChange={(e) => setF({ ...f, try_products_from: e.target.value })} />
            </FormField>
            <FormField label="Data fine">
              <Input type="date" value={f.try_products_to}
                     data-testid="prod-try-to"
                     onChange={(e) => setF({ ...f, try_products_to: e.target.value })} />
            </FormField>
          </div>
        </Card.Body>
      </Card>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
        <Button onClick={onClose}>Annulla</Button>
        <Button variant="primary" onClick={submit} data-testid="prod-submit">Salva</Button>
      </div>
    </Modal>
  );
}

// ---------- PROMOTIONS ----------
function PromotionsTab() {
  const { rows, err, load, apiPost, apiPatch } = useCrud("/api/admin/promotions");
  const { apiPost: doPost } = useAuth();
  const [modal, setModal] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkStatus, setBulkStatus] = useState(null);
  useEffect(() => { load(""); /* eslint-disable-next-line */ }, []);
  const toggle = async (p) => { await doPost(`/api/admin/promotions/${p.id}/toggle-active`, {}); await load(""); };

  const toggleRow = (id) => {
    const s = new Set(selectedIds);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelectedIds(s);
  };
  const toggleAllPromo = () => {
    if (!rows) return;
    if (selectedIds.size === rows.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(rows.map((r) => r.id)));
  };
  // Bulk attiva/disattiva: N chiamate al toggle-active singolo (Feature Freeze,
  // no nuovo endpoint dedicato). Le promo sono max ~50 → ok in parallelo.
  const doBulkToggle = async (targetActive) => {
    setBulkStatus(null);
    const targets = (rows || []).filter(
      (r) => selectedIds.has(r.id) && r.is_active !== targetActive
    );
    if (targets.length === 0) {
      setBulkStatus(`ℹ️ Nessuna promozione da ${targetActive ? "attivare" : "disattivare"} nella selezione.`);
      return;
    }
    let ok = 0, ko = 0;
    for (const p of targets) {
      try {
        await doPost(`/api/admin/promotions/${p.id}/toggle-active`, {});
        ok++;
      } catch { ko++; }
    }
    setBulkStatus(`✅ ${ok} promozioni ${targetActive ? "attivate" : "disattivate"}${ko > 0 ? ` · ${ko} errori` : ""}`);
    setSelectedIds(new Set());
    await load("");
  };

  return (
    <>
      {err && <Alert variant="danger">Errore: {String(err)}</Alert>}
      <Card>
        <Card.Header>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <strong>Promozioni</strong>
            <Button variant="primary" onClick={() => setModal({ mode: "create", data: {} })} data-testid="promo-new">+ Nuova promozione</Button>
          </div>
        </Card.Header>
        <Card.Body>
          {!rows && <p className="muted">Caricamento…</p>}
          {rows && rows.length === 0 && <Alert tone="info">Nessuna promozione.</Alert>}
          {rows && rows.length > 0 && (
            <div className="ds-table-wrap">
              <table className="ds-table" data-testid="promo-table">
                <thead><tr>
                  <th style={{ width: 30 }}>
                    <input type="checkbox"
                           data-testid="promo-select-all"
                           checked={selectedIds.size === rows.length && rows.length > 0}
                           onChange={toggleAllPromo} />
                  </th>
                  <th>Codice</th><th>Nome</th><th>Tipo</th><th>Valore</th><th>Ambito</th><th>Validità</th><th>Priorità</th><th>Attiva</th><th></th>
                </tr></thead>
                <tbody>
                  {rows.map((p) => {
                    const scopeLabel = ({
                      ALL_PRODUCTS: "Tutti",
                      CATEGORY: "Categoria",
                      SINGLE_PRODUCT: "Singolo SKU",
                      MULTI_PRODUCT: `${(p.product_ids || []).length} SKU`,
                    })[p.product_scope] || "Tutti";
                    const typeLabel = p.discount_type === "CARTONI_OMAGGIO"
                      ? `Cartoni ${p.buy_cartons || 0}+${p.free_cartons || 0}`
                      : p.discount_type;
                    const excCount = (p.excluded_product_ids || []).length;
                    return (
                      <tr key={p.id} data-testid={`promo-row-${p.code}`}>
                        <td>
                          <input type="checkbox"
                                 data-testid={`promo-select-${p.code}`}
                                 checked={selectedIds.has(p.id)}
                                 onChange={() => toggleRow(p.id)} />
                        </td>
                        <td><code>{p.code}</code></td>
                        <td>{p.name}</td>
                        <td>{typeLabel}</td>
                        <td>{p.discount_type === "CARTONI_OMAGGIO" ? "—" : p.value}</td>
                        <td>
                          <span data-testid={`promo-scope-${p.code}`}>{scopeLabel}</span>
                          {excCount > 0 && (
                            <span style={{ marginLeft: 4, fontSize: 11, color: "#dc2626" }}>
                              (–{excCount})
                            </span>
                          )}
                        </td>
                        <td>{p.starts_at} → {p.ends_at}</td>
                        <td>{p.priority}</td>
                        <td><Badge tone={p.is_active ? "success" : "neutral"}>{p.is_active ? "Attiva" : "Disattiva"}</Badge></td>
                        <td>
                          <div style={{ display: "flex", gap: 4 }}>
                            <Button size="sm" onClick={() => setModal({ mode: "edit", data: p })}>Modifica</Button>
                            <Button size="sm" onClick={() => toggle(p)}>{p.is_active ? "Disattiva" : "Attiva"}</Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {bulkStatus && (
                <div data-testid="promo-bulk-status"
                     style={{ marginTop: 8, padding: "8px 12px", borderRadius: 8,
                              background: bulkStatus.startsWith("❌") ? "#fee2e2" : "#dcfce7",
                              border: `1px solid ${bulkStatus.startsWith("❌") ? "#fca5a5" : "#86efac"}` }}>
                  {bulkStatus}
                  <button style={{ marginLeft: 12, background: "transparent", border: "none",
                                    cursor: "pointer" }}
                          onClick={() => setBulkStatus(null)}>✕</button>
                </div>
              )}
              {selectedIds.size > 0 && (
                <div data-testid="promo-bulk-bar"
                     style={{ position: "sticky", bottom: 0, background: "#fef3c7", border: "1px solid #fbbf24",
                              padding: "8px 12px", borderRadius: 8, marginTop: 8,
                              display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <strong>{selectedIds.size} promozioni selezionate</strong>
                  <span>·</span>
                  <Button variant="primary" data-testid="promo-bulk-activate"
                          onClick={() => doBulkToggle(true)}>Attiva</Button>
                  <Button data-testid="promo-bulk-deactivate"
                          onClick={() => doBulkToggle(false)}>Disattiva</Button>
                  <Button data-testid="promo-bulk-clear"
                          onClick={() => setSelectedIds(new Set())}>Deseleziona</Button>
                </div>
              )}
            </div>
          )}
        </Card.Body>
      </Card>
      {modal && (
        <PromotionModal mode={modal.mode} data={modal.data}
                        onClose={() => setModal(null)}
                        onSubmit={async (payload) => {
                          if (modal.mode === "create") await apiPost("/api/admin/promotions", payload);
                          else await apiPatch(`/api/admin/promotions/${modal.data.id}`, payload);
                          setModal(null); await load("");
                        }} />
      )}
    </>
  );
}

function PromotionModal({ mode, data, onClose, onSubmit }) {
  const { apiGet } = useAuth();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [custCategories, setCustCategories] = useState([]);
  const [custSearch, setCustSearch] = useState("");
  const [prodSearch, setProdSearch] = useState("");
  const [excSearch, setExcSearch] = useState("");
  useEffect(() => {
    apiGet("/api/products").then(setProducts).catch(() => {});
    apiGet("/api/categories").then(setCategories).catch(() => {});
    apiGet("/api/customer-categories").then(setCustCategories).catch(() => setCustCategories([]));
    apiGet("/api/customers").then(setCustomers).catch(() => setCustomers([]));
    // eslint-disable-next-line
  }, []);
  const [f, setF] = useState({
    code: data.code || "",
    name: data.name || "",
    discount_type: data.discount_type || "PERCENT",
    value: data.value || 0,
    min_qty: data.min_qty || 1,
    starts_at: data.starts_at || new Date().toISOString().slice(0, 10),
    ends_at: data.ends_at || new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
    target_type: data.target_type || "ALL",
    target_ref: data.target_ref || null,
    priority: data.priority || 20,
    stackable: data.stackable ?? true,
    is_active: data.is_active ?? true,
    notes: data.notes || "",
    // product-side targeting
    product_scope: data.product_scope || "ALL_PRODUCTS",
    product_id: data.product_id || null,
    category_id: data.category_id || null,
    product_ids: data.product_ids || [],
    excluded_product_ids: data.excluded_product_ids || [],
    // cartoni omaggio
    buy_cartons: data.buy_cartons ?? null,
    free_cartons: data.free_cartons ?? null,
    free_product_mode: data.free_product_mode || "SAME_PRODUCT",
    free_product_id: data.free_product_id || null,
  });
  const [err, setErr] = useState(null);
  const isFree = f.discount_type === "CARTONI_OMAGGIO";
  const setTarget = (target_type) => {
    setF({ ...f, target_type, target_ref: null });
    setCustSearch("");
  };
  const setScope = (scope) => {
    setF({
      ...f, product_scope: scope,
      product_id: scope === "SINGLE_PRODUCT" ? f.product_id : null,
      category_id: scope === "CATEGORY" ? f.category_id : null,
      product_ids: scope === "MULTI_PRODUCT" ? f.product_ids : [],
    });
  };
  const filteredProds = (prodSearch
    ? products.filter(p => (p.sku || "").toLowerCase().includes(prodSearch.toLowerCase())
                          || (p.name || "").toLowerCase().includes(prodSearch.toLowerCase()))
    : products).slice(0, 50);
  const filteredExc = (excSearch
    ? products.filter(p => (p.sku || "").toLowerCase().includes(excSearch.toLowerCase())
                          || (p.name || "").toLowerCase().includes(excSearch.toLowerCase()))
    : products).slice(0, 50);
  const submit = () => {
    setErr(null);
    // Validazione destinatari
    if (f.target_type === "CATEGORY" && !f.target_ref) {
      setErr("Seleziona una categoria clienti"); return;
    }
    if (f.target_type === "CUSTOMER" && !f.target_ref) {
      setErr("Seleziona un cliente specifico"); return;
    }
    // Validazione ambito prodotti
    if (f.product_scope === "CATEGORY" && !f.category_id) {
      setErr("Seleziona una categoria prodotto"); return;
    }
    if (f.product_scope === "SINGLE_PRODUCT" && !f.product_id) {
      setErr("Seleziona un prodotto"); return;
    }
    if (f.product_scope === "MULTI_PRODUCT" && (!f.product_ids || f.product_ids.length === 0)) {
      setErr("Seleziona almeno un prodotto"); return;
    }
    // Prune fields the backend doesn't accept for regular discounts
    const payload = { ...f };
    if (f.target_type === "ALL") payload.target_ref = null;
    if (!isFree) {
      payload.buy_cartons = null;
      payload.free_cartons = null;
      payload.free_product_mode = null;
      payload.free_product_id = null;
    } else if (f.free_product_mode === "SAME_PRODUCT") {
      payload.free_product_id = null;
    }
    onSubmit(payload);
  };
  return (
    <Modal title={mode === "create" ? "Nuova promozione" : "Modifica promozione"} onClose={onClose} testid="promo-modal">
      {mode === "create" && (
        <FormField label="Codice *"><Input value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} data-testid="promo-code" /></FormField>
      )}
      <FormField label="Nome *"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></FormField>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <FormField label="Tipo sconto">
          <select className="ds-input" value={f.discount_type} data-testid="promo-discount-type"
                  onChange={(e) => setF({ ...f, discount_type: e.target.value })}>
            {DISCOUNT_TYPE_OPTIONS.map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
        </FormField>
        {!isFree && (
          <FormField label="Valore *"><Input type="number" step="0.01" value={f.value} onChange={(e) => setF({ ...f, value: parseFloat(e.target.value) })} /></FormField>
        )}
        <FormField label="Dal"><Input type="date" value={f.starts_at} onChange={(e) => setF({ ...f, starts_at: e.target.value })} /></FormField>
        <FormField label="Al"><Input type="date" value={f.ends_at} onChange={(e) => setF({ ...f, ends_at: e.target.value })} /></FormField>
        <FormField label="Q.tà minima"><Input type="number" value={f.min_qty} onChange={(e) => setF({ ...f, min_qty: parseInt(e.target.value) })} /></FormField>
        <FormField label="Priorità (min=applicata prima)"><Input type="number" value={f.priority} onChange={(e) => setF({ ...f, priority: parseInt(e.target.value) })} /></FormField>
      </div>

      {err && <Alert variant="danger" data-testid="promo-err">{err}</Alert>}

      {/* Feb 2026 v33 — Destinatari promozione (customer targeting).
          Riusa Promotion.target_type/target_ref già supportati dal pricing engine. */}
      <div style={{ marginTop: 12, padding: 12, background: "#EEF2FF", borderRadius: 6, border: "1px solid #C7D2FE" }}>
        <div style={{ fontWeight: 600, marginBottom: 8, color: "#3730A3" }}>Destinatari promozione</div>
        <FormField label="A chi si applica">
          <select className="ds-input" value={f.target_type} data-testid="promo-target-type"
                  onChange={(e) => setTarget(e.target.value)}>
            <option value="ALL">Tutti i clienti</option>
            <option value="CATEGORY">Categoria clienti</option>
            <option value="CUSTOMER">Cliente specifico</option>
          </select>
        </FormField>
        {f.target_type === "CATEGORY" && (
          <FormField label="Categoria clienti *">
            <select className="ds-input" value={f.target_ref || ""} data-testid="promo-target-category"
                    onChange={(e) => setF({ ...f, target_ref: e.target.value || null })}>
              <option value="">— Seleziona categoria —</option>
              {custCategories.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </FormField>
        )}
        {f.target_type === "CUSTOMER" && (
          <FormField label="Cliente specifico *">
            <Input placeholder="Cerca per codice o ragione sociale…" value={custSearch}
                   onChange={(e) => setCustSearch(e.target.value)} data-testid="promo-target-customer-search" />
            <div style={{ maxHeight: 160, overflow: "auto", border: "1px solid #E5E7EB",
                          borderRadius: 4, marginTop: 4, background: "white" }}>
              {(custSearch
                ? customers.filter(c =>
                    (c.code || "").toLowerCase().includes(custSearch.toLowerCase())
                    || (c.business_name || "").toLowerCase().includes(custSearch.toLowerCase()))
                : customers).slice(0, 30).map(c => (
                <label key={c.id} data-testid={`promo-target-customer-opt-${c.code}`}
                       style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 8px",
                                background: f.target_ref === c.id ? "#DBEAFE" : "transparent",
                                cursor: "pointer" }}>
                  <input type="radio" checked={f.target_ref === c.id}
                         onChange={() => setF({ ...f, target_ref: c.id })} />
                  <span style={{ fontSize: 13 }}>{c.code} — {c.business_name}</span>
                </label>
              ))}
            </div>
            {f.target_ref && (
              <div style={{ fontSize: 12, color: "#3730A3", marginTop: 4 }} data-testid="promo-target-customer-selected">
                Selezionato: {(customers.find(c => c.id === f.target_ref) || {}).business_name || f.target_ref}
              </div>
            )}
          </FormField>
        )}
      </div>

      {isFree && (
        <div style={{ marginTop: 12, padding: 12, background: "#F0F9FF", borderRadius: 6, border: "1px solid #BAE6FD" }}>
          <div style={{ fontWeight: 600, marginBottom: 8, color: "#0369A1" }}>Configurazione cartoni omaggio</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            <FormField label="Cartoni totali per soglia *"><Input type="number" min="1" value={f.buy_cartons ?? ""} data-testid="promo-buy-cartons"
                            onChange={(e) => setF({ ...f, buy_cartons: parseInt(e.target.value || "0") })} /></FormField>
            <FormField label="Cartoni omaggio inclusi *"><Input type="number" min="1" value={f.free_cartons ?? ""} data-testid="promo-free-cartons"
                            onChange={(e) => setF({ ...f, free_cartons: parseInt(e.target.value || "0") })} /></FormField>
          </div>
          <FormField label="Prodotto omaggio">
            <select className="ds-input" value={f.free_product_mode} data-testid="promo-free-mode"
                    onChange={(e) => setF({ ...f, free_product_mode: e.target.value })}>
              <option value="SAME_PRODUCT">Stesso prodotto (il gratis è dello stesso SKU maturato)</option>
              <option value="SPECIFIC_PRODUCT">Prodotto specifico (scegli qui sotto)</option>
            </select>
          </FormField>
          {f.free_product_mode === "SPECIFIC_PRODUCT" && (
            <FormField label="SKU omaggio *">
              <select className="ds-input" value={f.free_product_id || ""} data-testid="promo-free-product"
                      onChange={(e) => setF({ ...f, free_product_id: e.target.value || null })}>
                <option value="">— Seleziona —</option>
                {products.map(p => (
                  <option key={p.id} value={p.id}>{p.sku} — {p.name}</option>
                ))}
              </select>
            </FormField>
          )}
          {f.buy_cartons > 0 && f.free_cartons > 0 && f.free_cartons < f.buy_cartons && (
            <div style={{ fontSize: 12, color: "#0369A1", marginTop: 6 }} data-testid="promo-preview">
              <strong>{f.buy_cartons}</strong> cartoni totali → <strong>{f.buy_cartons - f.free_cartons}</strong> pagati + <strong>{f.free_cartons}</strong> omaggio.
              Con {f.buy_cartons * 2} totali: {(f.buy_cartons - f.free_cartons) * 2} pagati + {f.free_cartons * 2} omaggio.
              Con {f.buy_cartons - 1} nessun omaggio.
            </div>
          )}
          {f.buy_cartons > 0 && f.free_cartons > 0 && f.free_cartons >= f.buy_cartons && (
            <div style={{ fontSize: 12, color: "#B91C1C", marginTop: 6 }}>
              I cartoni omaggio devono essere strettamente minori dei cartoni totali.
            </div>
          )}
        </div>
      )}

      <div style={{ marginTop: 12, padding: 12, background: "#FEF3C7", borderRadius: 6, border: "1px solid #FCD34D" }}>
        <div style={{ fontWeight: 600, marginBottom: 8, color: "#92400E" }}>Ambito promozione (prodotti)</div>
        <FormField label="Ambito">
          <select className="ds-input" value={f.product_scope} data-testid="promo-scope"
                  onChange={(e) => setScope(e.target.value)}>
            <option value="ALL_PRODUCTS">Tutti i prodotti</option>
            <option value="CATEGORY">Categoria prodotto</option>
            <option value="SINGLE_PRODUCT">Singolo prodotto</option>
            <option value="MULTI_PRODUCT">Più prodotti selezionati</option>
          </select>
        </FormField>
        {f.product_scope === "CATEGORY" && (
          <FormField label="Categoria *">
            <select className="ds-input" value={f.category_id || ""} data-testid="promo-category"
                    onChange={(e) => setF({ ...f, category_id: e.target.value || null })}>
              <option value="">— Seleziona categoria —</option>
              {categories.map(c => (
                <option key={c.id} value={c.id}>{c.code} — {c.name}</option>
              ))}
            </select>
          </FormField>
        )}
        {f.product_scope === "SINGLE_PRODUCT" && (
          <FormField label="Prodotto *">
            <select className="ds-input" value={f.product_id || ""} data-testid="promo-product"
                    onChange={(e) => setF({ ...f, product_id: e.target.value || null })}>
              <option value="">— Seleziona prodotto —</option>
              {products.map(p => (
                <option key={p.id} value={p.id}>{p.sku} — {p.name}</option>
              ))}
            </select>
          </FormField>
        )}
        {f.product_scope === "MULTI_PRODUCT" && (
          <FormField label="Prodotti selezionati *">
            <Input placeholder="Cerca per SKU o nome…" value={prodSearch}
                   onChange={(e) => setProdSearch(e.target.value)} data-testid="promo-multi-search" />
            <div style={{ maxHeight: 180, overflow: "auto", border: "1px solid #E5E7EB",
                          borderRadius: 4, marginTop: 4, background: "white" }}>
              {filteredProds.map(p => {
                const on = f.product_ids.includes(p.id);
                return (
                  <label key={p.id} data-testid={`promo-multi-opt-${p.sku}`}
                         style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 8px",
                                  background: on ? "#FEF3C7" : "transparent", cursor: "pointer" }}>
                    <input type="checkbox" checked={on}
                           onChange={(e) => setF({ ...f,
                             product_ids: e.target.checked
                               ? [...f.product_ids, p.id]
                               : f.product_ids.filter(x => x !== p.id) })} />
                    <span style={{ fontSize: 13 }}>{p.sku} — {p.name}</span>
                  </label>
                );
              })}
            </div>
            <div style={{ fontSize: 12, color: "#666", marginTop: 4 }}>
              {f.product_ids.length} prodotti selezionati
            </div>
          </FormField>
        )}
      </div>

      <details style={{ marginTop: 12 }}>
        <summary style={{ cursor: "pointer", fontSize: 13, color: "#666" }}>Prodotti esclusi (opzionale)</summary>
        <div style={{ padding: 8, background: "#FEE2E2", borderRadius: 6, border: "1px solid #FCA5A5", marginTop: 6 }}>
          <Input placeholder="Cerca per SKU o nome…" value={excSearch}
                 onChange={(e) => setExcSearch(e.target.value)} data-testid="promo-exc-search" />
          <div style={{ maxHeight: 140, overflow: "auto", border: "1px solid #E5E7EB",
                        borderRadius: 4, marginTop: 4, background: "white" }}>
            {filteredExc.map(p => {
              const on = f.excluded_product_ids.includes(p.id);
              return (
                <label key={p.id} data-testid={`promo-exc-opt-${p.sku}`}
                       style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 8px",
                                background: on ? "#FEE2E2" : "transparent", cursor: "pointer" }}>
                  <input type="checkbox" checked={on}
                         onChange={(e) => setF({ ...f,
                           excluded_product_ids: e.target.checked
                             ? [...f.excluded_product_ids, p.id]
                             : f.excluded_product_ids.filter(x => x !== p.id) })} />
                  <span style={{ fontSize: 13 }}>{p.sku} — {p.name}</span>
                </label>
              );
            })}
          </div>
          <div style={{ fontSize: 12, color: "#666", marginTop: 4 }}>
            {f.excluded_product_ids.length} prodotti esclusi (non ricevono la promo)
          </div>
        </div>
      </details>

      <label style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 12 }}>
        <input type="checkbox" checked={f.stackable} onChange={(e) => setF({ ...f, stackable: e.target.checked })} /> Cumulabile
      </label>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
        <Button onClick={onClose}>Annulla</Button>
        <Button variant="primary" onClick={submit} data-testid="promo-submit">Salva</Button>
      </div>
    </Modal>
  );
}

// ---------- CONTRACTS ----------
function ContractsTab() {
  const { apiGet, apiPost, apiPatch, apiDelete } = useAuth();
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [cooperatives, setCooperatives] = useState([]);
  const [modal, setModal] = useState(null);
  const [expanded, setExpanded] = useState(null); // contract_id for showing promotions
  const [deleteDialog, setDeleteDialog] = useState(null); // { contract, check|null, loading, error }

  const load = async () => {
    try {
      const [cs, cust, coops] = await Promise.all([
        apiGet("/api/admin/contracts"),
        apiGet("/api/customers"),
        apiGet("/api/admin/cooperatives?active=true").catch(() => []),
      ]);
      setRows(cs); setCustomers(cust); setCooperatives(coops || []);
    } catch (e) { setErr(e?.message || String(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const customerById = Object.fromEntries(customers.map((c) => [c.id, c]));
  const coopById = Object.fromEntries((cooperatives || []).map((c) => [c.id, c]));

  return (
    <>
      {err && <Alert variant="danger">Errore: {String(err)}</Alert>}
      <Card>
        <Card.Header>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <strong>Contratti</strong>
            <Button variant="primary" onClick={() => setModal({ mode: "create", data: {} })} data-testid="contract-new">+ Nuovo contratto</Button>
          </div>
        </Card.Header>
        <Card.Body>
          {!rows && <p className="muted">Caricamento…</p>}
          {rows && rows.length === 0 && <Alert tone="info">Nessun contratto.</Alert>}
          {rows && rows.length > 0 && (
            <div className="ds-table-wrap">
              <table className="ds-table" data-testid="contract-table">
                <thead><tr><th>Codice</th><th>Tipo</th><th>Titolare</th><th>Nome</th><th>Validità</th><th>Attivo</th><th></th></tr></thead>
                <tbody>
                  {rows.map((c) => (
                    <React.Fragment key={c.id}>
                      <tr data-testid={`contract-row-${c.code}`}>
                        <td><code>{c.code}</code></td>
                        <td>
                          {c.owner_type === "COOPERATIVE"
                            ? <Badge tone="info">🏛️ Cooperativa · {c.aggregation_mode === "GROUP_AGGREGATED" ? "Gruppo" : "Individuale"}</Badge>
                            : <Badge tone="neutral">Cliente</Badge>}
                        </td>
                        <td>
                          {c.owner_type === "COOPERATIVE"
                            ? (coopById[c.cooperative_id]?.name || c.cooperative_id?.slice(0, 8) || "—")
                            : (customerById[c.customer_id]?.business_name || c.customer_id?.slice(0, 8) || "—")}
                        </td>
                        <td>{c.name}</td>
                        <td>{c.starts_at} → {c.ends_at}</td>
                        <td><Badge tone={c.is_active ? "success" : "neutral"}>{c.is_active ? "Attivo" : "Non attivo"}</Badge></td>
                        <td>
                          <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                            <Button size="sm" onClick={() => setModal({ mode: "edit", data: c })}>Modifica</Button>
                            <Button size="sm" onClick={() => setExpanded(expanded === c.id ? null : c.id)}
                                    data-testid={`contract-expand-${c.code}`}>
                              {expanded === c.id ? "▲ Chiudi" : "▼ Vista contratto"}
                            </Button>
                            <Button size="sm" variant="danger"
                                    data-testid={`contract-delete-${c.code}`}
                                    onClick={async () => {
                                      setDeleteDialog({ contract: c, check: null, loading: true, error: null });
                                      try {
                                        const chk = await apiGet(`/api/admin/contracts/${c.id}/delete-check`);
                                        setDeleteDialog({ contract: c, check: chk, loading: false, error: null });
                                      } catch (e) {
                                        setDeleteDialog({ contract: c, check: null, loading: false,
                                                          error: e?.data?.detail || e?.message || String(e) });
                                      }
                                    }}>Elimina</Button>
                          </div>
                        </td>
                      </tr>
                      {expanded === c.id && (
                        <tr><td colSpan={7} style={{ background: "#fafafa" }}>
                          <ContractFullPanel contract={c} />
                        </td></tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card.Body>
      </Card>
      {modal && (
        <ContractModal mode={modal.mode} data={modal.data} customers={customers}
                       cooperatives={cooperatives}
                       onClose={() => setModal(null)}
                       onSubmit={async (payload) => {
                         if (modal.mode === "create") await apiPost("/api/admin/contracts", payload);
                         else await apiPatch(`/api/admin/contracts/${modal.data.id}`, payload);
                         setModal(null); await load();
                       }}
                       onSubmitFull={async (full) => {
                         // Feb 2026 — single-save orchestrator
                         try {
                           await apiPost("/api/admin/contracts/full", full);
                           setModal(null);
                           await load();
                         } catch (e) {
                           const msg = e?.data?.detail || e?.message || String(e);
                           alert("Errore salvataggio contratto: " + msg
                             + "\n\nNessun contratto/promo/PFA è stato creato (rollback atomico).");
                           throw e;
                         }
                       }} />
      )}
      {deleteDialog && (
        <ContractDeleteDialog
          state={deleteDialog}
          onClose={() => setDeleteDialog(null)}
          onConfirm={async () => {
            try {
              setDeleteDialog({ ...deleteDialog, loading: true });
              await apiDelete(`/api/admin/contracts/${deleteDialog.contract.id}`);
              setDeleteDialog(null);
              await load();
            } catch (e) {
              const msg = e?.data?.detail || e?.message || String(e);
              setDeleteDialog({ ...deleteDialog, loading: false,
                                error: typeof msg === "string" ? msg : JSON.stringify(msg) });
            }
          }}
        />
      )}
    </>
  );
}

// Mar 2026 v41 — Safe Contract Delete dialog (Pre-Live).
// Chiama /delete-check in read-only al mount; mostra blockers oppure
// conferma forte solo se can_delete=true.
function ContractDeleteDialog({ state, onClose, onConfirm }) {
  const { contract, check, loading, error } = state;
  const canDelete = check && check.can_delete;
  return (
    <div style={{
      position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
      background: "rgba(0,0,0,0.55)", display: "flex",
      alignItems: "center", justifyContent: "center", zIndex: 1000,
    }} data-testid="contract-delete-dialog">
      <div style={{ background: "#fff", borderRadius: 8, maxWidth: 640,
                    width: "92%", maxHeight: "90vh", overflow: "auto",
                    padding: 24, boxShadow: "0 10px 40px rgba(0,0,0,0.3)" }}>
        <h3 style={{ margin: 0, color: "#B91C1C" }}>
          Eliminazione contratto <code>{contract.code}</code>
        </h3>
        <p style={{ color: "#555", marginTop: 6 }}>{contract.name}</p>

        {loading && !check && <p className="muted">Verifica dipendenze in corso…</p>}

        {error && (
          <Alert variant="danger" data-testid="contract-delete-error">
            {typeof error === "string" ? error : JSON.stringify(error)}
          </Alert>
        )}

        {check && (
          <>
            <div style={{ background: "#F9FAFB", border: "1px solid #E5E7EB",
                          borderRadius: 6, padding: 12, marginTop: 12 }}>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>Dipendenze collegate</div>
              <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13 }}>
                <li>PFA target: <strong>{check.dependencies.pfa_targets}</strong></li>
                <li>PFA events: <strong>{check.dependencies.pfa_events}</strong></li>
                <li>Scaglioni rebate: <strong>{check.dependencies.rebate_tiers}</strong></li>
                <li>Target prodotto: <strong>{check.dependencies.product_targets}</strong></li>
                <li>Attività marketing: <strong>{check.dependencies.marketing_activities}</strong></li>
                <li>Promozioni: <strong>{check.dependencies.promotions}</strong></li>
              </ul>
              {Object.keys(check.pfa_event_sources || {}).length > 0 && (
                <div style={{ marginTop: 8, fontSize: 12, color: "#374151" }}>
                  <em>PfaEvent per source:</em>{" "}
                  {Object.entries(check.pfa_event_sources).map(([k, v]) =>
                    <code key={k} style={{ marginRight: 8 }}>{k}={v}</code>)}
                </div>
              )}
            </div>

            {!canDelete && (
              <Alert variant="danger" data-testid="contract-delete-blockers">
                <div style={{ fontWeight: 600, marginBottom: 4 }}>
                  Eliminazione bloccata. Blockers:
                </div>
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {check.blockers.map((b, i) => <li key={i}><code>{b}</code></li>)}
                </ul>
              </Alert>
            )}

            {canDelete && (
              <div style={{ background: "#FEF2F2", border: "1px solid #FCA5A5",
                            borderRadius: 6, padding: 12, marginTop: 12 }}>
                <div style={{ fontWeight: 600, color: "#991B1B", marginBottom: 4 }}>
                  ⚠️ Conferma eliminazione definitiva
                </div>
                <div style={{ fontSize: 13, color: "#7F1D1D" }}>
                  Eliminare definitivamente questo contratto? Verranno eliminati
                  anche i PFA, gli scaglioni e gli eventi PFA ricostruibili
                  collegati.<br />
                  <strong>L'operazione non è reversibile.</strong>
                </div>
              </div>
            )}
          </>
        )}

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end",
                      marginTop: 16 }}>
          <Button onClick={onClose} data-testid="contract-delete-cancel">
            Annulla
          </Button>
          {canDelete && (
            <Button variant="danger" onClick={onConfirm}
                    disabled={loading}
                    data-testid="contract-delete-confirm">
              {loading ? "Eliminazione…" : "Elimina definitivamente"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// Feb 2026 — Accordion "Condizioni contrattuali avanzate".
// Contiene gli override opzionali per-contratto (advance_payment_allowed,
// advance_discount_percent, pallet_mono_discount_percent). NULL = fallback
// alla regola globale. Il pricing engine legge questi campi da Contract
// (pricing.py:876-937 e 1050-1059) — NON toccare la business logic.
// Il default è chiuso: si apre solo se già valorizzato (edit contratto
// con override attivi) oppure se l'utente lo espande manualmente.
function ContractAdvancedAccordion({ f, setF }) {
  const hasValues =
    (f.advance_payment_allowed !== null && f.advance_payment_allowed !== undefined) ||
    (f.advance_discount_percent !== "" && f.advance_discount_percent !== null && f.advance_discount_percent !== undefined) ||
    (f.pallet_mono_discount_percent !== "" && f.pallet_mono_discount_percent !== null && f.pallet_mono_discount_percent !== undefined);
  const [open, setOpen] = useState(hasValues);
  return (
    <div style={{ marginTop: 12, border: "1px solid #e5e7eb", borderRadius: 6 }}
         data-testid="contract-advanced-accordion">
      <button type="button"
              data-testid="contract-advanced-toggle"
              onClick={() => setOpen(!open)}
              style={{ width: "100%", background: open ? "#F1F5F9" : "#fff",
                       border: "none", padding: "10px 14px",
                       display: "flex", justifyContent: "space-between",
                       alignItems: "center", cursor: "pointer",
                       borderRadius: 6, fontWeight: 600, color: "#0F172A" }}>
        <span>
          {open ? "▼" : "▶"} Condizioni contrattuali avanzate
          {hasValues && !open && (
            <Badge tone="info" style={{ marginLeft: 8 }}>override attivi</Badge>
          )}
        </span>
        <span style={{ fontSize: 11, color: "#64748b", fontWeight: 400 }}>
          {open ? "Nascondi" : "Mostra"}
        </span>
      </button>
      {open && (
        <div style={{ padding: 14, borderTop: "1px solid #e5e7eb" }}>
          <div style={{ fontSize: 12, color: "#475569", marginBottom: 10 }}
               data-testid="contract-advanced-hint">
            Lascia vuoto per utilizzare le condizioni globali. Compila solo per
            applicare un override specifico a questo contratto.
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            <FormField label="Anticipato consentito">
              <select className="ds-input" value={f.advance_payment_allowed ?? "null"}
                      data-testid="contract-adv-allowed"
                      onChange={(e) => setF({ ...f, advance_payment_allowed:
                        e.target.value === "null" ? null : e.target.value === "true" })}>
                <option value="null">— (globale) —</option>
                <option value="true">Sì</option>
                <option value="false">No</option>
              </select>
            </FormField>
            <FormField label="% sconto anticipato (vuoto=globale, 0=nessuno)">
              <Input type="number" step="0.1" value={f.advance_discount_percent}
                     onChange={(e) => setF({ ...f, advance_discount_percent: e.target.value })}
                     data-testid="contract-advance-discount" />
            </FormField>
            <FormField label="% sconto pallet mono (vuoto=globale, 0=nessuno)">
              <Input type="number" step="0.1" value={f.pallet_mono_discount_percent}
                     onChange={(e) => setF({ ...f, pallet_mono_discount_percent: e.target.value })}
                     data-testid="contract-pallet-mono" />
            </FormField>
          </div>
          <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 6 }}>
            NULL = fallback globale. Valore esplicito (anche 0) = prevale su globale.
          </div>
        </div>
      )}
    </div>
  );
}

function ContractModal({ mode, data, customers, cooperatives = [], onClose, onSubmit, onSubmitFull }) {
  const { apiGet } = useAuth();
  const [f, setF] = useState({
    owner_type: data.owner_type || "CUSTOMER",
    customer_id: data.customer_id || (customers[0]?.id || ""),
    cooperative_id: data.cooperative_id || (cooperatives[0]?.id || ""),
    aggregation_mode: data.aggregation_mode || "INDIVIDUAL_CUSTOMER",
    code: data.code || "", name: data.name || "",
    starts_at: data.starts_at || new Date().toISOString().slice(0, 10),
    ends_at: data.ends_at || new Date(Date.now() + 365 * 86400000).toISOString().slice(0, 10),
    notes: data.notes || "", is_active: data.is_active ?? true,
    payment_method: data.payment_method || "",
    payment_days: data.payment_days ?? "",
    advance_payment_allowed: data.advance_payment_allowed ?? null,
    advance_discount_percent: data.advance_discount_percent ?? "",
    pallet_mono_discount_percent: data.pallet_mono_discount_percent ?? "",
    requires_new_product_for_payout: data.requires_new_product_for_payout ?? false,
  });
  const [preview, setPreview] = useState(null);
  const [previewErr, setPreviewErr] = useState(null);
  // Feb 2026 — Drafts per un UNICO SALVATAGGIO in create mode.
  // In edit mode i pannelli inline (ContractPromotionsPanel / ContractPfaInlinePanel)
  // gestiscono CRUD live, quindi questi draft NON vengono usati.
  const [promoDrafts, setPromoDrafts] = useState([]);
  const [pfaDraft, setPfaDraft] = useState(null); // {period_start, period_end, target_kg, status}
  // Feb 2026 — Scaglioni PFA draft. Riutilizza ContractRebateTier / endpoint
  // esistenti; in create sono salvati nella stessa transazione tramite
  // POST /contracts/full → rebate_tiers.
  const [tierDrafts, setTierDrafts] = useState([]);
  const [tiers, setTiers] = useState([]); // edit-mode: caricati da API
  const [promoEditor, setPromoEditor] = useState(null); // {index?, promo?}
  const [saving, setSaving] = useState(false);

  // Retroactive detection: starts_at < today (only for editing an existing contract)
  const isRetroactive = f.starts_at && f.starts_at < new Date().toISOString().slice(0, 10);
  const canPreview = mode === "edit" && data.id;

  const runPreview = async () => {
    setPreviewErr(null);
    try {
      const r = await apiGet(`/api/admin/contracts/${data.id}/backfill-pfa/preview`);
      setPreview(r);
    } catch (e) {
      setPreviewErr(String(e.message || e));
    }
  };
  const runApply = async () => {
    if (!window.confirm("Confermare l'applicazione del backfill PFA? L'operazione è idempotente e può essere ripetuta.")) return;
    try {
      const r = await fetch(`/api/admin/contracts/${data.id}/backfill-pfa/apply`, {
        method: "POST", headers: { "Authorization": `Bearer ${localStorage.getItem("token")}` },
      });
      const j = await r.json();
      alert(`Backfill applicato: ${j.inserted} eventi inseriti (${j.skipped_already} già presenti). ${j.total_delta_kg} kg recuperati.`);
      await runPreview();
    } catch (e) {
      alert("Errore: " + e.message);
    }
  };

  const submit = () => {
    const payload = { ...f };
    // Normalize empty strings to null (NULL = fallback global). Numbers to Number.
    for (const k of ["payment_method", "payment_days", "advance_discount_percent",
                     "pallet_mono_discount_percent"]) {
      if (payload[k] === "" || payload[k] === undefined) payload[k] = null;
      else if (["payment_days"].includes(k)) payload[k] = parseInt(payload[k]);
      else if (["advance_discount_percent", "pallet_mono_discount_percent"].includes(k))
        payload[k] = parseFloat(payload[k]);
    }
    // Phase C — enforce owner_type invariants client-side (server also enforces)
    if (mode === "create") {
      if (payload.owner_type === "CUSTOMER") {
        payload.cooperative_id = null;
        payload.aggregation_mode = "INDIVIDUAL_CUSTOMER";  // irrelevant for CUSTOMER
      } else {
        payload.customer_id = null;
      }
      // Feb 2026 — In create mode invochiamo l'endpoint orchestratore per
      // salvare Contract + Promozioni + PFA in un'unica transazione.
      if (onSubmitFull) {
        setSaving(true);
        // Sort tiers by threshold ascending per business rule
        const sortedTiers = [...tierDrafts].sort(
          (a, b) => Number(a.threshold_kg) - Number(b.threshold_kg));
        // Feb 2026 — Con scaglioni, il target è derivato server-side dal max
        // threshold. Inviamo target_kg: null così il backend deriva.
        let pfaOut = pfaDraft;
        if (pfaDraft && sortedTiers.length > 0) {
          pfaOut = { ...pfaDraft, target_kg: null };
        }
        onSubmitFull({
          contract: payload,
          promotions: promoDrafts,
          pfa: pfaOut,
          rebate_tiers: sortedTiers,
        }).finally(() => setSaving(false));
        return;
      }
    } else {
      // On edit, owner_type / customer_id / cooperative_id can NOT be changed
      delete payload.owner_type;
      delete payload.customer_id;
      delete payload.cooperative_id;
    }
    onSubmit(payload);
  };

  return (
    <Modal title={mode === "create" ? "Nuovo contratto" : "Modifica contratto"} onClose={onClose} testid="contract-modal">
      {mode === "create" && (
        <div style={{ padding: 10, background: "#F0F9FF", borderRadius: 6,
                      border: "1px solid #BAE6FD", marginBottom: 10 }}>
          <div style={{ fontWeight: 600, color: "#0369A1", marginBottom: 6 }}>
            Titolare del contratto
          </div>
          <FormField label="Tipo *">
            <select className="ds-input" value={f.owner_type}
                    onChange={(e) => setF({ ...f, owner_type: e.target.value })}
                    data-testid="contract-owner-type">
              <option value="CUSTOMER">Cliente diretto</option>
              <option value="COOPERATIVE">Cooperativa / Gruppo</option>
            </select>
          </FormField>
          {f.owner_type === "COOPERATIVE" && (
            <>
              <FormField label="Cooperativa *">
                <select className="ds-input" value={f.cooperative_id}
                        onChange={(e) => setF({ ...f, cooperative_id: e.target.value })}
                        data-testid="contract-cooperative">
                  {cooperatives.length === 0 && <option value="">— Nessuna cooperativa attiva —</option>}
                  {cooperatives.map((co) => <option key={co.id} value={co.id}>{co.code} · {co.name}</option>)}
                </select>
              </FormField>
              <FormField label="Modalità maturazione">
                <select className="ds-input" value={f.aggregation_mode}
                        onChange={(e) => setF({ ...f, aggregation_mode: e.target.value })}
                        data-testid="contract-aggregation-mode">
                  <option value="GROUP_AGGREGATED">GROUP_AGGREGATED — target unico sul totale cooperativa</option>
                  <option value="INDIVIDUAL_CUSTOMER">INDIVIDUAL_CUSTOMER — target per singolo membro, condizioni condivise</option>
                </select>
              </FormField>
              <div style={{ fontSize: 11, color: "#0369A1" }}>
                Ogni Rebate Tier / Product Target può forzare la modalità (GROUP/INDIVIDUAL) indipendentemente da questa impostazione.
              </div>
            </>
          )}
        </div>
      )}
      {mode === "create" && f.owner_type === "CUSTOMER" && (
        <FormField label="Cliente *">
          <select className="ds-input" value={f.customer_id} onChange={(e) => setF({ ...f, customer_id: e.target.value })} data-testid="contract-customer">
            {customers.map((c) => <option key={c.id} value={c.id}>{c.business_name}</option>)}
          </select>
        </FormField>
      )}
      {mode === "create" && <FormField label="Codice *"><Input value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} data-testid="contract-code" /></FormField>}
      <FormField label="Nome *"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></FormField>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <FormField label="Validità dal *"><Input type="date" value={f.starts_at}
                        onChange={(e) => setF({ ...f, starts_at: e.target.value })}
                        data-testid="contract-starts-at" /></FormField>
        <FormField label="Validità al *"><Input type="date" value={f.ends_at}
                        onChange={(e) => setF({ ...f, ends_at: e.target.value })}
                        data-testid="contract-ends-at" /></FormField>
      </div>

      {mode === "edit" && data.created_at && (
        <div style={{ fontSize: 12, color: "#666", marginTop: -4, marginBottom: 8 }}>
          Data inserimento nel sistema: <strong>{data.created_at.slice(0, 10)}</strong> (sola lettura)
        </div>
      )}

      {isRetroactive && (
        <div style={{ padding: 12, background: "#FEF3C7", borderRadius: 6, border: "1px solid #FCD34D", margin: "8px 0" }}
             data-testid="contract-retroactive-warning">
          <div style={{ fontWeight: 600, color: "#92400E" }}>⚠️ Contratto retroattivo</div>
          <div style={{ fontSize: 13, color: "#92400E", marginTop: 4 }}>
            La validità dal <strong>{f.starts_at}</strong> è antecedente alla data odierna.
            Il sistema può ricalcolare PFA e target sulle spedizioni già effettuate nel periodo.
            L'operazione è idempotente (nessun doppio conteggio).
          </div>
          {canPreview && (
            <div style={{ marginTop: 8, display: "flex", gap: 6 }}>
              <Button size="sm" onClick={runPreview} data-testid="contract-backfill-preview-btn">
                📊 Anteprima kg recuperabili
              </Button>
              {preview && preview.candidates_to_backfill > 0 && (
                <Button size="sm" variant="primary" onClick={runApply}
                        data-testid="contract-backfill-apply-btn">
                  ✅ Applica backfill ({preview.total_delta_kg} kg)
                </Button>
              )}
            </div>
          )}
          {preview && (
            <div style={{ marginTop: 8, padding: 8, background: "white", borderRadius: 4,
                          fontSize: 12 }} data-testid="contract-backfill-preview">
              <div><strong>Da recuperare:</strong> {preview.total_delta_kg} kg su{" "}
                {preview.candidates_to_backfill} shipment_lines eleggibili</div>
              {preview.candidates_already_recorded > 0 && (
                <div style={{ color: "#0369A1" }}>
                  Già backfilled precedentemente: {preview.already_recorded_kg} kg
                  ({preview.candidates_already_recorded} righe)
                </div>
              )}
            </div>
          )}
          {previewErr && (
            <div style={{ color: "#dc2626", marginTop: 6, fontSize: 12 }}>{previewErr}</div>
          )}
        </div>
      )}

      <div style={{ marginTop: 12, padding: 12, background: "#F0F9FF", borderRadius: 6, border: "1px solid #BAE6FD" }}>
        <div style={{ fontWeight: 600, color: "#0369A1", marginBottom: 8 }}>Condizioni di pagamento</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          <FormField label="Metodo pagamento (vuoto=globale)">
            <Input value={f.payment_method} placeholder="es. Bonifico 60gg"
                   onChange={(e) => setF({ ...f, payment_method: e.target.value })}
                   data-testid="contract-payment-method" />
          </FormField>
          <FormField label="Giorni (vuoto=globale)">
            <Input type="number" value={f.payment_days}
                   onChange={(e) => setF({ ...f, payment_days: e.target.value })} />
          </FormField>
        </div>
      </div>

      {/* Feb 2026 — Accordion "Condizioni contrattuali avanzate":
          gli sconti/override qui sono OPZIONALI. NULL = fallback alla regola
          globale. Compilare solo per override esplicito su questo contratto.
          Chiuso di default per non appesantire il form base. */}
      <ContractAdvancedAccordion f={f} setF={setF} />

      {/* Feb 2026 v31 — Erogabilità PFA condizionata a "nuovo inserimento SKU".
          Solo per contratti INDIVIDUAL_CUSTOMER o COOPERATIVE con
          aggregation_mode=INDIVIDUAL. NON applicabile a GROUP_AGGREGATED
          (regola business: cumulativa non usa requisito nuovo inserimento). */}
      {f.aggregation_mode !== "GROUP_AGGREGATED" && (
        <div style={{ marginTop: 10, padding: 10, background: "#FFF7ED",
                      borderRadius: 6, border: "1px solid #FED7AA" }}
             data-testid="contract-payout-gate">
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
            <input type="checkbox"
                   data-testid="contract-requires-new-product"
                   checked={!!f.requires_new_product_for_payout}
                   onChange={(e) => setF({ ...f, requires_new_product_for_payout: e.target.checked })} />
            <span><strong>Erogazione PFA condizionata a "nuovo inserimento SKU"</strong></span>
          </label>
          <div style={{ fontSize: 11, color: "#7C2D12", marginTop: 4 }}>
            La maturazione PFA continua normalmente. Il premio maturato diventa
            erogabile SOLO dopo che il customer acquista almeno uno SKU mai
            comprato prima. Non applicabile alle cooperative cumulative.
          </div>
        </div>
      )}

      {mode === "edit" && data.id && (
        <ContractPfaInlinePanel contract={data} />
      )}

      {/* Feb 2026 — In CREATE mode le sezioni Promozioni/PFA sono DRAFT locali,
          confermate solo al click su "Salva contratto" (un solo salvataggio). */}
      {mode === "create" && (
        <>
          <div style={{ marginTop: 12, padding: 12, background: "#FEF3C7",
                        borderRadius: 6, border: "1px solid #FCD34D" }}
               data-testid="contract-promo-draft-panel">
            <div style={{ display: "flex", justifyContent: "space-between",
                          alignItems: "center", marginBottom: 6 }}>
              <strong style={{ color: "#92400E" }}>Promozioni del contratto</strong>
              <Button size="sm" variant="primary"
                      data-testid="contract-promo-draft-add"
                      onClick={() => setPromoEditor({ index: null, promo: null })}>
                + Aggiungi promozione
              </Button>
            </div>
            {promoDrafts.length === 0 ? (
              <div className="muted" style={{ fontSize: 12 }}>
                Nessuna promozione predisposta. Puoi aggiungerne una o più — verranno
                create insieme al contratto in un solo salvataggio.
              </div>
            ) : (
              <table className="ds-table" style={{ fontSize: 12 }}
                     data-testid="contract-promo-draft-table">
                <thead><tr>
                  <th>Descrizione</th><th>Tipo</th><th>Valore</th><th>Ambito</th><th>Stato</th><th></th>
                </tr></thead>
                <tbody>
                  {promoDrafts.map((p, i) => (
                    <tr key={i} data-testid={`contract-promo-draft-row-${i}`}>
                      <td>{p.notes}</td>
                      <td>{p.discount_type}</td>
                      <td>{p.value}</td>
                      <td>{p.product_scope || "ALL_PRODUCTS"}</td>
                      <td>{p.status}</td>
                      <td style={{ display: "flex", gap: 4 }}>
                        <Button size="sm"
                                onClick={() => setPromoEditor({ index: i, promo: p })}>
                          Modifica
                        </Button>
                        <Button size="sm" variant="danger"
                                data-testid={`contract-promo-draft-remove-${i}`}
                                onClick={() => setPromoDrafts(promoDrafts.filter((_, j) => j !== i))}>
                          Rimuovi
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div style={{ marginTop: 12, padding: 12, background: "#F0FDF4",
                        borderRadius: 6, border: "1px solid #BBF7D0" }}
               data-testid="contract-pfa-draft-panel">
            <div style={{ display: "flex", justifyContent: "space-between",
                          alignItems: "center", marginBottom: 6 }}>
              <strong style={{ color: "#166534" }}>PFA del contratto</strong>
              <label style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 6 }}>
                <input type="checkbox"
                       data-testid="contract-pfa-draft-enable"
                       checked={!!pfaDraft}
                       onChange={(e) => setPfaDraft(e.target.checked
                         ? { period_start: f.starts_at, period_end: f.ends_at,
                             target_kg: "", status: "ACTIVE" }
                         : null)} />
                Configura PFA per questo contratto
              </label>
            </div>
            {pfaDraft && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                <FormField label="Inizio periodo *">
                  <Input type="date" value={pfaDraft.period_start}
                         data-testid="contract-pfa-draft-start"
                         onChange={(e) => setPfaDraft({ ...pfaDraft, period_start: e.target.value })} />
                </FormField>
                <FormField label="Fine periodo *">
                  <Input type="date" value={pfaDraft.period_end}
                         data-testid="contract-pfa-draft-end"
                         onChange={(e) => setPfaDraft({ ...pfaDraft, period_end: e.target.value })} />
                </FormField>
                {tierDrafts.length === 0 ? (
                  <FormField label="Target kg *">
                    <Input type="number" step="0.001" min="0" value={pfaDraft.target_kg}
                           data-testid="contract-pfa-draft-target"
                           onChange={(e) => setPfaDraft({ ...pfaDraft, target_kg: e.target.value })} />
                  </FormField>
                ) : (
                  <FormField label="Target massimo automatico">
                    <div data-testid="contract-pfa-draft-target-auto"
                         style={{ padding: "8px 10px", background: "#ECFDF5",
                                  border: "1px dashed #6EE7B7", borderRadius: 4,
                                  fontSize: 12, color: "#166534" }}>
                      {(() => {
                        const max = Math.max(...tierDrafts
                          .map((t) => Number(t.threshold_kg) || 0));
                        return max > 0
                          ? <><strong>{max.toLocaleString("it-IT")} kg</strong>{" "}
                              <span className="muted">
                                — derivato dallo scaglione più alto</span></>
                          : <span className="muted">
                              Definisci almeno una soglia negli scaglioni</span>;
                      })()}
                    </div>
                  </FormField>
                )}
                <FormField label="Stato">
                  <select className="ds-input" value={pfaDraft.status}
                          data-testid="contract-pfa-draft-status"
                          onChange={(e) => setPfaDraft({ ...pfaDraft, status: e.target.value })}>
                    <option value="ACTIVE">ACTIVE</option>
                    <option value="SUSPENDED">SUSPENDED</option>
                    <option value="CLOSED">CLOSED</option>
                  </select>
                </FormField>
              </div>
            )}

            {/* Scaglioni PFA — riutilizza ContractRebateTier */}
            <div style={{ marginTop: 12, paddingTop: 10, borderTop: "1px dashed #BBF7D0" }}
                 data-testid="contract-rebate-tiers-panel">
              <div style={{ display: "flex", justifyContent: "space-between",
                            alignItems: "center", marginBottom: 4 }}>
                <strong style={{ fontSize: 13, color: "#166534" }}>Scaglioni PFA</strong>
                <Button size="sm" variant="primary"
                        data-testid="contract-rebate-tier-add"
                        onClick={() => setTierDrafts([...tierDrafts,
                          { threshold_kg: "", reward_percent: "", cumulative: true }])}>
                  + Aggiungi scaglione
                </Button>
              </div>
              {tierDrafts.length === 0 ? (
                <div className="muted" style={{ fontSize: 11 }}>
                  Nessuno scaglione. Aggiungi per configurare premi incrementali
                  (es. 2.000 kg → +0,5%, 4.000 kg → +0,5%, ecc.).
                </div>
              ) : (
                <table className="ds-table" style={{ fontSize: 12 }}
                       data-testid="contract-rebate-tier-table">
                  <thead><tr>
                    <th>Soglia kg</th><th>% premio</th><th>Cumulativo</th><th></th>
                  </tr></thead>
                  <tbody>
                    {tierDrafts.map((t, i) => (
                      <tr key={i} data-testid={`contract-rebate-tier-row-${i}`}>
                        <td>
                          <Input type="number" step="1" min="0" value={t.threshold_kg}
                                 data-testid={`contract-rebate-tier-threshold-${i}`}
                                 style={{ width: 100 }}
                                 onChange={(e) => setTierDrafts(tierDrafts.map((x, j) =>
                                   j === i ? { ...x, threshold_kg: e.target.value } : x))} />
                        </td>
                        <td>
                          <Input type="number" step="0.001" min="0" value={t.reward_percent}
                                 data-testid={`contract-rebate-tier-reward-${i}`}
                                 style={{ width: 80 }}
                                 onChange={(e) => setTierDrafts(tierDrafts.map((x, j) =>
                                   j === i ? { ...x, reward_percent: e.target.value } : x))} />
                        </td>
                        <td>
                          <input type="checkbox" checked={!!t.cumulative}
                                 data-testid={`contract-rebate-tier-cumulative-${i}`}
                                 onChange={(e) => setTierDrafts(tierDrafts.map((x, j) =>
                                   j === i ? { ...x, cumulative: e.target.checked } : x))} />
                        </td>
                        <td>
                          <Button size="sm" variant="danger"
                                  data-testid={`contract-rebate-tier-remove-${i}`}
                                  onClick={() => setTierDrafts(tierDrafts.filter((_, j) => j !== i))}>
                            Rimuovi
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {promoEditor && (
            <PromoDraftEditor
              initial={promoEditor.promo}
              onClose={() => setPromoEditor(null)}
              onSave={(p) => {
                setPromoDrafts((prev) => {
                  if (promoEditor.index === null) return [...prev, p];
                  const copy = [...prev]; copy[promoEditor.index] = p; return copy;
                });
                setPromoEditor(null);
              }} />
          )}
        </>
      )}

      {mode === "edit" && data.id && (
        <ContractPromotionsPanel contractId={data.id} inline />
      )}
      <FormField label="Note"><Input value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></FormField>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
        <Button onClick={onClose} loading={saving} disabled={saving}>Annulla</Button>
        <Button variant="primary" onClick={submit} data-testid="contract-submit"
                loading={saving} disabled={saving}>
          {saving ? "Salvataggio…" : (mode === "create" ? "Salva contratto" : "Salva")}
        </Button>
      </div>
    </Modal>
  );
}

// Feb 2026 — Editor draft promozione usato SOLO in create Contract per
// preparare le ContractPromotion prima del salvataggio orchestrato.
// I campi rispecchiano il payload accettato da POST /contracts/full → promotions.
// Feb 2026 (patch UI consistency): stessi campi, label, validazioni e
// serializzazione del PromotionModal della tab Promozioni.
function PromoDraftEditor({ initial, onClose, onSave }) {
  const { apiGet } = useAuth();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  useEffect(() => {
    apiGet("/api/products").then(setProducts).catch(() => setProducts([]));
    apiGet("/api/categories").then(setCategories).catch(() => setCategories([]));
    // eslint-disable-next-line
  }, []);
  const [p, setP] = useState(() => initial ? { ...initial } : {
    notes: "", discount_type: "PERCENT", value: 0,
    product_scope: "ALL_PRODUCTS", status: "DRAFT",
    product_id: null, product_ids: [], category_id: null,
    valid_from: null, valid_to: null, month: null,
    trigger_type: null, duration_value: null, duration_unit: null,
    new_product_promo_scope: null,
    // Cartoni omaggio (BUY X + Y)
    buy_cartons: null, free_cartons: null,
    free_product_mode: "SAME_PRODUCT", free_product_id: null,
  });
  const isFree = p.discount_type === "CARTONI_OMAGGIO";
  const [err, setErr] = useState(null);
  const [productQ, setProductQ] = useState("");
  const submit = () => {
    setErr(null);
    if (!p.notes) { setErr("Descrizione/nome obbligatorio"); return; }
    if (!isFree) {
      if (p.value === "" || Number(p.value) < 0) {
        setErr("Valore non valido"); return;
      }
    } else {
      // Validazioni cartoni omaggio — stesse del PromotionModal / backend
      if (!p.buy_cartons || Number(p.buy_cartons) <= 0) {
        setErr("Cartoni da acquistare deve essere > 0"); return;
      }
      if (!p.free_cartons || Number(p.free_cartons) <= 0) {
        setErr("Cartoni omaggio deve essere > 0"); return;
      }
      if (p.free_product_mode === "SPECIFIC_PRODUCT" && !p.free_product_id) {
        setErr("Seleziona lo SKU omaggio"); return;
      }
    }
    // Mar 2026 — Product scope validation (allinea backend routes_admin_entities.py:1097)
    const scope = p.product_scope || "ALL_PRODUCTS";
    if (scope === "MULTI_PRODUCT" && (!p.product_ids || p.product_ids.length === 0)) {
      setErr("Seleziona almeno un articolo per la promozione Multi SKU"); return;
    }
    if (scope === "SINGLE_PRODUCT" && !p.product_id) {
      setErr("Seleziona un articolo"); return;
    }
    if (scope === "CATEGORY" && !p.category_id) {
      setErr("Seleziona una categoria"); return;
    }
    // Serializzazione allineata a PromotionModal.submit()
    const out = { ...p };
    if (!isFree) {
      out.value = Number(out.value);
      out.buy_cartons = null;
      out.free_cartons = null;
      out.free_product_mode = null;
      out.free_product_id = null;
    } else {
      out.value = 0;  // ignorato dal backend quando CARTONI_OMAGGIO
      out.buy_cartons = Number(out.buy_cartons);
      out.free_cartons = Number(out.free_cartons);
      if (out.free_product_mode === "SAME_PRODUCT") out.free_product_id = null;
    }
    // Mar 2026 — scope cleanup: non trascinare valori dello scope precedente
    out.product_scope = scope;
    out.product_ids  = scope === "MULTI_PRODUCT"  ? (out.product_ids || []) : null;
    out.product_id   = scope === "SINGLE_PRODUCT" ? (out.product_id || null) : null;
    out.category_id  = scope === "CATEGORY"       ? (out.category_id || null) : null;
    onSave(out);
  };
  return (
    <Modal title={initial ? "Modifica promozione (draft)" : "Nuova promozione (draft)"}
           onClose={onClose} testid="promo-draft-modal">
      <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
        Draft: verrà creato insieme al contratto al click su "Salva contratto".
      </div>
      <div style={{ padding: 8, background: "#F1F5F9", borderRadius: 4, marginBottom: 8,
                    border: "1px solid #CBD5E1", fontSize: 12, color: "#334155" }}
           data-testid="cp-target-note">
        Il destinatario è determinato dal contratto. Per una cooperativa puoi limitare la
        promozione a specifici membri tramite <strong>"Clienti membri"</strong>.
      </div>
      {err && <Alert variant="danger">{err}</Alert>}
      <FormField label="Descrizione / nome *">
        <Input value={p.notes}
               data-testid="promo-draft-name"
               onChange={(e) => setP({ ...p, notes: e.target.value })} />
      </FormField>
      {/* Feb 2026 v31 — Trigger dinamico "primo acquisto nuovo SKU" */}
      <div style={{ marginTop: 8, padding: 8, background: "#F8FAFC",
                    border: "1px solid #E2E8F0", borderRadius: 4 }}>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
          <input type="checkbox"
                 data-testid="promo-draft-trigger-enable"
                 checked={p.trigger_type === "FIRST_PURCHASE_NEW_SKU"}
                 onChange={(e) => setP({ ...p,
                   trigger_type: e.target.checked ? "FIRST_PURCHASE_NEW_SKU" : null,
                   duration_value: e.target.checked ? (p.duration_value ?? 1) : null,
                   duration_unit: e.target.checked ? (p.duration_unit ?? "MONTHS") : null,
                   new_product_promo_scope: e.target.checked ? (p.new_product_promo_scope ?? "TRIGGER_SKU_ONLY") : null })} />
          <span><strong>Trigger: primo acquisto nuovo SKU</strong> — customer-specific</span>
        </label>
        {p.trigger_type === "FIRST_PURCHASE_NEW_SKU" && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 2fr", gap: 6, marginTop: 6 }}>
            <FormField label="Durata">
              <Input type="number" min="1" value={p.duration_value ?? 1}
                     data-testid="promo-draft-duration-value"
                     onChange={(e) => setP({ ...p, duration_value: parseInt(e.target.value) })} />
            </FormField>
            <FormField label="Unità">
              <select className="ds-input" value={p.duration_unit ?? "MONTHS"}
                      data-testid="promo-draft-duration-unit"
                      onChange={(e) => setP({ ...p, duration_unit: e.target.value })}>
                <option value="DAYS">Giorni</option>
                <option value="WEEKS">Settimane</option>
                <option value="MONTHS">Mesi</option>
              </select>
            </FormField>
            <FormField label="Ambito sconto">
              <select className="ds-input" value={p.new_product_promo_scope ?? "TRIGGER_SKU_ONLY"}
                      data-testid="promo-draft-scope"
                      onChange={(e) => setP({ ...p, new_product_promo_scope: e.target.value })}>
                <option value="TRIGGER_SKU_ONLY">Solo il nuovo SKU</option>
                <option value="ALL_CUSTOMER_PURCHASES">Tutti gli acquisti del cliente</option>
              </select>
            </FormField>
          </div>
        )}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <FormField label="Tipo sconto">
          <select className="ds-input" value={p.discount_type}
                  data-testid="promo-draft-type"
                  onChange={(e) => setP({ ...p, discount_type: e.target.value })}>
            {DISCOUNT_TYPE_OPTIONS.map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
        </FormField>
        {!isFree && (
          <FormField label="Valore">
            <Input type="number" step="0.01" value={p.value}
                   data-testid="promo-draft-value"
                   onChange={(e) => setP({ ...p, value: e.target.value })} />
          </FormField>
        )}
        <FormField label="Ambito">
          <select className="ds-input" value={p.product_scope || "ALL_PRODUCTS"}
                  onChange={(e) => setP({ ...p, product_scope: e.target.value })}>
            <option value="ALL_PRODUCTS">Tutti</option>
            <option value="CATEGORY">Categoria</option>
            <option value="SINGLE_PRODUCT">Singolo SKU</option>
            <option value="MULTI_PRODUCT">Multi SKU</option>
          </select>
        </FormField>
        {/* Mar 2026 — Scope-conditional product/category picker (draft promo).
           Sostituisce il gap dove Multi SKU esponeva l'opzione ma non permetteva
           di selezionare gli articoli. Field wiring allineato al backend
           ContractPromotion (product_scope + product_id/product_ids/category_id). */}
        {p.product_scope === "CATEGORY" && (
          <FormField label="Categoria *">
            <select className="ds-input" value={p.category_id || ""}
                    data-testid="promo-draft-category"
                    onChange={(e) => setP({ ...p, category_id: e.target.value || null })}>
              <option value="">— Seleziona categoria —</option>
              {(categories || []).map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </FormField>
        )}
        {p.product_scope === "SINGLE_PRODUCT" && (
          <FormField label="Articolo *">
            <select className="ds-input" value={p.product_id || ""}
                    data-testid="promo-draft-product"
                    onChange={(e) => setP({ ...p, product_id: e.target.value || null })}>
              <option value="">— Seleziona SKU —</option>
              {products.map((pr) => (
                <option key={pr.id} value={pr.id}>{pr.sku} — {pr.name}</option>
              ))}
            </select>
          </FormField>
        )}
        {p.product_scope === "MULTI_PRODUCT" && (
          <FormField label="Articoli della promozione *">
            <div data-testid="promo-draft-multi-products"
                 style={{ padding: 8, background: "#F8FAFC",
                          border: "1px solid #E2E8F0", borderRadius: 6 }}>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
                {(p.product_ids || []).length === 0
                  ? <span style={{ color: "#94A3B8", fontSize: 12 }}>
                      Nessun articolo selezionato — cerca sotto per aggiungerne
                    </span>
                  : (p.product_ids || []).map((pid) => {
                      const pr = products.find((x) => x.id === pid);
                      return (
                        <span key={pid}
                              data-testid={`promo-draft-multi-chip-${pr?.sku || pid}`}
                              style={{ background: "#0F4C81", color: "#fff",
                                       padding: "2px 8px", borderRadius: 12,
                                       fontSize: 12, display: "inline-flex",
                                       alignItems: "center", gap: 6 }}>
                          {pr ? `${pr.sku} — ${pr.name}` : pid.slice(0, 8)}
                          <button type="button"
                                  data-testid={`promo-draft-multi-remove-${pr?.sku || pid}`}
                                  onClick={() => setP({ ...p,
                                    product_ids: (p.product_ids || []).filter((x) => x !== pid) })}
                                  style={{ background: "transparent", color: "#fff",
                                           border: "none", cursor: "pointer",
                                           padding: 0, fontSize: 14 }}>×</button>
                        </span>
                      );
                    })}
              </div>
              <Input placeholder="Cerca per SKU o nome…" value={productQ}
                     data-testid="promo-draft-multi-search"
                     onChange={(e) => setProductQ(e.target.value)} />
              {productQ.trim().length >= 1 && (
                <div style={{ maxHeight: 160, overflowY: "auto", marginTop: 6,
                              background: "#fff", border: "1px solid #E2E8F0",
                              borderRadius: 4 }}>
                  {products
                    .filter((pr) => !(p.product_ids || []).includes(pr.id) &&
                      (pr.sku.toLowerCase().includes(productQ.toLowerCase()) ||
                       (pr.name || "").toLowerCase().includes(productQ.toLowerCase())))
                    .slice(0, 30)
                    .map((pr) => (
                      <div key={pr.id}
                           data-testid={`promo-draft-multi-option-${pr.sku}`}
                           onClick={() => { setP({ ...p,
                             product_ids: [...(p.product_ids || []), pr.id] });
                             setProductQ(""); }}
                           style={{ padding: 6, cursor: "pointer", fontSize: 12,
                                    borderBottom: "1px solid #F1F5F9" }}>
                        <strong>{pr.sku}</strong> — {pr.name}
                      </div>
                    ))}
                </div>
              )}
              <div style={{ fontSize: 11, color: "#64748B", marginTop: 4 }}>
                {(p.product_ids || []).length} articoli selezionati · almeno 1 richiesto
              </div>
            </div>
          </FormField>
        )}
        <FormField label="Stato">
          <select className="ds-input" value={p.status || "DRAFT"}
                  onChange={(e) => setP({ ...p, status: e.target.value })}>
            <option value="DRAFT">DRAFT (placeholder)</option>
            <option value="ACTIVE">ACTIVE</option>
            <option value="ARCHIVED">ARCHIVED</option>
          </select>
        </FormField>
      </div>

      {isFree && (
        <div style={{ marginTop: 12, padding: 12, background: "#F0F9FF",
                      borderRadius: 6, border: "1px solid #BAE6FD" }}
             data-testid="promo-draft-free-config">
          <div style={{ fontWeight: 600, marginBottom: 8, color: "#0369A1" }}>
            Configurazione cartoni omaggio
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            <FormField label="Cartoni totali per soglia *">
              <Input type="number" min="1" value={p.buy_cartons ?? ""}
                     data-testid="promo-draft-buy-cartons"
                     onChange={(e) => setP({ ...p,
                       buy_cartons: parseInt(e.target.value || "0") })} />
            </FormField>
            <FormField label="Cartoni omaggio inclusi *">
              <Input type="number" min="1" value={p.free_cartons ?? ""}
                     data-testid="promo-draft-free-cartons"
                     onChange={(e) => setP({ ...p,
                       free_cartons: parseInt(e.target.value || "0") })} />
            </FormField>
          </div>
          <FormField label="Prodotto omaggio">
            <select className="ds-input" value={p.free_product_mode || "SAME_PRODUCT"}
                    data-testid="promo-draft-free-mode"
                    onChange={(e) => setP({ ...p, free_product_mode: e.target.value })}>
              {Object.entries(FREE_PRODUCT_MODE_LABELS).map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>
          </FormField>
          {p.free_product_mode === "SPECIFIC_PRODUCT" && (
            <FormField label="SKU omaggio *">
              <select className="ds-input" value={p.free_product_id || ""}
                      data-testid="promo-draft-free-product"
                      onChange={(e) => setP({ ...p, free_product_id: e.target.value || null })}>
                <option value="">— Seleziona —</option>
                {products.map((pr) => (
                  <option key={pr.id} value={pr.id}>{pr.sku} — {pr.name}</option>
                ))}
              </select>
            </FormField>
          )}
          {p.buy_cartons > 0 && p.free_cartons > 0 && p.free_cartons < p.buy_cartons && (
            <div style={{ fontSize: 12, color: "#0369A1", marginTop: 6 }} data-testid="promo-draft-preview">
              <strong>{p.buy_cartons}</strong> cartoni totali → <strong>{p.buy_cartons - p.free_cartons}</strong> pagati + <strong>{p.free_cartons}</strong> omaggio.
              Con {p.buy_cartons * 2} totali: {(p.buy_cartons - p.free_cartons) * 2} pagati + {p.free_cartons * 2} omaggio.
              Con {p.buy_cartons - 1} nessun omaggio.
            </div>
          )}
          {p.buy_cartons > 0 && p.free_cartons > 0 && p.free_cartons >= p.buy_cartons && (
            <div style={{ fontSize: 12, color: "#B91C1C", marginTop: 6 }}>
              I cartoni omaggio devono essere strettamente minori dei cartoni totali.
            </div>
          )}
        </div>
      )}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
        <Button onClick={onClose}>Annulla</Button>
        <Button variant="primary" onClick={submit}
                data-testid="promo-draft-submit">Conferma</Button>
      </div>
    </Modal>
  );
}

function ContractFullPanel({ contract }) {
  const { apiGet, apiPost, apiDelete } = useAuth();
  const [tab, setTab] = useState("progress");
  const [progress, setProgress] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [targets, setTargets] = useState([]);
  const [marketing, setMarketing] = useState([]);
  const [recon, setRecon] = useState(null);
  const [products, setProducts] = useState([]);
  const [tierModal, setTierModal] = useState(null);
  const [targetModal, setTargetModal] = useState(null);
  const [mktModal, setMktModal] = useState(null);

  const loadAll = async () => {
    const [p, t, pt, mk] = await Promise.all([
      apiGet(`/api/admin/contracts/${contract.id}/progress`),
      apiGet(`/api/admin/contracts/${contract.id}/rebate-tiers`),
      apiGet(`/api/admin/contracts/${contract.id}/product-targets`),
      apiGet(`/api/admin/contracts/${contract.id}/marketing`),
    ]);
    setProgress(p); setTiers(t); setTargets(pt); setMarketing(mk);
    if (products.length === 0) setProducts(await apiGet("/api/products").catch(() => []));
  };
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [contract.id]);

  const addTier = () => setTierModal({});
  const addTarget = () => setTargetModal({});
  const addMarketing = () => setMktModal({});
  const runReconPreview = async () => {
    setRecon(await apiGet(`/api/admin/contracts/${contract.id}/reconciliation/preview`));
  };
  const runReconApply = async () => {
    if (!window.confirm("Applicare la reconciliation (delta event append-only, idempotent)?")) return;
    const r = await fetch(`/api/admin/contracts/${contract.id}/reconciliation/apply`, {
      method: "POST", headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    });
    const j = await r.json();
    alert(`Reconciliation: ${j.status} - delta ${j.delta_kg} kg`);
    await runReconPreview(); await loadAll();
  };

  const TabBtn = ({ k, label }) => (
    <button data-testid={`ctab-${k}`}
            onClick={() => setTab(k)}
            style={{ padding: "6px 12px", border: "1px solid #cbd5e1",
                     borderBottom: tab === k ? "3px solid #0369A1" : "1px solid #cbd5e1",
                     background: tab === k ? "#dbeafe" : "white", cursor: "pointer" }}>
      {label}
    </button>
  );

  return (
    <div style={{ padding: 12 }}>
      <div style={{ display: "flex", gap: 4, marginBottom: 10, flexWrap: "wrap" }}>
        <TabBtn k="progress" label="📊 Avanzamento" />
        <TabBtn k="tiers" label={`🏆 Rebate (${tiers.length})`} />
        <TabBtn k="targets" label={`🎯 Target articolo (${targets.length})`} />
        <TabBtn k="promos" label="🎁 Promo contrattuali" />
        <TabBtn k="marketing" label={`📣 Marketing (${marketing.length})`} />
        <TabBtn k="attachments" label="📎 Allegati" />
        <TabBtn k="recon" label="🔄 Reconciliation" />
      </div>

      {tab === "progress" && progress && (
        <div>
          <div style={{ background: "#F0F9FF", padding: 10, borderRadius: 6, marginBottom: 10 }}>
            <strong>PFA cliente maturato:</strong> {progress.customer_pfa_maturato_kg} kg
            <span style={{ color: "#666", fontSize: 12, marginLeft: 8 }}>
              (periodo {progress.effective_from} → {progress.period_evaluated_to})
            </span>
            {progress.owner_type === "COOPERATIVE" && (
              <span style={{ marginLeft: 8, fontSize: 12, color: "#0369A1" }}>
                · Modalità: <strong>{progress.aggregation_mode}</strong>
              </span>
            )}
          </div>

          {/* Feb 2026 — Vista soci per Cooperativa INDIVIDUAL_CUSTOMER */}
          {progress.owner_type === "COOPERATIVE"
            && progress.aggregation_mode === "INDIVIDUAL_CUSTOMER"
            && progress.members && progress.members.length > 0 && (
            <div data-testid="coop-members-progress"
                 style={{ marginBottom: 12, padding: 10, background: "white",
                          border: "1px solid #BAE6FD", borderRadius: 6 }}>
              <h4 style={{ marginTop: 0, color: "#0369A1" }}>
                Avanzamento soci ({progress.members.length})
              </h4>
              <table className="ds-table" style={{ fontSize: 12 }}>
                <thead><tr>
                  <th>Socio</th>
                  <th style={{ textAlign: "right" }}>Kg maturati</th>
                  <th style={{ textAlign: "center" }}>Scaglioni raggiunti</th>
                  <th style={{ textAlign: "right" }}>% cumulativa</th>
                  <th style={{ textAlign: "right" }}>Kg prossimo scaglione</th>
                </tr></thead>
                <tbody>
                  {progress.members
                    .slice()
                    .sort((a, b) => Number(b.matured_kg) - Number(a.matured_kg))
                    .map((m) => {
                      const kg = Number(m.matured_kg || 0);
                      const tiers = (progress.rebate_tiers || [])
                        .map((t) => ({ th: Number(t.threshold_kg),
                                       rp: Number(t.reward_percent),
                                       cum: t.cumulative }))
                        .sort((a, b) => a.th - b.th);
                      let reached = 0, totalPct = 0, nextMissing = null;
                      for (const t of tiers) {
                        if (kg >= t.th) {
                          reached++;
                          totalPct = t.cum ? totalPct + t.rp : t.rp;
                        } else if (nextMissing === null) {
                          nextMissing = t.th - kg;
                        }
                      }
                      return (
                        <tr key={m.customer_id}
                            data-testid={`coop-member-row-${m.customer_id}`}>
                          <td>
                            <div style={{ fontWeight: 600 }}>
                              {m.customer_name || <span className="muted">Socio non identificato</span>}
                            </div>
                            {m.customer_code && (
                              <div style={{ fontSize: 11, color: "#64748B",
                                            fontFamily: "monospace" }}>
                                {m.customer_code}
                              </div>
                            )}
                            {!m.customer_name && (
                              <div style={{ fontSize: 10, color: "#94A3B8",
                                            fontFamily: "monospace" }}>
                                {m.customer_id.slice(0, 8)}…
                              </div>
                            )}
                          </td>
                          <td style={{ textAlign: "right" }}>
                            <strong>{kg.toLocaleString("it-IT")}</strong> kg
                          </td>
                          <td style={{ textAlign: "center" }}>
                            {reached}/{tiers.length}
                          </td>
                          <td style={{ textAlign: "right" }}>
                            {totalPct > 0
                              ? <Badge tone="success">{totalPct.toFixed(2)}%</Badge>
                              : <span className="muted">—</span>}
                          </td>
                          <td style={{ textAlign: "right" }}>
                            {nextMissing !== null
                              ? `${nextMissing.toLocaleString("it-IT")} kg`
                              : <span style={{ color: "#059669" }}>Tutti raggiunti</span>}
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          )}

          <h4>Rebate tiers ({progress.rebate_tiers.length})</h4>
          {progress.rebate_tiers.map((r, i) => (
            <div key={i} style={{ padding: 8, background: r.reached ? "#DCFCE7" : "white",
                                  border: "1px solid #E5E7EB", borderRadius: 4, marginBottom: 4 }}>
              <div><strong>{r.threshold_kg} kg</strong> → {r.reward_percent}% ({r.cumulative ? "cumulativo" : "non cumulativo"}) —{" "}
                Base: {r.calculation_basis}, Metodo: {r.settlement_method}</div>
              <div style={{ fontSize: 12 }}>
                Maturato: <strong>{r.maturato_kg} kg</strong> · Mancanti: {r.missing_kg} kg ·{" "}
                {r.reached ? <span style={{ color: "#059669" }}>✅ Raggiunto</span> :
                             <span style={{ color: "#DC2626" }}>❌ Non raggiunto</span>}
              </div>
              {r.reward_note && <div style={{ fontSize: 11, color: "#92400E", fontStyle: "italic" }}>ℹ️ {r.reward_note}</div>}
            </div>
          ))}
          <h4 style={{ marginTop: 12 }}>Target articolo ({progress.product_targets.length})</h4>
          {progress.product_targets.map((t, i) => (
            <div key={i} style={{ padding: 8, background: t.reached ? "#DCFCE7" : "white",
                                  border: "1px solid #E5E7EB", borderRadius: 4, marginBottom: 4 }}>
              <div><strong>{t.product_sku}</strong> — {t.product_name}</div>
              <div style={{ fontSize: 12 }}>
                Target: {t.target_kg} kg · Maturato: <strong>{t.maturato_kg} kg</strong>{" "}
                ({t.percent_reached}%) · Reward: {t.reward_percent}% · Base: {t.calculation_basis}
              </div>
              {t.potential_reward_eur && (
                <div style={{ fontSize: 12, color: "#059669" }}>
                  💰 Premio potenziale: € {t.potential_reward_eur}
                </div>
              )}
              {t.reward_note && <div style={{ fontSize: 11, color: "#92400E", fontStyle: "italic" }}>ℹ️ {t.reward_note}</div>}
            </div>
          ))}
        </div>
      )}

      {tab === "tiers" && (
        <div>
          <Button size="sm" variant="primary" onClick={addTier} data-testid="add-tier">+ Aggiungi tier</Button>
          <table className="ds-table" style={{ marginTop: 8 }}>
            <thead><tr><th>Soglia kg</th><th>Reward %</th><th>Cumulativo</th><th>Base calcolo</th><th>Settlement</th><th></th></tr></thead>
            <tbody>
              {tiers.map(t => (
                <tr key={t.id}>
                  <td>{t.threshold_kg}</td><td>{t.reward_percent}%</td>
                  <td>{t.cumulative ? "Sì" : "No"}</td>
                  <td><code>{t.calculation_basis}</code></td>
                  <td><code>{t.settlement_method}</code></td>
                  <td><Button size="sm" variant="danger"
                              onClick={async () => { await apiDelete(`/api/admin/rebate-tiers/${t.id}`); await loadAll(); }}>✕</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "targets" && (
        <div>
          <Button size="sm" variant="primary" onClick={addTarget} data-testid="add-target">+ Aggiungi target articolo</Button>
          <table className="ds-table" style={{ marginTop: 8 }}>
            <thead><tr><th>SKU</th><th>Target kg</th><th>Reward %</th><th>Base</th><th></th></tr></thead>
            <tbody>
              {targets.map(t => {
                const prod = products.find(p => p.id === t.product_id);
                return (
                  <tr key={t.id}>
                    <td>{prod?.sku || t.product_id.slice(0, 8)}</td>
                    <td>{t.target_kg}</td><td>{t.reward_percent}%</td>
                    <td><code>{t.calculation_basis}</code></td>
                    <td><Button size="sm" variant="danger"
                                onClick={async () => { await apiDelete(`/api/admin/product-targets/${t.id}`); await loadAll(); }}>✕</Button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {tab === "promos" && <ContractPromotionsPanel contractId={contract.id} />}

      {tab === "marketing" && (
        <div>
          <Button size="sm" variant="primary" onClick={addMarketing} data-testid="add-marketing">+ Aggiungi attività</Button>
          <table className="ds-table" style={{ marginTop: 8 }}>
            <thead><tr><th></th><th>Tipo</th><th>Descrizione</th><th>Valore €</th><th>Data</th><th>Stato</th><th></th></tr></thead>
            <tbody>
              {marketing.map(m => (
                <MarketingRow key={m.id} m={m}
                  onDelete={async () => { await apiDelete(`/api/admin/marketing-activities/${m.id}`); await loadAll(); }} />
              ))}
            </tbody>
          </table>
          <div style={{ fontSize: 11, color: "#666", marginTop: 8 }}>
            📎 Espandi una riga (▸) per gestire gli allegati privati (Object Storage, RBAC-enforced).
          </div>
        </div>
      )}

      {tab === "attachments" && (
        <>
          <ContractSignedPdfBlock contractId={contract.id} />
          <AttachmentsPanel ownerType="contracts" ownerId={contract.id} />
        </>
      )}

      {tab === "recon" && (
        <div>
          <Button size="sm" onClick={runReconPreview} data-testid="recon-preview">📊 Anteprima delta reconciliation</Button>
          {recon && (
            <div style={{ padding: 10, background: "#FEF3C7", borderRadius: 4, marginTop: 8 }}>
              <div><strong>Recorded:</strong> {recon.recorded_kg} kg</div>
              <div><strong>Theoretical:</strong> {recon.theoretical_kg} kg</div>
              <div><strong>Delta:</strong> {recon.delta_kg} kg ({recon.delta_direction})</div>
              {recon.requires_action && (
                <Button size="sm" variant="primary" onClick={runReconApply} data-testid="recon-apply">
                  ✅ Applica delta (append-only)
                </Button>
              )}
            </div>
          )}
          <div style={{ fontSize: 11, color: "#666", marginTop: 8 }}>
            La reconciliation genera un evento PFA delta append-only. Non modifica mai eventi storici.
          </div>
        </div>
      )}

      {tierModal && (
        <RebateTierModal onClose={() => setTierModal(null)} onSubmit={async (p) => {
          await apiPost(`/api/admin/contracts/${contract.id}/rebate-tiers`, p);
          setTierModal(null); await loadAll();
        }} />
      )}
      {targetModal && (
        <ProductTargetModal products={products} onClose={() => setTargetModal(null)}
          onSubmit={async (p) => {
            await apiPost(`/api/admin/contracts/${contract.id}/product-targets`, p);
            setTargetModal(null); await loadAll();
          }} />
      )}
      {mktModal && (
        <MarketingModal onClose={() => setMktModal(null)} onSubmit={async (p) => {
          await apiPost(`/api/admin/contracts/${contract.id}/marketing`, p);
          setMktModal(null); await loadAll();
        }} />
      )}
    </div>
  );
}



function RebateTierModal({ onClose, onSubmit }) {
  const [f, setF] = useState({
    threshold_kg: 15000, reward_percent: 1.0,
    cumulative: true, calculation_basis: "MANUAL", settlement_method: "MANUAL",
    additional_conditions: "", valid_from: "", valid_to: "", is_active: true,
  });
  const [err, setErr] = useState(null);
  const submit = () => {
    setErr(null);
    if (!f.threshold_kg || Number(f.threshold_kg) <= 0) return setErr("Soglia kg > 0 obbligatoria");
    if (isNaN(Number(f.reward_percent))) return setErr("Reward % non valido");
    onSubmit({
      threshold_kg: Number(f.threshold_kg), reward_percent: Number(f.reward_percent),
      cumulative: f.cumulative,
      calculation_basis: f.calculation_basis, settlement_method: f.settlement_method,
      additional_conditions: f.additional_conditions || null,
      valid_from: f.valid_from || null, valid_to: f.valid_to || null,
      is_active: f.is_active,
    });
  };
  return (
    <Modal title="Nuovo rebate tier" onClose={onClose} testid="rebate-modal">
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <FormField label="Soglia kg *"><Input type="number" value={f.threshold_kg}
                        onChange={(e) => setF({ ...f, threshold_kg: e.target.value })}
                        data-testid="rebate-threshold" /></FormField>
        <FormField label="Reward % *"><Input type="number" step="0.01" value={f.reward_percent}
                        onChange={(e) => setF({ ...f, reward_percent: e.target.value })} /></FormField>
        <FormField label="Calculation basis">
          <select className="ds-input" value={f.calculation_basis} data-testid="rebate-basis"
                  onChange={(e) => setF({ ...f, calculation_basis: e.target.value })}>
            <option value="MANUAL">MANUAL — Importo calcolato manualmente</option>
            <option value="NET_REVENUE_YEAR">NET_REVENUE_YEAR — Base € da consolidare</option>
            <option value="REVENUE_UP_TO_TARGET">REVENUE_UP_TO_TARGET — Custom</option>
            <option value="REVENUE_ABOVE_TARGET">REVENUE_ABOVE_TARGET — Custom</option>
          </select>
        </FormField>
        <FormField label="Settlement method">
          <select className="ds-input" value={f.settlement_method}
                  onChange={(e) => setF({ ...f, settlement_method: e.target.value })}>
            <option value="MANUAL">MANUAL</option>
            <option value="CREDIT_NOTE">CREDIT_NOTE (nota di credito)</option>
          </select>
        </FormField>
        <FormField label="Valida dal"><Input type="date" value={f.valid_from}
                        onChange={(e) => setF({ ...f, valid_from: e.target.value })} /></FormField>
        <FormField label="Valida al"><Input type="date" value={f.valid_to}
                        onChange={(e) => setF({ ...f, valid_to: e.target.value })} /></FormField>
      </div>
      <label style={{ display: "flex", gap: 6, marginTop: 8 }}>
        <input type="checkbox" checked={f.cumulative}
               onChange={(e) => setF({ ...f, cumulative: e.target.checked })} /> Cumulativo con altri tier
      </label>
      <FormField label="Condizioni aggiuntive"><Input value={f.additional_conditions}
                      onChange={(e) => setF({ ...f, additional_conditions: e.target.value })}
                      placeholder="es. inserimento di almeno N nuovi articoli" /></FormField>
      {err && <div style={{ color: "#dc2626", fontSize: 12 }}>{err}</div>}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
        <Button onClick={onClose}>Annulla</Button>
        <Button variant="primary" onClick={submit} data-testid="rebate-submit">Salva</Button>
      </div>
    </Modal>
  );
}


function ProductTargetModal({ products, onClose, onSubmit }) {
  const [f, setF] = useState({
    product_id: "", target_kg: 500, reward_percent: 2.0,
    calculation_basis: "MANUAL", additional_conditions: "",
    valid_from: "", valid_to: "", is_active: true,
  });
  const [search, setSearch] = useState("");
  const [err, setErr] = useState(null);
  const filtered = (search
    ? products.filter(p => (p.sku || "").toLowerCase().includes(search.toLowerCase())
                        || (p.name || "").toLowerCase().includes(search.toLowerCase()))
    : products).slice(0, 30);
  const submit = () => {
    setErr(null);
    if (!f.product_id) return setErr("Seleziona un prodotto");
    if (!f.target_kg || Number(f.target_kg) <= 0) return setErr("Target kg > 0 obbligatorio");
    if (isNaN(Number(f.reward_percent))) return setErr("Reward % non valido");
    onSubmit({
      product_id: f.product_id, target_kg: Number(f.target_kg),
      reward_percent: Number(f.reward_percent),
      calculation_basis: f.calculation_basis,
      additional_conditions: f.additional_conditions || null,
      valid_from: f.valid_from || null, valid_to: f.valid_to || null,
      is_active: f.is_active,
    });
  };
  return (
    <Modal title="Nuovo target articolo" onClose={onClose} testid="target-modal">
      <FormField label="Cerca prodotto per SKU/nome">
        <Input value={search} onChange={(e) => setSearch(e.target.value)}
               placeholder="es. SUR-006" data-testid="target-search" />
      </FormField>
      <FormField label="Prodotto *">
        <select className="ds-input" value={f.product_id} data-testid="target-product"
                onChange={(e) => setF({ ...f, product_id: e.target.value })}>
          <option value="">— Seleziona —</option>
          {filtered.map(p => <option key={p.id} value={p.id}>{p.sku} — {p.name}</option>)}
        </select>
      </FormField>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <FormField label="Target kg *"><Input type="number" value={f.target_kg}
                        onChange={(e) => setF({ ...f, target_kg: e.target.value })} /></FormField>
        <FormField label="Reward % *"><Input type="number" step="0.01" value={f.reward_percent}
                        onChange={(e) => setF({ ...f, reward_percent: e.target.value })} /></FormField>
        <FormField label="Calculation basis">
          <select className="ds-input" value={f.calculation_basis}
                  onChange={(e) => setF({ ...f, calculation_basis: e.target.value })}>
            <option value="MANUAL">MANUAL</option>
            <option value="NET_REVENUE_YEAR">NET_REVENUE_YEAR (kg × prezzo shipped)</option>
            <option value="REVENUE_UP_TO_TARGET">REVENUE_UP_TO_TARGET — Custom</option>
            <option value="REVENUE_ABOVE_TARGET">REVENUE_ABOVE_TARGET — Custom</option>
          </select>
        </FormField>
        <FormField label="Valida dal"><Input type="date" value={f.valid_from}
                        onChange={(e) => setF({ ...f, valid_from: e.target.value })} /></FormField>
        <FormField label="Valida al"><Input type="date" value={f.valid_to}
                        onChange={(e) => setF({ ...f, valid_to: e.target.value })} /></FormField>
      </div>
      <FormField label="Condizioni aggiuntive"><Input value={f.additional_conditions}
                      onChange={(e) => setF({ ...f, additional_conditions: e.target.value })} /></FormField>
      {err && <div style={{ color: "#dc2626", fontSize: 12 }}>{err}</div>}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
        <Button onClick={onClose}>Annulla</Button>
        <Button variant="primary" onClick={submit} data-testid="target-submit">Salva</Button>
      </div>
    </Modal>
  );
}


function MarketingModal({ onClose, onSubmit }) {
  const [f, setF] = useState({
    activity_type: "FIERA", description: "",
    monetary_value: 0, goods_value: 0,
    event_name: "", planned_date: "", status: "PLANNED", notes: "",
  });
  const [err, setErr] = useState(null);
  const submit = () => {
    setErr(null);
    if (!f.description) return setErr("Descrizione obbligatoria");
    onSubmit({
      activity_type: f.activity_type,
      description: f.description,
      monetary_value: f.monetary_value ? Number(f.monetary_value) : null,
      goods_value: f.goods_value ? Number(f.goods_value) : null,
      event_name: f.event_name || null,
      planned_date: f.planned_date || null,
      status: f.status,
      notes: f.notes || null,
    });
  };
  return (
    <Modal title="Nuova attività marketing/contributo" onClose={onClose} testid="mkt-modal">
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <FormField label="Tipo *">
          <select className="ds-input" value={f.activity_type} data-testid="mkt-type"
                  onChange={(e) => setF({ ...f, activity_type: e.target.value })}>
            <option value="MERCE_OMAGGIO">Merce omaggio</option>
            <option value="CONTRIBUTO_ECONOMICO">Contributo economico</option>
            <option value="EVENTO">Evento</option>
            <option value="FIERA">Fiera</option>
            <option value="MARKETING">Marketing</option>
            <option value="ALTRO">Altro</option>
          </select>
        </FormField>
        <FormField label="Stato">
          <select className="ds-input" value={f.status}
                  onChange={(e) => setF({ ...f, status: e.target.value })}>
            <option value="PLANNED">Pianificato</option>
            <option value="COMPLETED">Completato</option>
            <option value="SETTLED">Liquidato</option>
            <option value="CANCELLED">Annullato</option>
          </select>
        </FormField>
      </div>
      <FormField label="Descrizione *"><Input value={f.description}
                      onChange={(e) => setF({ ...f, description: e.target.value })}
                      data-testid="mkt-description" /></FormField>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <FormField label="Valore economico €"><Input type="number" step="0.01" value={f.monetary_value}
                        onChange={(e) => setF({ ...f, monetary_value: e.target.value })} /></FormField>
        <FormField label="Valore merce €"><Input type="number" step="0.01" value={f.goods_value}
                        onChange={(e) => setF({ ...f, goods_value: e.target.value })} /></FormField>
        <FormField label="Evento/Fiera"><Input value={f.event_name}
                        onChange={(e) => setF({ ...f, event_name: e.target.value })}
                        placeholder="es. TuttoFood 2026" /></FormField>
        <FormField label="Data prevista"><Input type="date" value={f.planned_date}
                        onChange={(e) => setF({ ...f, planned_date: e.target.value })} /></FormField>
      </div>
      <FormField label="Note"><Input value={f.notes}
                      onChange={(e) => setF({ ...f, notes: e.target.value })} /></FormField>
      <div style={{ fontSize: 11, color: "#666", marginTop: 4 }}>
        📎 Dopo il salvataggio, espandi la riga (▸) per allegare documenti privati.
      </div>
      {err && <div style={{ color: "#dc2626", fontSize: 12 }}>{err}</div>}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
        <Button onClick={onClose}>Annulla</Button>
        <Button variant="primary" onClick={submit} data-testid="mkt-submit">Salva</Button>
      </div>
    </Modal>
  );
}



// Feb 2026 v33 — Blocco Admin per PDF contratto controfirmato. Riusa endpoint
// /api/admin/contracts/{id}/document già implementati. Semantica: append,
// l'ultimo elemento di attachments è il documento corrente.
function ContractSignedPdfBlock({ contractId }) {
  const { apiGet } = useAuth();
  const [doc, setDoc] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [err, setErr] = React.useState(null);
  const inputRef = React.useRef(null);
  const reload = React.useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      // Non esiste GET /api/admin/contracts/{id} (405). Uso list + filter.
      const all = await apiGet(`/api/admin/contracts`);
      const c = (all || []).find((x) => x.id === contractId);
      const atts = (c && c.attachments) || [];
      setDoc(atts.length > 0 ? atts[atts.length - 1] : null);
    } catch (e) { setErr("Errore caricamento stato PDF"); }
    finally { setLoading(false); }
  }, [contractId]);
  React.useEffect(() => { reload(); }, [reload]);

  const uploadFile = async (file) => {
    if (!file) return;
    if (file.type !== "application/pdf") {
      setErr("Il file deve essere un PDF (application/pdf)"); return;
    }
    setErr(null);
    try {
      const token = localStorage.getItem("access_token");
      const fd = new FormData(); fd.append("file", file);
      const r = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/contracts/${contractId}/document`,
        { method: "POST", body: fd,
          headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!r.ok) {
        const t = await r.text().catch(() => "");
        setErr(`Upload fallito (${r.status}): ${t.slice(0, 200)}`); return;
      }
      // Il POST restituisce {ok, document}: aggiorno stato senza re-fetch.
      const body = await r.json().catch(() => null);
      if (body && body.document) { setDoc(body.document); }
      else { await reload(); }
    } catch (e) { setErr("Errore rete durante upload"); }
  };

  const openPdf = async () => {
    try {
      const token = localStorage.getItem("access_token");
      const r = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/contracts/${contractId}/document`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!r.ok) { setErr(`Impossibile aprire (${r.status})`); return; }
      const raw = await r.blob();
      const pdfBlob = new Blob([raw], { type: "application/pdf" });
      const url = URL.createObjectURL(pdfBlob);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { setErr("Errore apertura PDF"); }
  };

  return (
    <div data-testid="contract-signed-pdf-block"
         style={{ padding: 12, background: "#F1F5F9", border: "1px solid #CBD5E1",
                  borderRadius: 6, marginBottom: 12 }}>
      <div style={{ fontWeight: 700, marginBottom: 8, color: "#0F172A" }}>
        Contratto controfirmato
      </div>
      {err && <Alert variant="danger" data-testid="contract-pdf-err">{err}</Alert>}
      {loading ? (
        <div className="muted">Caricamento…</div>
      ) : doc ? (
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div data-testid="contract-pdf-filename">
              <strong>{doc.filename_original}</strong>
              <span className="muted" style={{ marginLeft: 8, fontSize: ".85rem" }}>
                ({Math.round((doc.size_bytes || 0) / 1024)} KB)
              </span>
            </div>
            <div className="muted" style={{ fontSize: ".8rem" }}
                 data-testid="contract-pdf-uploaded-at">
              Caricato: {new Date(doc.uploaded_at).toLocaleString("it-IT")}
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={openPdf}
                  data-testid="contract-pdf-open">Apri</Button>
          <Button size="sm" variant="primary"
                  onClick={() => inputRef.current && inputRef.current.click()}
                  data-testid="contract-pdf-replace">Sostituisci</Button>
        </div>
      ) : (
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span className="muted" data-testid="contract-pdf-empty">Nessun PDF caricato</span>
          <Button size="sm" variant="primary"
                  onClick={() => inputRef.current && inputRef.current.click()}
                  data-testid="contract-pdf-upload">Carica PDF</Button>
        </div>
      )}
      <input ref={inputRef} type="file" accept="application/pdf"
             style={{ display: "none" }} data-testid="contract-pdf-input"
             onChange={(e) => { uploadFile(e.target.files && e.target.files[0]); e.target.value = ""; }} />
    </div>
  );
}

function ContractPromotionsPanel({ contractId }) {
  const { apiGet, apiPost, apiPatch, apiDelete } = useAuth();
  const [rows, setRows] = useState(null);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [modal, setModal] = useState(null);
  const load = async () => {
    setRows(await apiGet(`/api/admin/contracts/${contractId}/promotions`));
    if (products.length === 0) setProducts(await apiGet("/api/products").catch(() => []));
    if (categories.length === 0) setCategories(await apiGet("/api/categories").catch(() => []));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [contractId]);
  const rm = async (id) => {
    if (!window.confirm("Eliminare questa promozione contrattuale?")) return;
    await apiDelete(`/api/admin/contract-promotions/${id}`);
    await load();
  };
  const statusTone = (s) => ({
    DRAFT: "warning", CONFIGURED: "info", ACTIVE: "success", EXPIRED: "neutral",
  })[s] || "neutral";
  const scopeLabel = (r) => ({
    ALL_PRODUCTS: "Tutti", CATEGORY: "Categoria",
    SINGLE_PRODUCT: "Singolo SKU",
    MULTI_PRODUCT: `${(r.product_ids || []).length} SKU`,
  })[r.product_scope] || "Tutti";
  return (
    <div style={{ padding: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <strong>Promozioni contrattuali</strong>
        <Button variant="primary" size="sm" data-testid="cp-new"
                onClick={() => setModal({ mode: "create", data: {} })}>+ Nuova promo contrattuale</Button>
      </div>
      {rows && rows.length > 0 && (
        <table className="ds-table" style={{ marginTop: 8 }} data-testid="cp-table">
          <thead><tr><th>Ambito</th><th>Tipo</th><th>Valore</th><th>Mese</th><th>Min.qty</th><th>Priorità</th><th>Stato</th><th></th></tr></thead>
          <tbody>
            {rows.map((r) => {
              const typeLabel = r.discount_type === "CARTONI_OMAGGIO"
                ? `Cartoni ${r.buy_cartons || 0}+${r.free_cartons || 0}`
                : r.discount_type;
              return (
                <tr key={r.id} data-testid={`cp-row-${r.id}`}>
                  <td>{scopeLabel(r)}
                    {(r.excluded_product_ids || []).length > 0 && (
                      <span style={{ fontSize: 11, color: "#dc2626", marginLeft: 4 }}>
                        (–{r.excluded_product_ids.length} esclusi)
                      </span>
                    )}
                  </td>
                  <td>{typeLabel}</td>
                  <td>{r.discount_type === "CARTONI_OMAGGIO" ? "—" : r.value}</td>
                  <td>{r.month || "Sempre"}</td>
                  <td>{r.min_qty}</td>
                  <td>{r.priority}</td>
                  <td><Badge tone={statusTone(r.status)}>{r.status || "ACTIVE"}</Badge></td>
                  <td style={{ display: "flex", gap: 4 }}>
                    <Button size="sm" onClick={() => setModal({ mode: "edit", data: r })}>Modifica</Button>
                    <Button size="sm" variant="danger" onClick={() => rm(r.id)}>✕</Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {modal && (
        <ContractPromoModal mode={modal.mode} data={modal.data}
                            products={products} categories={categories}
                            onClose={() => setModal(null)}
                            onSubmit={async (payload) => {
                              if (modal.mode === "create")
                                await apiPost(`/api/admin/contracts/${contractId}/promotions`, payload);
                              else
                                await apiPatch(`/api/admin/contract-promotions/${modal.data.id}`, payload);
                              setModal(null); await load();
                            }} />
      )}
    </div>
  );
}

function ContractPromoModal({ mode, data, products, categories, onClose, onSubmit }) {
  const [f, setF] = useState({
    status: data.status || "ACTIVE",
    required_product_count: data.required_product_count ?? null,
    selection_deadline: data.selection_deadline || "",
    valid_from: data.valid_from || "",
    valid_to: data.valid_to || "",
    notes: data.notes || "",
    month: data.month ?? "",
    discount_type: data.discount_type || "PERCENT",
    value: data.value || 0,
    min_qty: data.min_qty || 1,
    stackable: data.stackable ?? true,
    priority: data.priority || 10,
    is_active: data.is_active ?? true,
    product_scope: data.product_scope || "ALL_PRODUCTS",
    product_id: data.product_id || null,
    category_id: data.category_id || null,
    product_ids: data.product_ids || [],
    excluded_product_ids: data.excluded_product_ids || [],
    buy_cartons: data.buy_cartons ?? null,
    free_cartons: data.free_cartons ?? null,
    free_product_mode: data.free_product_mode || "SAME_PRODUCT",
    free_product_id: data.free_product_id || null,
  });
  const [prodSearch, setProdSearch] = useState("");
  const isFree = f.discount_type === "CARTONI_OMAGGIO";
  const isDraft = f.status === "DRAFT";
  const filteredProds = (prodSearch
    ? products.filter(p => (p.sku || "").toLowerCase().includes(prodSearch.toLowerCase())
                        || (p.name || "").toLowerCase().includes(prodSearch.toLowerCase()))
    : products).slice(0, 50);
  const [err, setErr] = useState(null);
  const submit = () => {
    setErr(null);
    // Mar 2026 — Product scope validation (parity con PromotionEditor draft)
    const scope = f.product_scope || "ALL_PRODUCTS";
    if (scope === "MULTI_PRODUCT" && (!f.product_ids || f.product_ids.length === 0)) {
      setErr("Seleziona almeno un articolo per la promozione Multi SKU"); return;
    }
    if (scope === "SINGLE_PRODUCT" && !f.product_id) {
      setErr("Seleziona un articolo"); return;
    }
    if (scope === "CATEGORY" && !f.category_id) {
      setErr("Seleziona una categoria"); return;
    }
    const payload = { ...f };
    payload.value = parseFloat(payload.value || 0);
    payload.min_qty = parseInt(payload.min_qty || 1);
    payload.priority = parseInt(payload.priority || 10);
    payload.month = payload.month === "" ? null : parseInt(payload.month);
    payload.selection_deadline = payload.selection_deadline || null;
    payload.valid_from = payload.valid_from || null;
    payload.valid_to = payload.valid_to || null;
    payload.required_product_count = payload.required_product_count === "" || payload.required_product_count == null
      ? null : parseInt(payload.required_product_count);
    if (!isFree) {
      payload.buy_cartons = null;
      payload.free_cartons = null;
      payload.free_product_mode = null;
      payload.free_product_id = null;
    } else if (payload.free_product_mode === "SAME_PRODUCT") {
      payload.free_product_id = null;
    }
    // Mar 2026 — scope cleanup (nessun residuo su ALL_PRODUCTS/SINGLE/CATEGORY)
    payload.product_scope = scope;
    payload.product_ids  = scope === "MULTI_PRODUCT"  ? (payload.product_ids || []) : null;
    payload.product_id   = scope === "SINGLE_PRODUCT" ? (payload.product_id || null) : null;
    payload.category_id  = scope === "CATEGORY"       ? (payload.category_id || null) : null;
    onSubmit(payload);
  };
  return (
    <Modal title={mode === "create" ? "Nuova promo contrattuale" : "Modifica promo contrattuale"}
           onClose={onClose} testid="cp-modal">
      {err && <Alert variant="danger" data-testid="cp-error">{err}</Alert>}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <FormField label="Stato">
          <select className="ds-input" value={f.status} data-testid="cp-status"
                  onChange={(e) => setF({ ...f, status: e.target.value })}>
            <option value="DRAFT">Bozza (prodotti da definire — NON applicata dal pricing)</option>
            <option value="CONFIGURED">Configurata</option>
            <option value="ACTIVE">Attiva</option>
            <option value="EXPIRED">Scaduta</option>
          </select>
        </FormField>
        <FormField label="Tipo sconto">
          <select className="ds-input" value={f.discount_type} data-testid="cp-discount-type"
                  onChange={(e) => setF({ ...f, discount_type: e.target.value })}>
            {DISCOUNT_TYPE_OPTIONS.map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
        </FormField>
        {!isFree && (
          <FormField label="Valore *"><Input type="number" step="0.01" value={f.value}
                          onChange={(e) => setF({ ...f, value: e.target.value })} /></FormField>
        )}
        <FormField label="Mese (vuoto=sempre)"><Input type="number" min="1" max="12" value={f.month}
                        onChange={(e) => setF({ ...f, month: e.target.value })} /></FormField>
        <FormField label="Min.qty"><Input type="number" value={f.min_qty}
                        onChange={(e) => setF({ ...f, min_qty: e.target.value })} /></FormField>
        <FormField label="Priorità (min=applicata prima)"><Input type="number" value={f.priority}
                        onChange={(e) => setF({ ...f, priority: e.target.value })} /></FormField>
        <FormField label="Valida dal"><Input type="date" value={f.valid_from}
                        onChange={(e) => setF({ ...f, valid_from: e.target.value })} /></FormField>
        <FormField label="Valida al"><Input type="date" value={f.valid_to}
                        onChange={(e) => setF({ ...f, valid_to: e.target.value })} /></FormField>
      </div>

      {isDraft && (
        <div style={{ marginTop: 12, padding: 12, background: "#FEF3C7", borderRadius: 6, border: "1px solid #FCD34D" }}>
          <div style={{ fontWeight: 600, color: "#92400E", marginBottom: 8 }}>Promo bozza — prodotti da definire</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            <FormField label="Prodotti richiesti (N)"><Input type="number" min="1" value={f.required_product_count ?? ""}
                            onChange={(e) => setF({ ...f, required_product_count: e.target.value })} data-testid="cp-req-count" /></FormField>
            <FormField label="Scadenza selezione"><Input type="date" value={f.selection_deadline}
                            onChange={(e) => setF({ ...f, selection_deadline: e.target.value })} /></FormField>
          </div>
          <div style={{ fontSize: 12, color: "#92400E", marginTop: 4 }}>
            Le promo in bozza NON vengono applicate al pricing. Passa a ACTIVE quando i prodotti sono selezionati.
          </div>
        </div>
      )}

      {isFree && (
        <div style={{ marginTop: 12, padding: 12, background: "#F0F9FF", borderRadius: 6, border: "1px solid #BAE6FD" }}>
          <div style={{ fontWeight: 600, marginBottom: 8, color: "#0369A1" }}>Cartoni omaggio</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            <FormField label="Cartoni totali per soglia *"><Input type="number" min="1" value={f.buy_cartons ?? ""}
                            onChange={(e) => setF({ ...f, buy_cartons: parseInt(e.target.value || "0") })}
                            data-testid="cp-buy-cartons" /></FormField>
            <FormField label="Cartoni omaggio inclusi *"><Input type="number" min="1" value={f.free_cartons ?? ""}
                            onChange={(e) => setF({ ...f, free_cartons: parseInt(e.target.value || "0") })}
                            data-testid="cp-free-cartons" /></FormField>
          </div>
          <FormField label="Prodotto omaggio">
            <select className="ds-input" value={f.free_product_mode}
                    onChange={(e) => setF({ ...f, free_product_mode: e.target.value })}>
              <option value="SAME_PRODUCT">Stesso SKU</option>
              <option value="SPECIFIC_PRODUCT">SKU specifico</option>
            </select>
          </FormField>
          {f.free_product_mode === "SPECIFIC_PRODUCT" && (
            <FormField label="SKU omaggio">
              <select className="ds-input" value={f.free_product_id || ""}
                      onChange={(e) => setF({ ...f, free_product_id: e.target.value || null })}>
                <option value="">— Seleziona —</option>
                {products.map(p => <option key={p.id} value={p.id}>{p.sku} — {p.name}</option>)}
              </select>
            </FormField>
          )}
          {f.buy_cartons > 0 && f.free_cartons > 0 && f.free_cartons < f.buy_cartons && (
            <div style={{ fontSize: 12, color: "#0369A1", marginTop: 6 }} data-testid="cp-preview">
              <strong>{f.buy_cartons}</strong> cartoni totali → <strong>{f.buy_cartons - f.free_cartons}</strong> pagati + <strong>{f.free_cartons}</strong> omaggio.
              Con {f.buy_cartons * 2} totali: {(f.buy_cartons - f.free_cartons) * 2} pagati + {f.free_cartons * 2} omaggio.
            </div>
          )}
          {f.buy_cartons > 0 && f.free_cartons > 0 && f.free_cartons >= f.buy_cartons && (
            <div style={{ fontSize: 12, color: "#B91C1C", marginTop: 6 }}>
              I cartoni omaggio devono essere strettamente minori dei cartoni totali.
            </div>
          )}
        </div>
      )}

      <div style={{ marginTop: 12, padding: 12, background: "#FEF3C7", borderRadius: 6, border: "1px solid #FCD34D" }}>
        <div style={{ fontWeight: 600, marginBottom: 8, color: "#92400E" }}>Ambito prodotti</div>
        <FormField label="Ambito">
          <select className="ds-input" value={f.product_scope} data-testid="cp-scope"
                  onChange={(e) => setF({ ...f, product_scope: e.target.value,
                    product_id: e.target.value === "SINGLE_PRODUCT" ? f.product_id : null,
                    category_id: e.target.value === "CATEGORY" ? f.category_id : null,
                    product_ids: e.target.value === "MULTI_PRODUCT" ? f.product_ids : [] })}>
            <option value="ALL_PRODUCTS">Tutti i prodotti</option>
            <option value="CATEGORY">Categoria</option>
            <option value="SINGLE_PRODUCT">Singolo SKU</option>
            <option value="MULTI_PRODUCT">Più SKU</option>
          </select>
        </FormField>
        {f.product_scope === "CATEGORY" && (
          <FormField label="Categoria">
            <select className="ds-input" value={f.category_id || ""}
                    onChange={(e) => setF({ ...f, category_id: e.target.value || null })}>
              <option value="">— Seleziona —</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.code} — {c.name}</option>)}
            </select>
          </FormField>
        )}
        {f.product_scope === "SINGLE_PRODUCT" && (
          <FormField label="Prodotto">
            <select className="ds-input" value={f.product_id || ""}
                    onChange={(e) => setF({ ...f, product_id: e.target.value || null })}>
              <option value="">— Seleziona —</option>
              {products.map(p => <option key={p.id} value={p.id}>{p.sku} — {p.name}</option>)}
            </select>
          </FormField>
        )}
        {f.product_scope === "MULTI_PRODUCT" && (
          <FormField label="Prodotti selezionati">
            <Input placeholder="Cerca…" value={prodSearch} onChange={(e) => setProdSearch(e.target.value)} />
            <div style={{ maxHeight: 160, overflow: "auto", border: "1px solid #E5E7EB",
                          borderRadius: 4, marginTop: 4, background: "white" }}>
              {filteredProds.map(p => {
                const on = f.product_ids.includes(p.id);
                return (
                  <label key={p.id} style={{ display: "flex", alignItems: "center", gap: 6,
                                             padding: "4px 8px", background: on ? "#FEF3C7" : "transparent" }}>
                    <input type="checkbox" checked={on}
                           onChange={(e) => setF({ ...f,
                             product_ids: e.target.checked
                               ? [...f.product_ids, p.id]
                               : f.product_ids.filter(x => x !== p.id) })} />
                    <span style={{ fontSize: 13 }}>{p.sku} — {p.name}</span>
                  </label>
                );
              })}
            </div>
            <div style={{ fontSize: 12, color: "#666", marginTop: 4 }}>{f.product_ids.length} selezionati</div>
          </FormField>
        )}
      </div>

      <FormField label="Note"><Input value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></FormField>
      <label style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8 }}>
        <input type="checkbox" checked={f.stackable} onChange={(e) => setF({ ...f, stackable: e.target.checked })} /> Cumulabile
      </label>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
        <Button onClick={onClose}>Annulla</Button>
        <Button variant="primary" onClick={submit} data-testid="cp-submit">Salva</Button>
      </div>
    </Modal>
  );
}

// Feb 2026 — PFA gestito INLINE nel Contratto (single source of truth).
// La tab PFA autonoma è stata rimossa dalla navigazione. Questo pannello
// riutilizza esclusivamente API già esistenti (GET /api/admin/pfa,
// GET /api/admin/pfa/{id}/events, POST /api/admin/contracts/{id}/backfill-pfa/*).
// NON crea nuova business logic. NON aggiunge endpoint CRUD PFA target
// (che oggi non esistono via API — la creazione/aggiornamento è fuori scope
// di questo blocco; se serve va richiesto esplicitamente in un blocco separato).
function ContractPfaInlinePanel({ contract }) {
  const { apiGet, apiPost, apiPatch, apiDelete } = useAuth();
  const [rows, setRows] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [err, setErr] = useState(null);
  const [detail, setDetail] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  // Feb 2026 — PFA Target Editor inline: `null` = closed, `{mode:'create'}` |
  // `{mode:'edit', target}` when open.
  const [editor, setEditor] = useState(null);
  const [saveErr, setSaveErr] = useState(null);
  // v41 Step 2 — Per-PFA Scaglioni modal (null = closed, else the PFA row)
  const [tiersFor, setTiersFor] = useState(null);

  const load = async () => {
    setErr(null);
    try {
      const [r, tr] = await Promise.all([
        apiGet(`/api/admin/pfa?contract_id=${contract.id}`),
        apiGet(`/api/admin/contracts/${contract.id}/rebate-tiers`),
      ]);
      setRows(r);
      setTiers(tr || []);
    } catch (e) { setErr(e?.message || String(e)); setRows([]); setTiers([]); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [contract.id]);

  const showEvents = async (r) => {
    const events = await apiGet(`/api/admin/pfa/${r.id}/events`);
    setDetail({ ...r, events });
  };

  const runBackfillPreview = async () => {
    setBusy(true); setPreview(null);
    try {
      const p = await apiGet(`/api/admin/contracts/${contract.id}/backfill-pfa/preview`);
      setPreview(p);
    } catch (e) { setErr(e?.message || String(e)); }
    finally { setBusy(false); }
  };
  const runBackfillApply = async () => {
    if (!window.confirm("Applicare il backfill PFA? Idempotente, ripetibile.")) return;
    setBusy(true);
    try {
      await apiPost(`/api/admin/contracts/${contract.id}/backfill-pfa/apply`, {});
      await runBackfillPreview();
      await load();
    } catch (e) { setErr(e?.message || String(e)); }
    finally { setBusy(false); }
  };

  const savePfa = async (form) => {
    setSaveErr(null);
    try {
      // Mar 2026 — target_kg è l'OBIETTIVO COMMERCIALE del PFA. Sempre inviato.
      // Non è più sincronizzato dai tier.
      if (editor.mode === "create") {
        const body = {
          contract_id: contract.id,
          period_start: form.period_start,
          period_end: form.period_end,
          target_kg: form.target_kg,
          status: form.status || "ACTIVE",
          name: form.name || null,
          scope_type: form.scope_type || "GLOBAL",
          scope_category_id: null,
          scope_product_id: null,
          eligible_category_ids: form.scope_type === "CATEGORY" ? (form.eligible_category_ids || []) : null,
          eligible_product_ids: form.scope_type === "PRODUCT" ? (form.eligible_product_ids || []) : null,
        };
        await apiPost("/api/admin/pfa", body);
      } else {
        const body = {
          period_start: form.period_start,
          period_end: form.period_end,
          target_kg: form.target_kg,
          status: form.status,
          name: form.name || null,
          scope_type: form.scope_type || "GLOBAL",
          scope_category_id: null,
          scope_product_id: null,
          eligible_category_ids: form.scope_type === "CATEGORY" ? (form.eligible_category_ids || []) : null,
          eligible_product_ids: form.scope_type === "PRODUCT" ? (form.eligible_product_ids || []) : null,
        };
        await apiPatch(`/api/admin/pfa/${editor.target.id}`, body);
      }
      setEditor(null);
      await load();
    } catch (e) {
      setSaveErr(e?.data?.detail || e?.message || String(e));
    }
  };

  return (
    <div style={{ marginTop: 12, padding: 12, background: "#F0FDF4",
                  borderRadius: 6, border: "1px solid #BBF7D0" }}
         data-testid="contract-pfa-inline-panel">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 6 }}>
        <strong style={{ color: "#166534" }}>PFA del contratto</strong>
        <div style={{ display: "flex", gap: 6 }}>
          {rows && rows.length === 0 && (
            <Button size="sm" variant="primary"
                    data-testid="contract-pfa-configure-btn"
                    onClick={() => setEditor({ mode: "create" })}>
              Configura PFA
            </Button>
          )}
          <Button size="sm" onClick={runBackfillPreview} loading={busy} disabled={busy}
                  data-testid="contract-pfa-backfill-preview">
            Anteprima kg recuperabili
          </Button>
          {preview && preview.candidates_to_backfill > 0 && (
            <Button size="sm" variant="primary" onClick={runBackfillApply} loading={busy} disabled={busy}
                    data-testid="contract-pfa-backfill-apply">
              Applica backfill ({preview.total_delta_kg} kg)
            </Button>
          )}
        </div>
      </div>
      {err && <Alert variant="danger" style={{ marginTop: 6 }}>{err}</Alert>}
      {preview && (
        <div style={{ marginTop: 6, padding: 8, background: "white",
                      borderRadius: 4, fontSize: 12 }}
             data-testid="contract-pfa-preview-result">
          <div>Da recuperare: <strong>{preview.total_delta_kg} kg</strong>{" "}
            su {preview.candidates_to_backfill} shipment_lines eleggibili</div>
          {preview.candidates_already_recorded > 0 && (
            <div style={{ color: "#0369A1" }}>
              Già backfilled: {preview.already_recorded_kg} kg
              ({preview.candidates_already_recorded} righe)
            </div>
          )}
        </div>
      )}
      {rows && rows.length === 0 && (
        <div style={{ marginTop: 8, fontSize: 12, color: "#166534" }}
             data-testid="contract-pfa-empty">
          Nessun PFA configurato per questo contratto. Clicca <strong>+ Aggiungi PFA</strong>{" "}
          per crearne uno nuovo.
        </div>
      )}
      {rows && rows.length > 0 && (
        <div style={{ marginTop: 8, display: "flex", justifyContent: "flex-end" }}>
          <Button size="sm" variant="primary"
                  data-testid="contract-pfa-add-btn"
                  onClick={() => setEditor({ mode: "create" })}>
            + Aggiungi PFA
          </Button>
        </div>
      )}
      {rows && rows.length > 0 && (
        <table className="ds-table" style={{ marginTop: 8, fontSize: 12 }}
               data-testid="contract-pfa-table">
          <thead><tr>
            <th>Ambito</th><th>Periodo</th><th>Target kg</th><th>Maturato</th><th>%</th>
            <th>Residuo</th><th>Stato</th><th></th>
          </tr></thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.id} data-testid={`contract-pfa-row-${p.id}`}>
                <td data-testid={`contract-pfa-scope-${p.id}`}>
                  <Badge tone={p.scope_type === "PRODUCT" ? "success"
                              : p.scope_type === "CATEGORY" ? "info" : "neutral"}>
                    {p.scope_type || "GLOBAL"}
                  </Badge>
                  {p.scope_type === "CATEGORY" && p.scope_category_id && (
                    <div style={{ fontSize: 10, color: "#64748b", marginTop: 2 }}>
                      cat: {(p.scope_category_id || "").slice(0, 8)}
                    </div>
                  )}
                  {p.scope_type === "PRODUCT" && p.scope_product_id && (
                    <div style={{ fontSize: 10, color: "#64748b", marginTop: 2 }}>
                      sku: {(p.scope_product_id || "").slice(0, 8)}
                    </div>
                  )}
                  {(p.name || (p.eligible_category_ids && p.eligible_category_ids.length > 0)
                    || (p.eligible_product_ids && p.eligible_product_ids.length > 0)) && (
                    <div style={{ fontSize: 10, color: "#0f172a", marginTop: 2 }}
                         data-testid={`contract-pfa-name-${p.id}`}>
                      {p.display_name || p.name || "—"}
                      {p.scope_type === "CATEGORY" && p.eligible_category_ids && p.eligible_category_ids.length > 0 && (
                        <span style={{ color: "#64748b" }}> · {p.eligible_category_ids.length} categorie</span>
                      )}
                      {p.scope_type === "PRODUCT" && p.eligible_product_ids && p.eligible_product_ids.length > 0 && (
                        <span style={{ color: "#64748b" }}> · {p.eligible_product_ids.length} articoli</span>
                      )}
                    </div>
                  )}
                </td>
                <td>{p.period_start} → {p.period_end}</td>
                <td>{p.target_kg}</td>
                <td>{p.matured_kg}</td>
                <td><Badge tone={p.percentage >= 100 ? "success" : p.percentage > 75 ? "info" : "warning"}>{p.percentage}%</Badge></td>
                <td>{p.residual_kg}</td>
                <td>{p.status}</td>
                <td style={{ display: "flex", gap: 4 }}>
                  <Button size="sm"
                          data-testid={`contract-pfa-edit-${p.id}`}
                          onClick={() => setEditor({ mode: "edit", target: p })}>
                    Modifica
                  </Button>
                  <Button size="sm"
                          data-testid={`contract-pfa-tiers-${p.id}`}
                          onClick={() => setTiersFor(p)}>
                    Scaglioni
                  </Button>
                  <Button size="sm" onClick={() => showEvents(p)}>Eventi</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {editor && (
        <PfaEditorModal
          mode={editor.mode}
          target={editor.target}
          contract={contract}
          hasTiers={tiers.length > 0}
          maxTierKg={tiers.length > 0
            ? Math.max(...tiers.map((t) => Number(t.threshold_kg) || 0))
            : null}
          error={saveErr}
          onClose={() => { setEditor(null); setSaveErr(null); }}
          onSubmit={savePfa} />
      )}
      {tiersFor && (
        <PfaTiersModal
          pfa={tiersFor}
          contract={contract}
          onClose={() => setTiersFor(null)}
          onChanged={() => load()} />
      )}
      {detail && (
        <Modal title="Eventi PFA" onClose={() => setDetail(null)} testid="contract-pfa-events-modal">
          <p className="muted">{detail.events.length} evento/i append-only</p>
          {detail.events.length === 0 ? <Alert tone="info">Nessun evento registrato.</Alert> : (
            <table className="ds-table">
              <thead><tr><th>Data</th><th>Δ kg</th><th>Sorgente</th><th>Ordine</th></tr></thead>
              <tbody>
                {detail.events.map((e) => (
                  <tr key={e.id}>
                    <td style={{ fontSize: ".85rem" }}>{new Date(e.at).toLocaleString("it-IT")}</td>
                    <td><Badge tone={e.delta_kg >= 0 ? "success" : "danger"}>{e.delta_kg > 0 ? "+" : ""}{e.delta_kg}</Badge></td>
                    <td><code style={{ fontSize: ".8rem" }}>{e.source_event}</code></td>
                    <td style={{ fontSize: ".8rem" }}>{(e.order_id || "").slice(0, 8) || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Modal>
      )}
    </div>
  );
}

// v41 Step 2 — Per-PFA Scaglioni modal. Manages tiers scoped to a single PFA
// with legacy-fallback semantics. Never mixes specific + legacy.
function PfaTiersModal({ pfa, contract, onClose, onChanged }) {
  const { apiGet, apiPost, apiPatch, apiDelete } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [newTier, setNewTier] = useState({ threshold_kg: "", reward_percent: "", cumulative: true });
  const load = async () => {
    setErr(null);
    try {
      const r = await apiGet(`/api/admin/pfa/${pfa.id}/rebate-tiers`);
      setData(r);
    } catch (e) { setErr(e?.data?.detail || e?.message || String(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [pfa.id]);
  const addTier = async () => {
    setErr(null);
    if (!newTier.threshold_kg || Number(newTier.threshold_kg) <= 0) { setErr("Soglia kg > 0 obbligatoria"); return; }
    if (newTier.reward_percent === "" || Number(newTier.reward_percent) < 0) { setErr("Reward % ≥ 0 obbligatorio"); return; }
    setBusy(true);
    try {
      await apiPost(`/api/admin/pfa/${pfa.id}/rebate-tiers`, {
        threshold_kg: Number(newTier.threshold_kg),
        reward_percent: Number(newTier.reward_percent),
        cumulative: !!newTier.cumulative,
      });
      setNewTier({ threshold_kg: "", reward_percent: "", cumulative: true });
      await load();
      onChanged && onChanged();
    } catch (e) {
      setErr(e?.data?.detail || e?.message || String(e));
    } finally { setBusy(false); }
  };
  const patchTier = async (tierId, patch) => {
    setBusy(true); setErr(null);
    try {
      await apiPatch(`/api/admin/rebate-tiers/${tierId}`, patch);
      await load();
      onChanged && onChanged();
    } catch (e) {
      setErr(e?.data?.detail || e?.message || String(e));
    } finally { setBusy(false); }
  };
  const removeTier = async (tierId) => {
    if (!window.confirm("Eliminare lo scaglione?")) return;
    setBusy(true); setErr(null);
    try {
      await apiDelete(`/api/admin/rebate-tiers/${tierId}`);
      await load();
      onChanged && onChanged();
    } catch (e) {
      setErr(e?.data?.detail || e?.message || String(e));
    } finally { setBusy(false); }
  };
  const mode = data?.mode;
  const isLegacy = mode === "legacy";
  const effective = data?.effective || [];
  return (
    <Modal title={`Scaglioni PFA — ${pfa.display_name || pfa.name || "PFA"}`}
           onClose={onClose} testid="pfa-tiers-modal">
      <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
        Contratto: <code>{contract.code || contract.id.slice(0, 8)}</code>
        {" · "}Periodo: {pfa.period_start} → {pfa.period_end}
        {" · "}Target attuale: <strong>{pfa.target_kg} kg</strong>
      </div>
      {err && <Alert variant="danger" data-testid="pfa-tiers-error">{String(err)}</Alert>}
      {isLegacy && effective.length > 0 && (
        <Alert data-testid="pfa-tiers-legacy-notice">
          Questo PFA usa gli <strong>scaglioni legacy del contratto</strong> come fallback.
          Aggiungi almeno uno scaglione specifico per gestirlo autonomamente.
        </Alert>
      )}
      <table className="ds-table" data-testid="pfa-tiers-table" style={{ fontSize: 12 }}>
        <thead><tr><th>#</th><th>Soglia kg</th><th>Reward %</th><th>Cumulativo</th><th>Origine</th><th></th></tr></thead>
        <tbody>
          {effective.length === 0 && (
            <tr><td colSpan={6} className="muted" style={{ textAlign: "center", padding: 8 }}>
              Nessuno scaglione. Aggiungi il primo qui sotto per configurare i premi.
            </td></tr>
          )}
          {effective.map((t, i) => {
            const editable = !isLegacy;
            return (
              <tr key={t.id} data-testid={`pfa-tier-row-${t.id}`}>
                <td>{i + 1}</td>
                <td>
                  {editable ? (
                    <Input type="number" step="0.001" defaultValue={t.threshold_kg}
                           data-testid={`pfa-tier-threshold-${t.id}`}
                           onBlur={(e) => {
                             const v = Number(e.target.value);
                             if (v > 0 && v !== Number(t.threshold_kg)) patchTier(t.id, { threshold_kg: v });
                           }} />
                  ) : t.threshold_kg}
                </td>
                <td>
                  {editable ? (
                    <Input type="number" step="0.001" defaultValue={t.reward_percent}
                           data-testid={`pfa-tier-reward-${t.id}`}
                           onBlur={(e) => {
                             const v = Number(e.target.value);
                             if (v >= 0 && v !== Number(t.reward_percent)) patchTier(t.id, { reward_percent: v });
                           }} />
                  ) : `${t.reward_percent}%`}
                </td>
                <td>{t.cumulative ? "Sì" : "No"}</td>
                <td>
                  <Badge tone={t.pfa_target_id ? "success" : "neutral"}>
                    {t.pfa_target_id ? "Specifico" : "Legacy"}
                  </Badge>
                </td>
                <td>
                  {editable && (
                    <Button size="sm" variant="danger" disabled={busy}
                            data-testid={`pfa-tier-remove-${t.id}`}
                            onClick={() => removeTier(t.id)}>✕</Button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div style={{ marginTop: 12, padding: 8, background: "#F0FDF4", borderRadius: 4,
                    border: "1px solid #BBF7D0" }}>
        <strong style={{ fontSize: 13, color: "#166534" }}>+ Aggiungi scaglione</strong>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto auto", gap: 8, marginTop: 6 }}>
          <FormField label="Soglia kg">
            <Input type="number" step="0.001" min="0" value={newTier.threshold_kg}
                   data-testid="pfa-tier-new-threshold"
                   onChange={(e) => setNewTier({ ...newTier, threshold_kg: e.target.value })} />
          </FormField>
          <FormField label="Reward %">
            <Input type="number" step="0.001" min="0" value={newTier.reward_percent}
                   data-testid="pfa-tier-new-reward"
                   onChange={(e) => setNewTier({ ...newTier, reward_percent: e.target.value })} />
          </FormField>
          <FormField label="Cumulativo">
            <input type="checkbox" checked={newTier.cumulative}
                   data-testid="pfa-tier-new-cumulative"
                   onChange={(e) => setNewTier({ ...newTier, cumulative: e.target.checked })} />
          </FormField>
          <Button variant="primary" disabled={busy}
                  data-testid="pfa-tier-add-submit"
                  onClick={addTier}>Aggiungi</Button>
        </div>
        {isLegacy && effective.length > 0 && (
          <div style={{ marginTop: 6, fontSize: 11, color: "#0369a1" }}>
            Aggiungendo il primo scaglione specifico, il fallback legacy viene disattivato per questo PFA.
          </div>
        )}
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
        <Button onClick={onClose}>Chiudi</Button>
      </div>
    </Modal>
  );
}

// Feb 2026 — Editor PFA compatto usato SOLO dentro ContractPfaInlinePanel.
// v41 Multi-PFA UX: Nome PFA + multi-select chip Categorie/Articoli.
// contract_id NON è editabile (immutabile — arriva dal contratto padre).
function PfaEditorModal({ mode, target, contract, hasTiers, maxTierKg, error, onClose, onSubmit }) {
  const { apiGet } = useAuth();
  // Legacy preload: se `eligible_*_ids` è vuoto ma `scope_*_id` è valorizzato,
  // seed la chip list col valore legacy per evitare data loss (nessun backfill DB).
  const initialCatIds = (() => {
    if (!target) return [];
    const arr = Array.isArray(target.eligible_category_ids) ? [...target.eligible_category_ids] : [];
    if (target.scope_category_id && !arr.includes(target.scope_category_id)) arr.push(target.scope_category_id);
    return arr;
  })();
  const initialProdIds = (() => {
    if (!target) return [];
    const arr = Array.isArray(target.eligible_product_ids) ? [...target.eligible_product_ids] : [];
    if (target.scope_product_id && !arr.includes(target.scope_product_id)) arr.push(target.scope_product_id);
    return arr;
  })();
  const [f, setF] = useState(() => target ? {
    period_start: target.period_start || "",
    period_end: target.period_end || "",
    target_kg: target.target_kg ?? "",
    status: target.status || "ACTIVE",
    name: target.name || "",
    scope_type: target.scope_type || "GLOBAL",
    eligible_category_ids: initialCatIds,
    eligible_product_ids: initialProdIds,
  } : {
    period_start: "",
    period_end: "",
    target_kg: "",
    status: "ACTIVE",
    name: "",
    scope_type: "GLOBAL",
    eligible_category_ids: [],
    eligible_product_ids: [],
  });  const [localErr, setLocalErr] = useState(null);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [catInput, setCatInput] = useState("");
  const [prodInput, setProdInput] = useState("");
  useEffect(() => {
    if (f.scope_type === "CATEGORY" && categories.length === 0) {
      apiGet("/api/admin/categories").then((r) => setCategories(r || [])).catch(() => setCategories([]));
    }
    if (f.scope_type === "PRODUCT" && products.length === 0) {
      apiGet("/api/admin/products?limit=1000").then((r) => setProducts(Array.isArray(r) ? r : (r?.items || []))).catch(() => setProducts([]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f.scope_type]);

  const isNew = mode === "create";
  const nameTrimmed = (f.name || "").trim();

  // ---- Chip helpers ----
  const catById = new Map(categories.map((c) => [c.id, c]));
  const prodById = new Map(products.map((p) => [p.id, p]));

  const addCategory = (id) => {
    if (!id) return;
    if (f.eligible_category_ids.includes(id)) return;
    setF({ ...f, eligible_category_ids: [...f.eligible_category_ids, id] });
    setCatInput("");
  };
  const removeCategory = (id) => {
    setF({ ...f, eligible_category_ids: f.eligible_category_ids.filter((x) => x !== id) });
  };
  const tryAddCategoryFromLabel = (label) => {
    // Match by exact name (case-insensitive)
    const q = (label || "").trim().toLowerCase();
    if (!q) return;
    const match = categories.find((c) => (c.name || "").toLowerCase() === q);
    if (match) addCategory(match.id);
  };

  const addProduct = (id) => {
    if (!id) return;
    if (f.eligible_product_ids.includes(id)) return;
    setF({ ...f, eligible_product_ids: [...f.eligible_product_ids, id] });
    setProdInput("");
  };
  const removeProduct = (id) => {
    setF({ ...f, eligible_product_ids: f.eligible_product_ids.filter((x) => x !== id) });
  };
  const tryAddProductFromLabel = (label) => {
    // Datalist option value is `SKU — Name`; try match by SKU prefix first.
    const q = (label || "").trim().toLowerCase();
    if (!q) return;
    const sku = q.split(" — ")[0].trim();
    const match = products.find((p) => (p.sku || "").toLowerCase() === sku)
                  || products.find((p) => (p.name || "").toLowerCase() === q);
    if (match) addProduct(match.id);
  };

  const submit = () => {
    setLocalErr(null);
    if (isNew && !nameTrimmed) { setLocalErr("Nome PFA obbligatorio"); return; }
    if (!f.period_start || !f.period_end) { setLocalErr("Periodo obbligatorio"); return; }
    if (f.period_end <= f.period_start) { setLocalErr("La fine deve essere successiva all'inizio"); return; }
    // Mar 2026 — Obiettivo PFA sempre obbligatorio > 0 (source of truth business)
    if (!f.target_kg || Number(f.target_kg) <= 0) { setLocalErr("Obiettivo PFA (kg) deve essere > 0"); return; }
    if (f.scope_type === "CATEGORY" && f.eligible_category_ids.length === 0) {
      setLocalErr("Seleziona almeno una categoria per ambito CATEGORY"); return;
    }
    if (f.scope_type === "PRODUCT" && f.eligible_product_ids.length === 0) {
      setLocalErr("Seleziona almeno un articolo per ambito PRODUCT"); return;
    }
    onSubmit({
      period_start: f.period_start,
      period_end: f.period_end,
      target_kg: Number(f.target_kg),
      status: f.status,
      name: nameTrimmed || null,
      scope_type: f.scope_type,
      eligible_category_ids: f.scope_type === "CATEGORY" ? f.eligible_category_ids : [],
      eligible_product_ids: f.scope_type === "PRODUCT" ? f.eligible_product_ids : [],
    });
  };

  // Datalist options filtrate per performance/UX (max 200)
  const catInputLower = catInput.trim().toLowerCase();
  const filteredCategoriesForList = categories
    .filter((c) => !f.eligible_category_ids.includes(c.id))
    .filter((c) => !catInputLower || (c.name || "").toLowerCase().includes(catInputLower))
    .slice(0, 200);
  const prodInputLower = prodInput.trim().toLowerCase();
  const filteredProductsForList = products
    .filter((p) => !f.eligible_product_ids.includes(p.id))
    .filter((p) => !prodInputLower
                    || (p.sku || "").toLowerCase().includes(prodInputLower)
                    || (p.name || "").toLowerCase().includes(prodInputLower))
    .slice(0, 200);

  const chipStyle = { display: "inline-flex", alignItems: "center", gap: 4,
                      background: "#DBEAFE", color: "#1E40AF",
                      padding: "2px 6px", borderRadius: 4, fontSize: 12,
                      marginRight: 4, marginBottom: 4, cursor: "default" };
  const chipXStyle = { cursor: "pointer", color: "#1E40AF", fontWeight: 700 };

  return (
    <Modal title={mode === "create" ? "Configura PFA" : "Modifica PFA"}
           onClose={onClose} testid="pfa-editor-modal">
      <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
        Contratto: <code>{contract.code || contract.id.slice(0, 8)}</code>
        {" · "}Il PFA è vincolato a questo contratto (contract_id immutabile).
      </div>
      {(error || localErr) && (
        <Alert variant="danger" data-testid="pfa-editor-error">
          {String(localErr || (typeof error === "string" ? error : JSON.stringify(error)))}
        </Alert>
      )}
      <FormField label={isNew ? "Nome PFA *" : "Nome PFA"}>
        <Input type="text" value={f.name}
               data-testid="pfa-editor-name"
               placeholder="es. PFA Pollo, PFA Speedyfood 2026, ecc."
               onChange={(e) => setF({ ...f, name: e.target.value })} />
      </FormField>
      <FormField label="Obiettivo PFA (kg) *">
        <Input type="number" step="0.001" min="0" value={f.target_kg}
               data-testid="pfa-editor-target-kg"
               placeholder="es. 5000"
               onChange={(e) => setF({ ...f, target_kg: e.target.value })} />
        <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
          Obiettivo commerciale autonomo del PFA. Gli scaglioni non lo sovrascrivono.
        </div>
      </FormField>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <FormField label="Ambito *">
          <select className="ds-input" value={f.scope_type}
                  data-testid="pfa-editor-scope-type"
                  onChange={(e) => setF({ ...f, scope_type: e.target.value,
                                          eligible_category_ids: [],
                                          eligible_product_ids: [] })}>
            <option value="GLOBAL">Globale</option>
            <option value="CATEGORY">Categoria</option>
            <option value="PRODUCT">Articolo</option>
          </select>
        </FormField>
        {f.scope_type === "CATEGORY" && (
          <FormField label="Categorie *">
            <div>
              <div data-testid="pfa-editor-category-chips" style={{ marginBottom: 4 }}>
                {f.eligible_category_ids.length === 0 && (
                  <span className="muted" style={{ fontSize: 12 }}>Nessuna categoria selezionata</span>
                )}
                {f.eligible_category_ids.map((cid) => (
                  <span key={cid} style={chipStyle}
                        data-testid={`pfa-editor-category-chip-${cid}`}>
                    {(catById.get(cid) || {}).name || cid.slice(0, 8)}
                    <span style={chipXStyle}
                          data-testid={`pfa-editor-category-chip-remove-${cid}`}
                          onClick={() => removeCategory(cid)}>×</span>
                  </span>
                ))}
              </div>
              <input type="text" className="ds-input"
                     value={catInput}
                     data-testid="pfa-editor-category-input"
                     placeholder="Digita e seleziona categoria…"
                     list="pfa-editor-category-datalist"
                     onChange={(e) => {
                       setCatInput(e.target.value);
                       // Auto-add se il valore corrisponde esattamente a una option
                       tryAddCategoryFromLabel(e.target.value);
                     }}
                     onKeyDown={(e) => {
                       if (e.key === "Enter") {
                         e.preventDefault();
                         tryAddCategoryFromLabel(catInput);
                       }
                     }} />
              <datalist id="pfa-editor-category-datalist">
                {filteredCategoriesForList.map((c) => (
                  <option key={c.id} value={c.name} />
                ))}
              </datalist>
            </div>
          </FormField>
        )}
        {f.scope_type === "PRODUCT" && (
          <FormField label="Articoli *">
            <div>
              <div data-testid="pfa-editor-product-chips" style={{ marginBottom: 4 }}>
                {f.eligible_product_ids.length === 0 && (
                  <span className="muted" style={{ fontSize: 12 }}>Nessun articolo selezionato</span>
                )}
                {f.eligible_product_ids.map((pid) => {
                  const p = prodById.get(pid);
                  return (
                    <span key={pid} style={chipStyle}
                          data-testid={`pfa-editor-product-chip-${pid}`}>
                      {p ? `${p.sku} — ${p.name}` : pid.slice(0, 8)}
                      <span style={chipXStyle}
                            data-testid={`pfa-editor-product-chip-remove-${pid}`}
                            onClick={() => removeProduct(pid)}>×</span>
                    </span>
                  );
                })}
              </div>
              <input type="text" className="ds-input"
                     value={prodInput}
                     data-testid="pfa-editor-product-input"
                     placeholder="Cerca per SKU o nome…"
                     list="pfa-editor-product-datalist"
                     onChange={(e) => {
                       setProdInput(e.target.value);
                       tryAddProductFromLabel(e.target.value);
                     }}
                     onKeyDown={(e) => {
                       if (e.key === "Enter") {
                         e.preventDefault();
                         tryAddProductFromLabel(prodInput);
                       }
                     }} />
              <datalist id="pfa-editor-product-datalist">
                {filteredProductsForList.map((p) => (
                  <option key={p.id} value={`${p.sku} — ${p.name}`} />
                ))}
              </datalist>
            </div>
          </FormField>
        )}
        <FormField label="Inizio periodo *">
          <Input type="date" value={f.period_start}
                 data-testid="pfa-editor-period-start"
                 onChange={(e) => setF({ ...f, period_start: e.target.value })} />
        </FormField>
        <FormField label="Fine periodo *">
          <Input type="date" value={f.period_end}
                 data-testid="pfa-editor-period-end"
                 onChange={(e) => setF({ ...f, period_end: e.target.value })} />
        </FormField>
        <FormField label="Stato">
          <select className="ds-input" value={f.status}
                  data-testid="pfa-editor-status"
                  onChange={(e) => setF({ ...f, status: e.target.value })}>
            <option value="ACTIVE">ACTIVE</option>
            <option value="SUSPENDED">SUSPENDED</option>
            <option value="CLOSED">CLOSED</option>
          </select>
        </FormField>
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
        <Button onClick={onClose}>Annulla</Button>
        <Button variant="primary" data-testid="pfa-editor-submit" onClick={submit}>
          {mode === "create" ? "Crea PFA" : "Salva"}
        </Button>
      </div>
    </Modal>
  );
}

// ---------- SHIPPING RULES ----------
function ShippingRulesTab() { return <GenericRuleTab basePath="/api/admin/shipping-rules" title="Regole Trasporto" testid="sr" schema="shipping" />; }
function DiscountRulesTab() {
  const [sub, setSub] = useState("pallet");
  // Feb 2026 v34 — Standard globali dopo cleanup dati: Pallet 2% · Ritiro 4%
  // (Castenaso+Bentivoglio) · Anticipato 1%. Sconti diversi → Promozioni/Contratti.
  // v34.5 — Pallet e Payment-terms filtrati active-only (backend accetta
  // ?active=true). Pickup e Stacking restano invariati (pickup ha già la sua
  // tab dedicata con toggle "Mostra archiviate").
  const activeOnly = { pallet: true, "payment-terms": true };
  const basePath = activeOnly[sub]
    ? `/api/admin/discount-rules/${sub}?active=true`
    : `/api/admin/discount-rules/${sub}`;
  return (
    <>
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        {["pallet", "pickup", "payment-terms", "stacking"].map((s) => (
          <Button key={s} variant={sub === s ? "primary" : "ghost"} size="sm" onClick={() => setSub(s)} data-testid={`disc-sub-${s}`}>
            {s === "pallet" ? "Pallet completo" : s === "pickup" ? "Ritiro cliente" : s === "payment-terms" ? "Pagamento anticipato" : "Stacking"}
          </Button>
        ))}
      </div>
      <GenericRuleTab basePath={basePath} title="" testid={`disc-${sub}`} schema={sub} />
    </>
  );
}
function CarriersTab() {
  // Feb 2026 v34 — Default active-only view. Vettori inattivi restano in DB
  // per preservare FK con Shipment/LoadPlan storici (ondelete=SET_NULL/RESTRICT).
  // Non esposti nella lista operativa Admin.
  return <GenericRuleTab basePath="/api/admin/carriers?active=true" title="Vettori" testid="carrier" schema="carrier" />;
}

function GenericRuleTab({ basePath, title, testid, schema }) {
  const { apiGet, apiPost, apiPatch } = useAuth();
  // Mar 2026 — basePath può includere una query string (es.
  // "/api/admin/carriers?active=true"). Le mutation devono colpire il
  // path RISORSA (`/api/admin/carriers/{id}`) altrimenti l'URL diventa
  // `/api/admin/carriers?active=true/{id}` e il server risponde 405
  // Method Not Allowed sul path `/api/admin/carriers` (ha solo POST/GET,
  // niente PATCH sul plurale). Split senza modificare la GET.
  const resourcePath = basePath.split("?")[0];
  const [rows, setRows] = useState(null);
  const [modal, setModal] = useState(null);
  const [err, setErr] = useState(null);
  const load = async () => {
    setErr(null);
    try { setRows(await apiGet(basePath)); }
    catch (e) { setErr(e?.data?.detail || e?.message || String(e)); setRows([]); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [basePath]);

  const toggle = async (r) => {
    try { await apiPost(`${resourcePath}/${r.id}/toggle-active`, {}); await load(); }
    catch (e) { setErr(e?.data?.detail || String(e)); }
  };

  const cols = useMemo(() => COLUMNS[schema] || [], [schema]);

  return (
    <>
      {title && <h3 style={{ marginTop: 0 }}>{title}</h3>}
      {err && <Alert variant="danger">Errore: {String(err)}</Alert>}
      <Card>
        <Card.Header>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button variant="primary" onClick={() => setModal({ mode: "create", data: {} })} data-testid={`${testid}-new`}>+ Nuovo</Button>
          </div>
        </Card.Header>
        <Card.Body>
          {!rows && <p className="muted">Caricamento…</p>}
          {rows && rows.length === 0 && <Alert tone="info">Nessun elemento.</Alert>}
          {rows && rows.length > 0 && (
            <div className="ds-table-wrap">
              <table className="ds-table" data-testid={`${testid}-table`}>
                <thead><tr>{cols.map((c) => <th key={c.key}>{c.label}</th>)}<th>Attivo</th><th></th></tr></thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} data-testid={`${testid}-row-${r.id}`}>
                      {cols.map((c) => <td key={c.key}>{c.render ? c.render(r) : (r[c.key] ?? "—")}</td>)}
                      <td><Badge tone={r.is_active ? "success" : "neutral"}>{r.is_active ? "Sì" : "No"}</Badge></td>
                      <td>
                        <div style={{ display: "flex", gap: 4 }}>
                          <Button size="sm" onClick={() => setModal({ mode: "edit", data: r })}>Modifica</Button>
                          {(schema !== "stacking" && schema !== "payment-terms") && (
                            <Button size="sm" onClick={() => toggle(r)}>{r.is_active ? "Disattiva" : "Attiva"}</Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card.Body>
      </Card>
      {modal && (
        <GenericRuleModal schema={schema} mode={modal.mode} data={modal.data}
                          onClose={() => setModal(null)}
                          onSubmit={async (payload) => {
                            try {
                              if (modal.mode === "create") await apiPost(resourcePath, payload);
                              else await apiPatch(`${resourcePath}/${modal.data.id}`, payload);
                              setModal(null); await load();
                            } catch (e) { setErr(e?.data?.detail || String(e)); }
                          }} />
      )}
    </>
  );
}

const COLUMNS = {
  shipping: [
    { key: "region", label: "Regione" },
    { key: "min_order_amount", label: "Min €" },
    { key: "max_order_amount", label: "Max €" },
    { key: "transport_cost", label: "Trasporto €" },
    { key: "free_shipping_threshold", label: "Franco €" },
    { key: "valid_from", label: "Dal" },
    { key: "valid_to", label: "Al" },
  ],
  pallet: [
    { key: "min_full_pallets", label: "Min pallet" },
    { key: "discount_type", label: "Tipo" },
    { key: "value", label: "Valore" },
    { key: "valid_from", label: "Dal" },
    { key: "valid_to", label: "Al" },
    { key: "stackable", label: "Stackable", render: (r) => r.stackable ? "✓" : "✗" },
  ],
  pickup: [
    { key: "warehouse_name", label: "Nome sede" },
    { key: "city", label: "Città" },
    { key: "province", label: "Provincia" },
    { key: "phone", label: "Telefono" },
    { key: "discount_type", label: "Tipo sconto" },
    { key: "discount_value", label: "Sconto" },
  ],
  "payment-terms": [
    { key: "code", label: "Codice" },
    { key: "name", label: "Nome" },
    { key: "days", label: "Giorni" },
    { key: "advance_payment", label: "Anticipato", render: (r) => r.advance_payment ? "✓" : "✗" },
    { key: "advance_discount_value", label: "Sconto anticipo" },
  ],
  stacking: [
    { key: "discount_type_a", label: "Tipo A" },
    { key: "discount_type_b", label: "Tipo B" },
    { key: "compatibility", label: "Compatibilità" },
  ],
  carrier: [
    { key: "code", label: "Codice" },
    { key: "business_name", label: "Ragione sociale" },
    { key: "phone", label: "Telefono" },
    { key: "admin_email", label: "Email admin" },
    { key: "operational_emails", label: "Email operative", render: (r) => (r.operational_emails || []).join(", ") || "—" },
  ],
};

// ============================================================================
// Mar 2026 v36 — Editor manuale configurazione giorni di carico + cut-off ordini.
// Non usa valid_from/valid_to né stagioni automatiche. Ogni giorno ON richiede
// cutoff_weekday + cutoff_time (validazione anche server-side).
// ============================================================================
const _LOADING_DAYS = [
  ["MONDAY", "Lunedì"], ["TUESDAY", "Martedì"], ["WEDNESDAY", "Mercoledì"],
  ["THURSDAY", "Giovedì"], ["FRIDAY", "Venerdì"],
];
const _CUTOFF_WEEKDAYS = [
  ["MONDAY", "Lunedì"], ["TUESDAY", "Martedì"], ["WEDNESDAY", "Mercoledì"],
  ["THURSDAY", "Giovedì"], ["FRIDAY", "Venerdì"], ["SATURDAY", "Sabato"],
  ["SUNDAY", "Domenica"],
];
function CarrierLoadingScheduleEditor({ value, onChange }) {
  const cfg = value || {};
  const update = (day, patch) => {
    const next = { ...cfg, [day]: { ...(cfg[day] || {}), ...patch } };
    onChange(next);
  };
  return (
    <div style={{ marginTop: 12, borderTop: "1px solid #eee", paddingTop: 12 }}
         data-testid="carrier-loading-schedule">
      <div style={{ fontWeight: 600, marginBottom: 6 }}>
        Calendario carichi / Cut-off ordini
      </div>
      <div className="muted" style={{ fontSize: ".82rem", marginBottom: 10 }}>
        I giorni di carico si modificano manualmente. Nessun calendario stagionale.
        Per ogni giorno attivo è obbligatorio specificare il giorno e l'ora
        entro cui l'ordine deve arrivare per poter usare quel carico
        (fuso Europe/Rome).
      </div>
      <div style={{ display: "grid", gap: 8 }}>
        {_LOADING_DAYS.map(([key, label]) => {
          const dc = cfg[key] || {};
          const enabled = !!dc.enabled;
          return (
            <div key={key}
                 data-testid={`loading-day-${key}`}
                 style={{ border: "1px solid #e5e7eb", borderRadius: 8,
                          padding: 10, background: enabled ? "#f0fdf4" : "#fff" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <label style={{ display: "flex", alignItems: "center", gap: 6,
                                fontWeight: 600, minWidth: 150 }}>
                  <input type="checkbox"
                         data-testid={`loading-day-${key}-enabled`}
                         checked={enabled}
                         onChange={(e) => update(key, { enabled: e.target.checked })} />
                  {label} — giorno di carico
                </label>
                {enabled && (
                  <>
                    <span className="muted" style={{ fontSize: ".85rem" }}>
                      Cut-off ordine:
                    </span>
                    <select className="ds-input"
                            data-testid={`loading-day-${key}-cutoff-weekday`}
                            value={dc.cutoff_weekday || ""}
                            onChange={(e) => update(key, { cutoff_weekday: e.target.value || null })}
                            style={{ maxWidth: 140 }}>
                      <option value="">—</option>
                      {_CUTOFF_WEEKDAYS.map(([k, l]) => (
                        <option key={k} value={k}>{l}</option>
                      ))}
                    </select>
                    <input type="time"
                           className="ds-input"
                           data-testid={`loading-day-${key}-cutoff-time`}
                           value={dc.cutoff_time || ""}
                           onChange={(e) => update(key, { cutoff_time: e.target.value || null })}
                           style={{ maxWidth: 110 }} />
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


function GenericRuleModal({ schema, mode, data, onClose, onSubmit }) {
  const [f, setF] = useState(() => {
    if (schema === "shipping") return {
      region: data.region || "", min_order_amount: data.min_order_amount || 0,
      max_order_amount: data.max_order_amount || null,
      transport_cost: data.transport_cost || 0,
      free_shipping_threshold: data.free_shipping_threshold || null,
      valid_from: data.valid_from || new Date().toISOString().slice(0, 10),
      valid_to: data.valid_to || null,
      is_active: data.is_active ?? true,
    };
    if (schema === "pallet") return {
      product_id: data.product_id || null, customer_category_id: data.customer_category_id || null,
      min_full_pallets: data.min_full_pallets || 1, discount_type: data.discount_type || "PERCENT",
      value: data.value || 0, valid_from: data.valid_from || new Date().toISOString().slice(0, 10),
      valid_to: data.valid_to || null, stackable: data.stackable ?? true, is_active: data.is_active ?? true,
    };
    if (schema === "pickup") return {
      warehouse_name: data.warehouse_name || "", discount_type: data.discount_type || "PERCENT",
      discount_value: data.discount_value || 0, min_order_amount: data.min_order_amount || 0,
      notes: data.notes || "", is_active: data.is_active ?? true,
      address: data.address || "", zip_code: data.zip_code || "",
      city: data.city || "", province: data.province || "",
      phone: data.phone || "", pickup_hours: data.pickup_hours || "",
    };
    if (schema === "payment-terms") return {
      code: data.code || "", name: data.name || "", days: data.days || 0,
      advance_payment: data.advance_payment ?? false,
      advance_discount_type: data.advance_discount_type || null,
      advance_discount_value: data.advance_discount_value || null,
      is_active: data.is_active ?? true,
    };
    if (schema === "stacking") return {
      discount_type_a: data.discount_type_a || "PROMOTION",
      discount_type_b: data.discount_type_b || "CONTRACT",
      compatibility: data.compatibility || "COMPATIBLE",
      notes: data.notes || "", is_active: data.is_active ?? true,
    };
    if (schema === "carrier") return {
      code: data.code || "", business_name: data.business_name || "",
      phone: data.phone || "", email: data.email || "",
      admin_email: data.admin_email || "",
      operational_emails: (data.operational_emails || []).join(","),
      notes: data.notes || "", is_active: data.is_active ?? true,
      loading_schedule_config: data.loading_schedule_config || {},
    };
    return {};
  });

  const submit = () => {
    let payload = { ...f };
    if (schema === "carrier" && typeof payload.operational_emails === "string") {
      payload.operational_emails = payload.operational_emails.split(",").map((e) => e.trim()).filter(Boolean);
    }
    // Mar 2026 v36 — Validazione client-side loading_schedule_config vettori:
    // se un giorno di carico è ON deve avere sia cutoff_weekday che cutoff_time.
    if (schema === "carrier") {
      const cfg = payload.loading_schedule_config || {};
      for (const day of ["MONDAY","TUESDAY","WEDNESDAY","THURSDAY","FRIDAY"]) {
        const dc = cfg[day];
        if (dc && dc.enabled) {
          if (!dc.cutoff_weekday || !dc.cutoff_time) {
            alert(`Configura giorno di cut-off e orario per ${day} prima di salvare.`);
            return;
          }
        }
      }
    }
    onSubmit(payload);
  };

  return (
    <Modal title={mode === "create" ? "Nuovo elemento" : "Modifica"} onClose={onClose} testid={`${schema}-modal`}>
      {schema === "shipping" && (<>
        <FormField label="Regione *"><Input value={f.region} onChange={(e) => setF({ ...f, region: e.target.value })} /></FormField>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          <FormField label="Min ordine €"><Input type="number" step="0.01" value={f.min_order_amount} onChange={(e) => setF({ ...f, min_order_amount: parseFloat(e.target.value) })} /></FormField>
          <FormField label="Max ordine € (vuoto=∞)"><Input type="number" step="0.01" value={f.max_order_amount || ""} onChange={(e) => setF({ ...f, max_order_amount: e.target.value ? parseFloat(e.target.value) : null })} /></FormField>
          <FormField label="Costo trasporto €"><Input type="number" step="0.01" value={f.transport_cost} onChange={(e) => setF({ ...f, transport_cost: parseFloat(e.target.value) })} /></FormField>
          <FormField label="Soglia porto franco €"><Input type="number" step="0.01" value={f.free_shipping_threshold || ""} onChange={(e) => setF({ ...f, free_shipping_threshold: e.target.value ? parseFloat(e.target.value) : null })} /></FormField>
          <FormField label="Dal"><Input type="date" value={f.valid_from} onChange={(e) => setF({ ...f, valid_from: e.target.value })} /></FormField>
          <FormField label="Al (vuoto=∞)"><Input type="date" value={f.valid_to || ""} onChange={(e) => setF({ ...f, valid_to: e.target.value || null })} /></FormField>
        </div>
      </>)}
      {schema === "pallet" && (<>
        <FormField label="Min pallet completi"><Input type="number" value={f.min_full_pallets} onChange={(e) => setF({ ...f, min_full_pallets: parseInt(e.target.value) })} /></FormField>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          <FormField label="Tipo">
            <select className="ds-input" value={f.discount_type} onChange={(e) => setF({ ...f, discount_type: e.target.value })}>
              <option value="PERCENT">%</option><option value="FIXED_PER_CARTON">€/cart</option><option value="FIXED_TOTAL">€ tot</option>
            </select>
          </FormField>
          <FormField label="Valore"><Input type="number" step="0.01" value={f.value} onChange={(e) => setF({ ...f, value: parseFloat(e.target.value) })} /></FormField>
          <FormField label="Dal"><Input type="date" value={f.valid_from} onChange={(e) => setF({ ...f, valid_from: e.target.value })} /></FormField>
          <FormField label="Al"><Input type="date" value={f.valid_to || ""} onChange={(e) => setF({ ...f, valid_to: e.target.value || null })} /></FormField>
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <input type="checkbox" checked={f.stackable} onChange={(e) => setF({ ...f, stackable: e.target.checked })} /> Stackable
        </label>
      </>)}
      {schema === "pickup" && (<>
        <FormField label="Nome sede *"><Input value={f.warehouse_name} data-testid="pickup-warehouse-name" onChange={(e) => setF({ ...f, warehouse_name: e.target.value })} /></FormField>
        <FormField label="Indirizzo"><Input value={f.address} data-testid="pickup-address" onChange={(e) => setF({ ...f, address: e.target.value })} placeholder="Es. Via delle Industrie 12" /></FormField>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr 1fr", gap: 8 }}>
          <FormField label="CAP"><Input value={f.zip_code} data-testid="pickup-zip" onChange={(e) => setF({ ...f, zip_code: e.target.value })} placeholder="20066" maxLength={10} /></FormField>
          <FormField label="Città"><Input value={f.city} data-testid="pickup-city" onChange={(e) => setF({ ...f, city: e.target.value })} placeholder="Melzo" /></FormField>
          <FormField label="Provincia"><Input value={f.province} data-testid="pickup-province" onChange={(e) => setF({ ...f, province: e.target.value.toUpperCase() })} placeholder="MI" maxLength={4} /></FormField>
        </div>
        <FormField label="Telefono"><Input value={f.phone} data-testid="pickup-phone" onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="+39 02 9550000" /></FormField>
        <FormField label="Orari / note per il ritiro">
          <textarea className="ds-input" rows={3} value={f.pickup_hours || ""}
                    data-testid="pickup-hours"
                    onChange={(e) => setF({ ...f, pickup_hours: e.target.value })}
                    placeholder="Es. Lun-Ven 08:00-12:00 / 14:00-17:00" />
        </FormField>
        <div style={{ borderTop: "1px solid #eee", margin: "12px 0 6px" }} />
        <div style={{ fontSize: ".85rem", color: "#666", marginBottom: 6 }}>Sconto ritiro cliente (non tocca gli ordini storici)</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
          <FormField label="Tipo">
            <select className="ds-input" value={f.discount_type} onChange={(e) => setF({ ...f, discount_type: e.target.value })}>
              <option value="PERCENT">%</option><option value="FIXED_PER_CARTON">€/cart</option><option value="FIXED_TOTAL">€ tot</option>
            </select>
          </FormField>
          <FormField label="Valore sconto"><Input type="number" step="0.01" value={f.discount_value} onChange={(e) => setF({ ...f, discount_value: parseFloat(e.target.value) })} /></FormField>
          <FormField label="Min ordine €"><Input type="number" step="0.01" value={f.min_order_amount} onChange={(e) => setF({ ...f, min_order_amount: parseFloat(e.target.value) })} /></FormField>
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8 }}>
          <input type="checkbox" checked={f.is_active} data-testid="pickup-active"
                 onChange={(e) => setF({ ...f, is_active: e.target.checked })} /> Sede attiva
        </label>
      </>)}
      {schema === "payment-terms" && (<>
        {mode === "create" && <FormField label="Codice *"><Input value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} /></FormField>}
        <FormField label="Nome *"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></FormField>
        <FormField label="Giorni"><Input type="number" value={f.days} onChange={(e) => setF({ ...f, days: parseInt(e.target.value) })} /></FormField>
        <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <input type="checkbox" checked={f.advance_payment} onChange={(e) => setF({ ...f, advance_payment: e.target.checked })} /> Consente pagamento anticipato
        </label>
        {f.advance_payment && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            <FormField label="Tipo sconto anticipo">
              <select className="ds-input" value={f.advance_discount_type || ""} onChange={(e) => setF({ ...f, advance_discount_type: e.target.value || null })}>
                <option value="">—</option>
                <option value="PERCENT">%</option><option value="FIXED_PER_CARTON">€/cart</option><option value="FIXED_TOTAL">€ tot</option>
              </select>
            </FormField>
            <FormField label="Valore sconto anticipo"><Input type="number" step="0.01" value={f.advance_discount_value || ""} onChange={(e) => setF({ ...f, advance_discount_value: e.target.value ? parseFloat(e.target.value) : null })} /></FormField>
          </div>
        )}
      </>)}
      {schema === "stacking" && (<>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          <FormField label="Tipo A"><Input value={f.discount_type_a} onChange={(e) => setF({ ...f, discount_type_a: e.target.value })} /></FormField>
          <FormField label="Tipo B"><Input value={f.discount_type_b} onChange={(e) => setF({ ...f, discount_type_b: e.target.value })} /></FormField>
        </div>
        <FormField label="Compatibilità">
          <select className="ds-input" value={f.compatibility} onChange={(e) => setF({ ...f, compatibility: e.target.value })}>
            <option value="COMPATIBLE">COMPATIBLE (cumulativo)</option>
            <option value="INCOMPATIBLE">INCOMPATIBLE (non cumulativo)</option>
            <option value="PREFER_A">PREFER_A</option>
            <option value="PREFER_B">PREFER_B</option>
          </select>
        </FormField>
        <FormField label="Note"><Input value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></FormField>
      </>)}
      {schema === "carrier" && (<>
        {mode === "create" && <FormField label="Codice *"><Input value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} /></FormField>}
        <FormField label="Ragione sociale *"><Input value={f.business_name} onChange={(e) => setF({ ...f, business_name: e.target.value })} /></FormField>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          <FormField label="Telefono"><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></FormField>
          <FormField label="Email admin"><Input value={f.admin_email} onChange={(e) => setF({ ...f, admin_email: e.target.value })} /></FormField>
        </div>
        <FormField label="Email operative (separate da virgola)">
          <Input value={f.operational_emails} onChange={(e) => setF({ ...f, operational_emails: e.target.value })}
                 placeholder="op1@vettore.it, op2@vettore.it" />
        </FormField>
        <FormField label="Note"><Input value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></FormField>
        <CarrierLoadingScheduleEditor
          value={f.loading_schedule_config || {}}
          onChange={(cfg) => setF({ ...f, loading_schedule_config: cfg })} />
      </>)}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
        <Button onClick={onClose}>Annulla</Button>
        <Button variant="primary" onClick={submit} data-testid={`${schema}-submit`}>Salva</Button>
      </div>
    </Modal>
  );
}

// ---------- Reusable primitives ----------
function Modal({ title, onClose, children, testid }) {
  return (
    <div role="dialog" data-testid={testid}
         style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)",
                  display: "flex", alignItems: "center", justifyContent: "center", zIndex: 60 }}>
      <div style={{ background: "#fff", borderRadius: 12, width: "95%", maxWidth: 640,
                    maxHeight: "92vh", overflow: "auto", padding: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h2 style={{ margin: 0 }}>{title}</h2>
          <Button onClick={onClose}>✕</Button>
        </div>
        <div style={{ marginTop: 12 }}>{children}</div>
      </div>
    </div>
  );
}

function FormField({ label, children }) {
  return <div style={{ marginBottom: 8 }}><Label>{label}</Label>{children}</div>;
}


// ============================================================================
// B2-refinement B — Object Storage attachments (RBAC-aware UI)
// ============================================================================
function fmtSize(n) {
  if (n == null) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export function AttachmentsPanel({ ownerType, ownerId, compact = false }) {
  const { user, apiGet, apiPost, token } = useAuth();
  const [atts, setAtts] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const roles = user?.roles || [];
  const isAdmin = roles.includes("ADMIN");
  const canUpload = isAdmin;
  const canArchive = isAdmin;

  const load = async () => {
    try {
      setAtts(await apiGet(`/api/admin/attachments/${ownerType}/${ownerId}`));
    } catch (e) {
      setErr(e?.message || "load_failed");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [ownerType, ownerId]);

  const onFile = async (e) => {
    setErr(null);
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 25 * 1024 * 1024) { setErr("File > 25MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const API = process.env.REACT_APP_BACKEND_URL;
      const resp = await fetch(`${API}/api/admin/attachments/${ownerType}/${ownerId}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!resp.ok) {
        const t = await resp.text();
        throw new Error(t || `status_${resp.status}`);
      }
      await load();
    } catch (ex) {
      setErr(ex?.message || "upload_failed");
    } finally {
      setUploading(false);
    }
  };

  const doDownload = async (a) => {
    setErr(null);
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const resp = await fetch(
        `${API}/api/admin/attachments/${ownerType}/${ownerId}/${a.id}/download`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!resp.ok) throw new Error(`status_${resp.status}`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url; link.download = a.filename || "file"; document.body.appendChild(link);
      link.click(); document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (ex) {
      setErr(ex?.message || "download_failed");
    }
  };

  const doArchive = async (a) => {
    if (!window.confirm(`Archiviare "${a.filename}"? Il file non sarà più scaricabile.`)) return;
    try {
      await apiPost(`/api/admin/attachments/${ownerType}/${ownerId}/${a.id}/archive`, {});
      await load();
    } catch (e) {
      setErr(e?.message || "archive_failed");
    }
  };

  if (atts === null && !err) return <div style={{ padding: 8, color: "#64748b" }}>Caricamento allegati…</div>;
  const list = atts || [];
  const active = list.filter(a => !a.is_archived);
  const archived = list.filter(a => a.is_archived);
  const showList = showArchived ? list : active;

  return (
    <div data-testid={`attachments-${ownerType}-${ownerId}`} style={{
      border: compact ? "1px dashed #cbd5e1" : "1px solid #e5e7eb",
      background: compact ? "#F8FAFC" : "white",
      borderRadius: 6, padding: compact ? 8 : 12, marginTop: 6,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 6 }}>
        <strong style={{ fontSize: compact ? 12 : 14 }}>
          📎 Allegati privati ({active.length}{archived.length ? ` · ${archived.length} archiviati` : ""})
        </strong>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          {archived.length > 0 && (
            <label style={{ fontSize: 11, color: "#475569" }}>
              <input type="checkbox" checked={showArchived}
                     onChange={(e) => setShowArchived(e.target.checked)}
                     data-testid={`att-show-archived-${ownerType}`} /> mostra archiviati
            </label>
          )}
          {canUpload && (
            <label style={{ cursor: "pointer" }}>
              <input type="file" onChange={onFile} style={{ display: "none" }} disabled={uploading}
                     data-testid={`att-upload-input-${ownerType}`} />
              <span className="ds-btn ds-btn-primary" style={{
                display: "inline-block", padding: "4px 12px", background: uploading ? "#94A3B8" : "#0369A1",
                color: "white", borderRadius: 4, fontSize: 12,
              }} data-testid={`att-upload-btn-${ownerType}`}>
                {uploading ? "Caricamento…" : "+ Upload"}
              </span>
            </label>
          )}
        </div>
      </div>

      {err && <div style={{ color: "#dc2626", fontSize: 12, marginTop: 6 }}
                   data-testid={`att-err-${ownerType}`}>⚠️ {err}</div>}

      {showList.length === 0 ? (
        <div style={{ color: "#64748b", fontSize: 12, marginTop: 8, fontStyle: "italic" }}>
          Nessun allegato{canUpload ? " — carica un file per iniziare." : "."}
        </div>
      ) : (
        <table className="ds-table" style={{ marginTop: 8, fontSize: 12 }}>
          <thead><tr><th>Nome</th><th>Tipo</th><th>Dim.</th><th>Caricato</th><th>Stato</th><th></th></tr></thead>
          <tbody>
            {showList.map(a => (
              <tr key={a.id} style={{ opacity: a.is_archived ? 0.55 : 1 }}
                  data-testid={`att-row-${a.id}`}>
                <td>{a.filename}</td>
                <td style={{ color: "#64748b" }}>{(a.content_type || "").split("/").pop() || "—"}</td>
                <td>{fmtSize(a.size)}</td>
                <td style={{ color: "#64748b" }}>{a.uploaded_at ? new Date(a.uploaded_at).toLocaleDateString() : "—"}</td>
                <td>{a.is_archived
                    ? <Badge tone="neutral">Archiviato</Badge>
                    : <Badge tone="success">Attivo</Badge>}</td>
                <td style={{ display: "flex", gap: 4 }}>
                  {!a.is_archived && (
                    <Button size="sm" onClick={() => doDownload(a)}
                            data-testid={`att-download-${a.id}`}>⬇︎</Button>
                  )}
                  {canArchive && !a.is_archived && (
                    <Button size="sm" variant="danger" onClick={() => doArchive(a)}
                            data-testid={`att-archive-${a.id}`}>Archivia</Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {!isAdmin && (
        <div style={{ fontSize: 11, color: "#64748b", marginTop: 6, fontStyle: "italic" }}>
          Sola visione. Upload/archiviazione riservati all'Admin.
        </div>
      )}
    </div>
  );
}

function MarketingRow({ m, onDelete }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr data-testid={`mkt-row-${m.id}`}>
        <td style={{ width: 24 }}>
          <button type="button" onClick={() => setOpen(o => !o)}
                  aria-label={open ? "Chiudi" : "Espandi"}
                  data-testid={`mkt-toggle-${m.id}`}
                  style={{ border: "none", background: "transparent", cursor: "pointer", fontSize: 14 }}>
            {open ? "▾" : "▸"}
          </button>
        </td>
        <td>{m.activity_type}</td><td>{m.description}</td>
        <td>{m.monetary_value}</td><td>{m.planned_date || "—"}</td>
        <td><Badge tone={m.status === "COMPLETED" ? "success" : "neutral"}>{m.status}</Badge></td>
        <td><Button size="sm" variant="danger" onClick={onDelete}
                    data-testid={`mkt-delete-${m.id}`}>✕</Button></td>
      </tr>
      {open && (
        <tr>
          <td></td>
          <td colSpan={6}>
            <AttachmentsPanel ownerType="marketing-activities" ownerId={m.id} compact />
          </td>
        </tr>
      )}
    </>
  );
}

// ============================================================================
// Feb 2026 — Agents Tab (list · create · edit · soft-delete · assigned customers)
function AgentsTab() {
  const { apiGet, apiPost, apiPatch, apiDelete } = useAuth();
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);
  const [modal, setModal] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [q, setQ] = useState("");

  const load = async () => {
    setErr(null);
    try {
      const qs = new URLSearchParams();
      if (!showInactive) qs.set("active", "true");
      if (q) qs.set("q", q);
      const r = await apiGet(`/api/admin/agents?${qs.toString()}`);
      setRows(r);
    } catch (e) { setErr(e?.message || String(e)); setRows([]); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [showInactive]);

  const doDelete = async (a) => {
    if (!window.confirm(`Eliminare l'agente ${a.display_name || a.code}?`)) return;
    try {
      await apiDelete(`/api/admin/agents/${a.id}`);
      await load();
    } catch (e) {
      const msg = e?.data?.detail || e?.message || String(e);
      if (String(msg).startsWith("agent_has_customers:")) {
        const n = msg.split(":")[1];
        alert(`Impossibile eliminare: l'agente ha ${n} clienti assegnati. Riassegnali prima.`);
      } else {
        alert(`Errore: ${msg}`);
      }
    }
  };

  return (
    <>
      {err && <Alert variant="danger">Errore: {String(err)}</Alert>}
      <Card>
        <Card.Header>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
                        gap: 8, flexWrap: "wrap" }}>
            <strong>Agenti</strong>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <Input placeholder="Cerca codice/nome/email…" value={q}
                     data-testid="agent-search" style={{ width: 240 }}
                     onChange={(e) => setQ(e.target.value)}
                     onKeyDown={(e) => e.key === "Enter" && load()} />
              <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 13 }}>
                <input type="checkbox" checked={showInactive}
                       data-testid="agent-show-inactive"
                       onChange={(e) => setShowInactive(e.target.checked)} />
                Mostra inattivi
              </label>
              <Button variant="primary" onClick={() => setModal({ mode: "create", data: {} })}
                      data-testid="agent-new">+ Nuovo agente</Button>
            </div>
          </div>
        </Card.Header>
        <Card.Body>
          {!rows && <p className="muted">Caricamento…</p>}
          {rows && rows.length === 0 && <Alert tone="info">Nessun agente.</Alert>}
          {rows && rows.length > 0 && (
            <div className="ds-table-wrap">
              <table className="ds-table" data-testid="agent-table">
                <thead><tr>
                  <th>Codice</th><th>Nome commerciale</th><th>Email</th>
                  <th>Telefono</th><th style={{ textAlign: "center" }}>Clienti</th>
                  <th>Stato</th><th></th>
                </tr></thead>
                <tbody>
                  {rows.map((a) => (
                    <React.Fragment key={a.id}>
                      <tr data-testid={`agent-row-${a.code}`}>
                        <td><code>{a.code}</code></td>
                        <td>
                          <strong>{a.display_name
                            || [a.first_name, a.last_name].filter(Boolean).join(" ")
                            || <span className="muted">—</span>}</strong>
                        </td>
                        <td>{a.email || <span className="muted">—</span>}</td>
                        <td>{a.phone || <span className="muted">—</span>}</td>
                        <td style={{ textAlign: "center" }}>
                          {a.customers_count > 0
                            ? <Badge tone="info">{a.customers_count}</Badge>
                            : <span className="muted">0</span>}
                        </td>
                        <td>
                          <Badge tone={a.is_active ? "success" : "neutral"}>
                            {a.is_active ? "Attivo" : "Inattivo"}
                          </Badge>
                        </td>
                        <td>
                          <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                            <Button size="sm"
                                    onClick={() => setModal({ mode: "edit", data: a })}
                                    data-testid={`agent-edit-${a.code}`}>Modifica</Button>
                            {a.customers_count > 0 && (
                              <Button size="sm"
                                      onClick={() => setExpanded(expanded === a.id ? null : a.id)}
                                      data-testid={`agent-customers-${a.code}`}>
                                {expanded === a.id ? "▲ Chiudi" : "▼ Clienti"}
                              </Button>
                            )}
                            <Button size="sm" variant="danger"
                                    onClick={() => doDelete(a)}
                                    data-testid={`agent-delete-${a.code}`}>Elimina</Button>
                          </div>
                        </td>
                      </tr>
                      {expanded === a.id && (
                        <tr><td colSpan={7} style={{ background: "#fafafa" }}>
                          <AgentCustomersPanel agentId={a.id} />
                        </td></tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card.Body>
      </Card>
      {modal && (
        <AgentModal mode={modal.mode} data={modal.data}
                    onClose={() => setModal(null)}
                    onSubmit={async (payload) => {
                      try {
                        if (modal.mode === "create")
                          await apiPost("/api/admin/agents", payload);
                        else
                          await apiPatch(`/api/admin/agents/${modal.data.id}`, payload);
                        setModal(null);
                        await load();
                      } catch (e) {
                        const msg = e?.data?.detail || e?.message || String(e);
                        alert(`Errore: ${msg}`);
                      }
                    }} />
      )}
    </>
  );
}

function AgentModal({ mode, data, onClose, onSubmit }) {
  // Feb 2026 v33 — Contatti persona (fino a 2, opzionali). Struttura JSONB
  // salvata in Agent.contacts; email/telefono/WhatsApp separati dai dati
  // fiscali dell'agenzia (che restano nei campi principali sopra).
  const emptyContact = { first_name: "", last_name: "", email: "", phone: "", whatsapp: "" };
  const initialContacts = (() => {
    const c = Array.isArray(data?.contacts) ? data.contacts : [];
    return [
      { ...emptyContact, ...(c[0] || {}) },
      { ...emptyContact, ...(c[1] || {}) },
    ].map((x) => ({
      first_name: x.first_name || "",
      last_name: x.last_name || "",
      email: x.email || "",
      phone: x.phone || "",
      whatsapp: x.whatsapp || "",
    }));
  })();
  const [f, setF] = useState(() => ({
    code: data?.code || "",
    first_name: data?.first_name || "",
    last_name: data?.last_name || "",
    display_name: data?.display_name || "",
    email: data?.email || "",
    pec_email: data?.pec_email || "",
    phone: data?.phone || "",
    whatsapp: data?.whatsapp || "",
    address: data?.address || "",
    zip_code: data?.zip_code || "",
    city: data?.city || "",
    province: data?.province || "",
    vat_number: data?.vat_number || "",
    tax_code: data?.tax_code || "",
    notes: data?.notes || "",
    is_active: data?.is_active !== false,
  }));
  const [contacts, setContacts] = useState(initialContacts);
  const [err, setErr] = useState(null);
  const setContactField = (idx, key, value) => {
    setContacts((prev) => prev.map((c, i) => i === idx ? { ...c, [key]: value } : c));
  };
  const isContactFilled = (c) =>
    !!(c.first_name || c.last_name || c.email || c.phone || c.whatsapp);
  const isValidEmail = (e) => !e || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
  const submit = () => {
    setErr(null);
    if (mode === "create" && !f.code) { setErr("Codice obbligatorio"); return; }
    for (let i = 0; i < contacts.length; i++) {
      if (!isValidEmail(contacts[i].email)) {
        setErr(`Email Contatto ${i + 1} non valida`); return;
      }
    }
    const payload = { ...f };
    if (mode === "edit") delete payload.code;
    Object.keys(payload).forEach((k) => {
      if (payload[k] === "" && k !== "is_active") payload[k] = null;
    });
    // Contacts: strip empty entries; backend applica la stessa normalizzazione
    // ma inviamo solo i non-vuoti per audit log leggibile.
    const cleanContacts = contacts
      .filter(isContactFilled)
      .map((c) => ({
        first_name: c.first_name.trim() || null,
        last_name: c.last_name.trim() || null,
        email: c.email.trim() || null,
        phone: c.phone.trim() || null,
        whatsapp: c.whatsapp.trim() || null,
      }));
    payload.contacts = cleanContacts;
    onSubmit(payload);
  };
  const renderContactBlock = (idx) => (
    <details data-testid={`agent-contact-${idx + 1}`}
             open={isContactFilled(contacts[idx])}
             style={{ border: "1px solid #e5e7eb", borderRadius: 6, padding: 8, marginTop: 8 }}>
      <summary style={{ cursor: "pointer", fontWeight: 600, fontSize: ".92rem" }}>
        Contatto {idx + 1}{isContactFilled(contacts[idx]) ? " ✓" : " (opzionale)"}
      </summary>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 8 }}>
        <FormField label="Nome">
          <Input value={contacts[idx].first_name}
                 data-testid={`agent-contact-${idx + 1}-first-name`}
                 onChange={(e) => setContactField(idx, "first_name", e.target.value)} />
        </FormField>
        <FormField label="Cognome">
          <Input value={contacts[idx].last_name}
                 data-testid={`agent-contact-${idx + 1}-last-name`}
                 onChange={(e) => setContactField(idx, "last_name", e.target.value)} />
        </FormField>
        <FormField label="Email">
          <Input type="email" value={contacts[idx].email}
                 data-testid={`agent-contact-${idx + 1}-email`}
                 onChange={(e) => setContactField(idx, "email", e.target.value)} />
        </FormField>
        <FormField label="Telefono">
          <Input value={contacts[idx].phone}
                 data-testid={`agent-contact-${idx + 1}-phone`}
                 onChange={(e) => setContactField(idx, "phone", e.target.value)} />
        </FormField>
        <FormField label="WhatsApp">
          <Input value={contacts[idx].whatsapp}
                 data-testid={`agent-contact-${idx + 1}-whatsapp`}
                 onChange={(e) => setContactField(idx, "whatsapp", e.target.value)} />
        </FormField>
      </div>
    </details>
  );
  return (
    <Modal onClose={onClose} title={mode === "create" ? "Nuovo agente" : `Modifica agente ${data.code}`}
           size="lg" data-testid="agent-modal">
      {err && <Alert variant="danger">{err}</Alert>}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <FormField label="Codice *">
          <Input value={f.code} data-testid="agent-code"
                 disabled={mode === "edit"}
                 onChange={(e) => setF({ ...f, code: e.target.value })} />
        </FormField>
        <FormField label="Nome commerciale (display)">
          <Input value={f.display_name} data-testid="agent-display-name"
                 onChange={(e) => setF({ ...f, display_name: e.target.value })} />
        </FormField>
        <FormField label="Nome"><Input value={f.first_name}
          onChange={(e) => setF({ ...f, first_name: e.target.value })} /></FormField>
        <FormField label="Cognome"><Input value={f.last_name}
          onChange={(e) => setF({ ...f, last_name: e.target.value })} /></FormField>
        <FormField label="Email agenzia">
          <Input type="email" value={f.email} data-testid="agent-email"
                 onChange={(e) => setF({ ...f, email: e.target.value })} />
        </FormField>
        <FormField label="PEC">
          <Input type="email" value={f.pec_email}
                 onChange={(e) => setF({ ...f, pec_email: e.target.value })} />
        </FormField>
        <FormField label="Telefono">
          <Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} />
        </FormField>
        <FormField label="WhatsApp">
          <Input value={f.whatsapp} onChange={(e) => setF({ ...f, whatsapp: e.target.value })} />
        </FormField>
        <FormField label="Indirizzo">
          <Input value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} />
        </FormField>
        <FormField label="CAP · Città · Prov.">
          <div style={{ display: "flex", gap: 4 }}>
            <Input value={f.zip_code} style={{ width: 90 }}
                   onChange={(e) => setF({ ...f, zip_code: e.target.value })} />
            <Input value={f.city} placeholder="Città"
                   onChange={(e) => setF({ ...f, city: e.target.value })} />
            <Input value={f.province} style={{ width: 60 }}
                   onChange={(e) => setF({ ...f, province: e.target.value })} />
          </div>
        </FormField>
        <FormField label="P.IVA">
          <Input value={f.vat_number}
                 onChange={(e) => setF({ ...f, vat_number: e.target.value })} />
        </FormField>
        <FormField label="Codice fiscale">
          <Input value={f.tax_code}
                 onChange={(e) => setF({ ...f, tax_code: e.target.value })} />
        </FormField>
      </div>
      <FormField label="Note">
        <textarea className="ds-input" rows={2} value={f.notes}
                  onChange={(e) => setF({ ...f, notes: e.target.value })} />
      </FormField>
      <div style={{ marginTop: 12 }}>
        <div style={{ fontWeight: 700, fontSize: ".92rem", marginBottom: 4 }}>
          Contatti agenzia
        </div>
        <div className="muted" style={{ fontSize: ".78rem", marginBottom: 6 }}>
          Fino a 2 referenti opzionali. Tutti i campi sono facoltativi.
        </div>
        {renderContactBlock(0)}
        {renderContactBlock(1)}
      </div>
      <label style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8 }}>
        <input type="checkbox" checked={f.is_active} data-testid="agent-active"
               onChange={(e) => setF({ ...f, is_active: e.target.checked })} />
        Agente attivo
      </label>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
        <Button onClick={onClose}>Annulla</Button>
        <Button variant="primary" onClick={submit} data-testid="agent-submit">Salva</Button>
      </div>
    </Modal>
  );
}

function AgentCustomersPanel({ agentId }) {
  const { apiGet } = useAuth();
  const [rows, setRows] = useState(null);
  useEffect(() => {
    apiGet(`/api/admin/agents/${agentId}/customers`).then(setRows).catch(() => setRows([]));
    // eslint-disable-next-line
  }, [agentId]);
  if (!rows) return <p className="muted" style={{ padding: 8 }}>Caricamento clienti…</p>;
  if (rows.length === 0) return <p className="muted" style={{ padding: 8 }}>Nessun cliente assegnato.</p>;
  return (
    <div style={{ padding: 8 }}>
      <h4 style={{ marginTop: 0 }}>Clienti assegnati ({rows.length})</h4>
      <table className="ds-table" style={{ fontSize: 12 }}>
        <thead><tr><th>Codice</th><th>Ragione sociale</th><th>Email</th><th>Telefono</th></tr></thead>
        <tbody>
          {rows.map((c) => (
            <tr key={c.id}>
              <td><code>{c.code}</code></td>
              <td>{c.business_name}</td>
              <td>{c.email || <span className="muted">—</span>}</td>
              <td>{c.phone || <span className="muted">—</span>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


// Phase C — Cooperatives Tab (list · create · members)
// ============================================================================
function CooperativesTab() {
  const { apiGet, apiPost, apiPatch, apiDelete, user } = useAuth();
  const [rows, setRows] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [err, setErr] = useState(null);
  const [modal, setModal] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const isAdmin = (user?.roles || []).includes("ADMIN");

  const load = async () => {
    try {
      const [cs, cust] = await Promise.all([
        apiGet("/api/admin/cooperatives"),
        apiGet("/api/customers"),
      ]);
      setRows(cs); setCustomers(cust);
    } catch (e) { setErr(e?.message || String(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <>
      {err && <Alert variant="danger">Errore: {String(err)}</Alert>}
      <Card>
        <Card.Header>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <strong>Cooperative</strong>
            {isAdmin && (
              <Button variant="primary" onClick={() => setModal({ mode: "create", data: {} })}
                      data-testid="coop-new">+ Nuova cooperativa</Button>
            )}
          </div>
        </Card.Header>
        <Card.Body>
          {!rows && <p className="muted">Caricamento…</p>}
          {rows && rows.length === 0 && <Alert tone="info">Nessuna cooperativa.</Alert>}
          {rows && rows.length > 0 && (
            <div className="ds-table-wrap">
              <table className="ds-table" data-testid="coop-table">
                <thead><tr><th>Codice</th><th>Nome</th><th>Descrizione</th><th>Attiva</th><th></th></tr></thead>
                <tbody>
                  {rows.map((co) => (
                    <React.Fragment key={co.id}>
                      <tr data-testid={`coop-row-${co.code}`}>
                        <td><code>{co.code}</code></td>
                        <td>{co.name}</td>
                        <td style={{ color: "#64748b", fontSize: 12 }}>
                          {co.description || "—"}
                        </td>
                        <td><Badge tone={co.is_active ? "success" : "neutral"}>
                          {co.is_active ? "Attiva" : "Inattiva"}
                        </Badge></td>
                        <td>
                          <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                            {isAdmin && (
                              <Button size="sm" onClick={() => setModal({ mode: "edit", data: co })}
                                      data-testid={`coop-edit-${co.code}`}>Modifica</Button>
                            )}
                            <Button size="sm" onClick={() => setExpanded(expanded === co.id ? null : co.id)}
                                    data-testid={`coop-expand-${co.code}`}>
                              {expanded === co.id ? "▲ Chiudi" : "▼ Membri"}
                            </Button>
                          </div>
                        </td>
                      </tr>
                      {expanded === co.id && (
                        <tr><td colSpan={5} style={{ background: "#fafafa" }}>
                          <CooperativeMembersPanel coop={co} customers={customers} canEdit={isAdmin} />
                        </td></tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card.Body>
      </Card>
      {modal && (
        <CooperativeModal mode={modal.mode} data={modal.data}
          onClose={() => setModal(null)}
          onSubmit={async (payload) => {
            if (modal.mode === "create") await apiPost("/api/admin/cooperatives", payload);
            else await apiPatch(`/api/admin/cooperatives/${modal.data.id}`, payload);
            setModal(null); await load();
          }} />
      )}
    </>
  );
}


function CooperativeModal({ mode, data, onClose, onSubmit }) {
  const [f, setF] = useState({
    code: data.code || "", name: data.name || "",
    description: data.description || "",
    contacts_email: (data.contacts?.email) || "",
    contacts_phone: (data.contacts?.phone) || "",
    is_active: data.is_active ?? true,
    notes: data.notes || "",
  });
  const [err, setErr] = useState(null);
  const submit = () => {
    setErr(null);
    if (!f.name?.trim()) return setErr("Nome obbligatorio");
    if (mode === "create" && !f.code?.trim()) return setErr("Codice obbligatorio");
    const payload = {
      name: f.name, description: f.description || null,
      contacts: (f.contacts_email || f.contacts_phone)
        ? { email: f.contacts_email || null, phone: f.contacts_phone || null }
        : null,
      is_active: f.is_active,
      notes: f.notes || null,
    };
    if (mode === "create") payload.code = f.code;
    onSubmit(payload);
  };
  return (
    <Modal title={mode === "create" ? "Nuova cooperativa" : "Modifica cooperativa"}
           onClose={onClose} testid="coop-modal">
      {mode === "create" && (
        <FormField label="Codice *">
          <Input value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })}
                 data-testid="coop-code" placeholder="COOP-01" />
        </FormField>
      )}
      <FormField label="Nome *">
        <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })}
               data-testid="coop-name" />
      </FormField>
      <FormField label="Descrizione">
        <Input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
      </FormField>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <FormField label="Email contatto">
          <Input type="email" value={f.contacts_email}
                 onChange={(e) => setF({ ...f, contacts_email: e.target.value })} />
        </FormField>
        <FormField label="Telefono">
          <Input value={f.contacts_phone}
                 onChange={(e) => setF({ ...f, contacts_phone: e.target.value })} />
        </FormField>
      </div>
      <FormField label="Note">
        <Input value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
      </FormField>
      <FormField label="Stato">
        <select className="ds-input" value={f.is_active ? "true" : "false"}
                onChange={(e) => setF({ ...f, is_active: e.target.value === "true" })}>
          <option value="true">Attiva</option>
          <option value="false">Inattiva</option>
        </select>
      </FormField>
      {err && <div style={{ color: "#dc2626", fontSize: 12 }}>{err}</div>}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
        <Button onClick={onClose}>Annulla</Button>
        <Button variant="primary" onClick={submit} data-testid="coop-submit">Salva</Button>
      </div>
    </Modal>
  );
}


function CooperativeMembersPanel({ coop, customers, canEdit }) {
  const { apiGet, apiPost, apiPatch, apiDelete } = useAuth();
  const [members, setMembers] = useState(null);
  const [historyView, setHistoryView] = useState(false);
  const [addModal, setAddModal] = useState(false);
  const [err, setErr] = useState(null);
  const customerById = Object.fromEntries((customers || []).map(c => [c.id, c]));

  const load = async () => {
    try {
      const rows = await apiGet(
        `/api/admin/cooperatives/${coop.id}/members${historyView ? "?all_history=true" : ""}`
      );
      setMembers(rows);
    } catch (e) { setErr(e?.message || String(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [coop.id, historyView]);

  const setValidTo = async (m) => {
    const t = window.prompt("Imposta valid_to (YYYY-MM-DD, vuoto = aperto)");
    if (t === null) return;
    await apiPatch(`/api/admin/cooperatives/${coop.id}/members/${m.id}`,
                   { valid_to: t || null });
    await load();
  };

  const associatedContracts = async () => {
    const cs = await apiGet(`/api/admin/contracts?cooperative_id=${coop.id}`);
    alert(`Contratti associati: ${cs.length}\n${cs.map(c => `${c.code} · ${c.name}`).join("\n") || "— nessuno —"}`);
  };
  // Feb 2026 — Contratti associati inline (sostituisce alert())
  const [contracts, setContracts] = useState(null);
  const [contractsOpen, setContractsOpen] = useState(false);
  const loadContracts = async () => {
    setContractsOpen(true);
    try {
      const cs = await apiGet(`/api/admin/contracts?cooperative_id=${coop.id}`);
      setContracts(cs);
    } catch (e) { setContracts([]); }
  };

  return (
    <div style={{ padding: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, flexWrap: "wrap", gap: 6 }}>
        <strong>Membri cooperativa</strong>
        <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
          <label style={{ fontSize: 12, color: "#64748b" }}>
            <input type="checkbox" checked={historyView}
                   onChange={(e) => setHistoryView(e.target.checked)}
                   data-testid={`coop-history-toggle-${coop.code}`} /> mostra storico completo
          </label>
          <Button size="sm" onClick={loadContracts}
                  data-testid={`coop-contracts-${coop.code}`}>
            📄 Contratti associati{contracts ? ` (${contracts.length})` : ""}
          </Button>
          {canEdit && (
            <Button size="sm" variant="primary" onClick={() => setAddModal(true)}
                    data-testid={`coop-add-member-${coop.code}`}>+ Aggiungi membro</Button>
          )}
        </div>
      </div>
      {err && <div style={{ color: "#dc2626", fontSize: 12 }}>⚠️ {err}</div>}
      {contractsOpen && (
        <div data-testid={`coop-contracts-panel-${coop.code}`}
             style={{ background: "#f8fafc", border: "1px solid #cbd5e1",
                      borderRadius: 6, padding: 10, marginBottom: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <strong style={{ fontSize: 13 }}>Contratti associati alla cooperativa</strong>
            <Button size="sm" variant="ghost" onClick={() => setContractsOpen(false)}>✕ Chiudi</Button>
          </div>
          {contracts === null && <p className="muted" style={{ margin: 0, fontSize: 12 }}>Caricamento…</p>}
          {contracts && contracts.length === 0 && (
            <div className="muted" style={{ fontSize: 12 }}>Nessun contratto collegato a questa cooperativa.</div>
          )}
          {contracts && contracts.length > 0 && (
            <table className="ds-table" style={{ fontSize: 12 }}>
              <thead><tr><th>Codice</th><th>Nome</th><th>Validità</th><th>Attivo</th></tr></thead>
              <tbody>
                {contracts.map((c) => (
                  <tr key={c.id} data-testid={`coop-contract-row-${c.code}`}>
                    <td><code>{c.code}</code></td>
                    <td>{c.name}</td>
                    <td>{c.starts_at} → {c.ends_at}</td>
                    <td><Badge tone={c.is_active ? "success" : "neutral"}>
                      {c.is_active ? "Sì" : "No"}
                    </Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
      {!members && <p className="muted">Caricamento…</p>}
      {members && members.length === 0 && (
        <div style={{ color: "#64748b", fontSize: 12 }}>Nessun membro nel range selezionato.</div>
      )}
      {members && members.length > 0 && (
        <table className="ds-table" data-testid={`coop-members-table-${coop.code}`}>
          <thead><tr><th>Cliente</th><th>Valid from</th><th>Valid to</th><th>Attivo</th><th></th></tr></thead>
          <tbody>
            {members.map(m => (
              <tr key={m.id} data-testid={`membership-row-${m.id}`}>
                <td>{customerById[m.customer_id]?.business_name || m.customer_id.slice(0, 8)}</td>
                <td>{m.valid_from}</td>
                <td>{m.valid_to || <span style={{ color: "#059669" }}>aperto</span>}</td>
                <td><Badge tone={m.is_active ? "success" : "neutral"}>{m.is_active ? "Attivo" : "Chiuso"}</Badge></td>
                <td>
                  {canEdit && m.is_active && !m.valid_to && (
                    <Button size="sm" onClick={() => setValidTo(m)}
                            data-testid={`membership-close-${m.id}`}>Chiudi</Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {addModal && (
        <MembershipAddModal customers={customers}
          onClose={() => setAddModal(false)}
          onSubmit={async (payload) => {
            await apiPost(`/api/admin/cooperatives/${coop.id}/members`, payload);
            setAddModal(false); await load();
          }} />
      )}
    </div>
  );
}


function MembershipAddModal({ customers, onClose, onSubmit }) {
  const [f, setF] = useState({
    customer_id: customers?.[0]?.id || "",
    valid_from: new Date().toISOString().slice(0, 10),
    valid_to: "",
    is_active: true,
  });
  const [err, setErr] = useState(null);
  const submit = () => {
    setErr(null);
    if (!f.customer_id) return setErr("Cliente obbligatorio");
    if (!f.valid_from) return setErr("valid_from obbligatorio");
    onSubmit({
      customer_id: f.customer_id,
      valid_from: f.valid_from,
      valid_to: f.valid_to || null,
      is_active: f.is_active,
    });
  };
  return (
    <Modal title="Aggiungi membro" onClose={onClose} testid="membership-modal">
      <FormField label="Cliente *">
        <select className="ds-input" value={f.customer_id}
                onChange={(e) => setF({ ...f, customer_id: e.target.value })}
                data-testid="membership-customer">
          {(customers || []).map(c => (
            <option key={c.id} value={c.id}>{c.business_name || c.code}</option>
          ))}
        </select>
      </FormField>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <FormField label="Valid from *">
          <Input type="date" value={f.valid_from}
                 onChange={(e) => setF({ ...f, valid_from: e.target.value })}
                 data-testid="membership-valid-from" />
        </FormField>
        <FormField label="Valid to (vuoto = aperto)">
          <Input type="date" value={f.valid_to}
                 onChange={(e) => setF({ ...f, valid_to: e.target.value })} />
        </FormField>
      </div>
      {err && <div style={{ color: "#dc2626", fontSize: 12 }}>{err}</div>}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
        <Button onClick={onClose}>Annulla</Button>
        <Button variant="primary" onClick={submit} data-testid="membership-submit">Salva</Button>
      </div>
    </Modal>
  );
}


// ============================================================================
// P2d — Delivery Points multi-sede editor
// ============================================================================
function DeliveryPointsTab() {
  const { apiGet, apiPost, apiPatch, apiDelete } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [rows, setRows] = useState(null);
  const [modal, setModal] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const cs = await apiGet("/api/customers");
        setCustomers(cs);
        if (cs.length > 0 && !selectedCustomer) setSelectedCustomer(cs[0].id);
      } catch (e) { setErr(e?.message || String(e)); }
    })();
    /* eslint-disable-next-line */
  }, []);

  const loadPoints = async () => {
    if (!selectedCustomer) return;
    try {
      const dp = await apiGet(`/api/delivery-points?customer_id=${selectedCustomer}`);
      setRows(dp);
    } catch (e) { setErr(e?.message || String(e)); }
  };
  useEffect(() => { loadPoints(); /* eslint-disable-next-line */ }, [selectedCustomer]);

  const setDefault = async (dp) => {
    if (!window.confirm(`Impostare "${dp.name}" come sede predefinita?`)) return;
    await apiPatch(`/api/customers/${dp.customer_id}/delivery-points/${dp.id}/set-default`, {});
    await loadPoints();
  };

  const remove = async (dp) => {
    if (dp.is_default) return alert("Impossibile eliminare la sede predefinita.");
    if (!window.confirm(`Eliminare "${dp.name}"?`)) return;
    try { await apiDelete(`/api/delivery-points/${dp.id}`); await loadPoints(); }
    catch (e) { alert(`Errore: ${e?.message || e}`); }
  };

  return (
    <>
      {err && <Alert variant="danger">{err}</Alert>}
      <Card>
        <Card.Header>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <strong>Sedi consegna</strong>
              <select className="ds-input" value={selectedCustomer}
                      onChange={(e) => setSelectedCustomer(e.target.value)}
                      data-testid="dp-select-customer">
                {customers.map(c => (
                  <option key={c.id} value={c.id}>{c.business_name || c.code}</option>
                ))}
              </select>
            </div>
            <Button variant="primary"
                    onClick={() => setModal({ mode: "create", data: { customer_id: selectedCustomer } })}
                    data-testid="dp-new">+ Nuova sede</Button>
          </div>
        </Card.Header>
        <Card.Body>
          {!rows && <p className="muted">Caricamento…</p>}
          {rows && rows.length === 0 && <Alert tone="info">Nessuna sede consegna per il cliente selezionato.</Alert>}
          {rows && rows.length > 0 && (
            <div className="ds-table-wrap">
              <table className="ds-table" data-testid="dp-table">
                <thead><tr>
                  <th>Nome</th><th>Indirizzo</th><th>Città</th><th>Camion</th>
                  <th>Sponda</th><th>Default</th><th></th>
                </tr></thead>
                <tbody>
                  {rows.map(dp => (
                    <tr key={dp.id} data-testid={`dp-row-${dp.id}`}>
                      <td>{dp.name}</td>
                      <td style={{ fontSize: 12 }}>{dp.address}</td>
                      <td>{dp.city} ({dp.province})</td>
                      <td><Badge tone={dp.truck_accessible === true ? "success" : dp.truck_accessible === false ? "warning" : "neutral"}>
                        {dp.truck_accessible === true ? "SI" : dp.truck_accessible === false ? "NO" : "DA VERIFICARE"}
                      </Badge></td>
                      <td>{dp.tail_lift_required === true ? <Badge tone="warning">SI</Badge> : dp.tail_lift_required === false ? <Badge tone="info">NO</Badge> : <Badge tone="neutral">DA VERIFICARE</Badge>}</td>
                      <td>{dp.is_default
                        ? <Badge tone="info" data-testid={`dp-default-${dp.id}`}>Predefinita</Badge>
                        : <Button size="sm" onClick={() => setDefault(dp)}
                                  data-testid={`dp-set-default-${dp.id}`}>Rendi default</Button>}
                      </td>
                      <td style={{ display: "flex", gap: 4 }}>
                        <Button size="sm" onClick={() => setModal({ mode: "edit", data: dp })}
                                data-testid={`dp-edit-${dp.id}`}>Modifica</Button>
                        {!dp.is_default && (
                          <Button size="sm" variant="danger" onClick={() => remove(dp)}
                                  data-testid={`dp-delete-${dp.id}`}>✕</Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card.Body>
      </Card>
      {modal && (
        <DeliveryPointModal mode={modal.mode} data={modal.data} customers={customers}
          onClose={() => setModal(null)}
          onSubmit={async (payload) => {
            if (modal.mode === "create") await apiPost("/api/delivery-points", payload);
            else await apiPatch(`/api/delivery-points/${modal.data.id}`, payload);
            setModal(null); await loadPoints();
          }} />
      )}
    </>
  );
}


function DeliveryPointModal({ mode, data, customers, onClose, onSubmit }) {
  const [f, setF] = useState({
    customer_id: data.customer_id || (customers?.[0]?.id || ""),
    name: data.name || "", address: data.address || "",
    zip_code: data.zip_code || "", city: data.city || "",
    province: data.province || "", region: data.region || "",
    phone: data.phone || "",
    warehouse_contact_name: data.warehouse_contact_name || "",
    warehouse_contact_phone: data.warehouse_contact_phone || "",
    truck_accessible: data.truck_accessible ?? null,
    tail_lift_required: data.tail_lift_required ?? null,
    booking_required: data.booking_required ?? null,
    loading_dock_available: data.loading_dock_available ?? null,
    delivery_time_from: data.delivery_time_from || "",
    delivery_time_to: data.delivery_time_to || "",
    access_notes: data.access_notes || "",
    logistic_notes: data.logistic_notes || "",
    unloading_instructions: data.unloading_instructions || "",
    is_default: data.is_default || false,
  });
  const [err, setErr] = useState(null);
  const submit = () => {
    setErr(null);
    const req = ["name", "address", "zip_code", "city", "province", "region"];
    for (const k of req) if (!f[k]?.trim()) return setErr(`Campo obbligatorio: ${k}`);
    const payload = { ...f };
    for (const k of ["phone", "warehouse_contact_name", "warehouse_contact_phone",
                     "delivery_time_from", "delivery_time_to",
                     "access_notes", "logistic_notes", "unloading_instructions"]) {
      if (payload[k] === "") payload[k] = null;
    }
    if (mode === "edit") { delete payload.customer_id; delete payload.is_default; }
    onSubmit(payload);
  };
  return (
    <Modal title={mode === "create" ? "Nuova sede consegna" : "Modifica sede"}
           onClose={onClose} testid="dp-modal">
      {mode === "create" && (
        <FormField label="Cliente *">
          <select className="ds-input" value={f.customer_id}
                  onChange={(e) => setF({ ...f, customer_id: e.target.value })}
                  data-testid="dp-form-customer">
            {(customers || []).map(c => (
              <option key={c.id} value={c.id}>{c.business_name || c.code}</option>
            ))}
          </select>
        </FormField>
      )}
      <FormField label="Nome sede *">
        <Input value={f.name} data-testid="dp-form-name"
               onChange={(e) => setF({ ...f, name: e.target.value })} />
      </FormField>
      <FormField label="Indirizzo *">
        <Input value={f.address}
               onChange={(e) => setF({ ...f, address: e.target.value })} />
      </FormField>
      <div style={{ display: "grid", gridTemplateColumns: "80px 1fr 60px", gap: 6 }}>
        <FormField label="CAP *">
          <Input value={f.zip_code} onChange={(e) => setF({ ...f, zip_code: e.target.value })} />
        </FormField>
        <FormField label="Città *">
          <Input value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} />
        </FormField>
        <FormField label="Prov *">
          <Input value={f.province}
                 onChange={(e) => setF({ ...f, province: e.target.value.toUpperCase() })} />
        </FormField>
      </div>
      <FormField label="Regione *">
        <Input value={f.region}
               onChange={(e) => setF({ ...f, region: e.target.value })} />
      </FormField>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
        <FormField label="Telefono">
          <Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} />
        </FormField>
        <FormField label="Contatto magazzino">
          <Input value={f.warehouse_contact_name}
                 onChange={(e) => setF({ ...f, warehouse_contact_name: e.target.value })} />
        </FormField>
      </div>
      <FormField label="Telefono magazzino">
        <Input value={f.warehouse_contact_phone}
               onChange={(e) => setF({ ...f, warehouse_contact_phone: e.target.value })} />
      </FormField>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
        <FormField label="Accesso bilico">
          <select className="ds-input" data-testid="dp-admin-truck_accessible"
                  value={f.truck_accessible === true ? "yes" : f.truck_accessible === false ? "no" : ""}
                  onChange={(e) => setF({ ...f, truck_accessible: e.target.value === "yes" ? true : e.target.value === "no" ? false : null })}>
            <option value="">— Da verificare —</option>
            <option value="yes">SI</option>
            <option value="no">NO</option>
          </select>
        </FormField>
        <FormField label="Sponda idraulica">
          <select className="ds-input" data-testid="dp-admin-tail_lift_required"
                  value={f.tail_lift_required === true ? "yes" : f.tail_lift_required === false ? "no" : ""}
                  onChange={(e) => setF({ ...f, tail_lift_required: e.target.value === "yes" ? true : e.target.value === "no" ? false : null })}>
            <option value="">— Da verificare —</option>
            <option value="yes">SI</option>
            <option value="no">NO</option>
          </select>
        </FormField>
        <FormField label="Prenotazione scarico">
          <select className="ds-input" data-testid="dp-admin-booking_required"
                  value={f.booking_required === true ? "yes" : f.booking_required === false ? "no" : ""}
                  onChange={(e) => setF({ ...f, booking_required: e.target.value === "yes" ? true : e.target.value === "no" ? false : null })}>
            <option value="">— Da verificare —</option>
            <option value="yes">SI</option>
            <option value="no">NO</option>
          </select>
        </FormField>
        <FormField label="Banchina / rampa">
          <select className="ds-input" data-testid="dp-admin-loading_dock_available"
                  value={f.loading_dock_available === true ? "yes" : f.loading_dock_available === false ? "no" : ""}
                  onChange={(e) => setF({ ...f, loading_dock_available: e.target.value === "yes" ? true : e.target.value === "no" ? false : null })}>
            <option value="">— Da verificare —</option>
            <option value="yes">SI</option>
            <option value="no">NO</option>
          </select>
        </FormField>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
        <FormField label="Consegna dalle">
          <Input type="time" value={f.delivery_time_from}
                 onChange={(e) => setF({ ...f, delivery_time_from: e.target.value })} />
        </FormField>
        <FormField label="Alle">
          <Input type="time" value={f.delivery_time_to}
                 onChange={(e) => setF({ ...f, delivery_time_to: e.target.value })} />
        </FormField>
      </div>
      <FormField label="Note accesso">
        <Input value={f.access_notes}
               onChange={(e) => setF({ ...f, access_notes: e.target.value })} />
      </FormField>
      <FormField label="Note logistiche">
        <Input value={f.logistic_notes}
               onChange={(e) => setF({ ...f, logistic_notes: e.target.value })} />
      </FormField>
      <FormField label="Istruzioni scarico">
        <Input value={f.unloading_instructions}
               onChange={(e) => setF({ ...f, unloading_instructions: e.target.value })} />
      </FormField>
      {mode === "create" && (
        <FormField label="Sede predefinita">
          <select className="ds-input" value={f.is_default ? "true" : "false"}
                  onChange={(e) => setF({ ...f, is_default: e.target.value === "true" })}>
            <option value="false">No</option>
            <option value="true">Sì (sostituisce l'attuale default)</option>
          </select>
        </FormField>
      )}
      {err && <div style={{ color: "#dc2626", fontSize: 12 }}>{err}</div>}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
        <Button onClick={onClose}>Annulla</Button>
        <Button variant="primary" onClick={submit} data-testid="dp-submit">Salva</Button>
      </div>
    </Modal>
  );
}

